import React, { useState, useEffect, useRef, useCallback } from 'react';
// Security headers deployment trigger
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, useSearchParams, useParams } from 'react-router-dom';
import HeadshotGenerator from './HeadshotGenerator';
import ImageTimer from './ImageTimer'; // <-- ADD THIS
import GrowthDashboard from './GrowthDashboard'; // <-- NEW DASHBOARD
import { Helmet } from 'react-helmet-async';
import { initializeApp } from 'firebase/app';
// import { loadStripe } from '@stripe/stripe-js'; // FIX: Commented out unused 'loadStripe'
import { PRODUCT_CONFIG } from './stripeConfig';
import { useGoogleIdentity } from './useGoogleIdentity'; // Adjust path if needed
import { getNicheFromPath, getSectionContent } from './config/nicheConfig';
import {
    getAuth,
    signInWithPopup,
    GoogleAuthProvider,
    onAuthStateChanged,
    setPersistence,
    browserLocalPersistence
} from 'firebase/auth';
import {
    getFirestore,
    doc,
    getDoc,
    setDoc,
    updateDoc,
    onSnapshot,
    collection,
    query,
    where
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage'; // Add this

// Load Stripe with publishable key
// const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY); // FIX: Removed unused var 'stripePromise'

const GuidePageLoader = ({ setShowLoginModal }) => {
    const { slug } = useParams();
    const [guideData, setGuideData] = React.useState(null);
    const [error, setError] = React.useState(false);

    React.useEffect(() => {
        import(`./pages/guides/data/${slug}.json`)
            .then(module => setGuideData(module.default))
            .catch(() => setError(true));
    }, [slug]);

    if (error) return <div className="p-20 text-center text-white text-2xl font-bold">SEO Guide Not Found</div>;
    if (!guideData) return <div className="w-full min-h-screen bg-[#121212] p-20 text-center text-white text-2xl">Loading Localized Content...</div>;

    const UnifiedGuideTemplate = React.lazy(() => import('./pages/guides/UnifiedGuideTemplate.jsx'));

    return (
        <React.Suspense fallback={<div className="w-full min-h-screen bg-[#121212] p-20 text-center text-white text-2xl">Loading UI Template...</div>}>
            <UnifiedGuideTemplate data={guideData} setShowLoginModal={setShowLoginModal} />
        </React.Suspense>
    );
};

// --- Firebase Configuration ---
// NOTE: For production, these values should be loaded from secure environment variables.
const firebaseConfig = {
    apiKey: process.env.REACT_APP_API,
    authDomain: process.env.REACT_APP_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_PROJECT_ID,
    storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_APP_ID,
    measurementId: process.env.REACT_APP_MEASUREMENT_ID,
    imageapiKey: process.env.REACT_APP_GEMINI_API
};

// --- Firebase Initialization ---
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app); // Add this

// Set auth persistence to preserve login state
setPersistence(auth, browserLocalPersistence)
    .catch((error) => {
        console.error("Error setting auth persistence:", error);
    });

// Spinner component
const Spinner = ({ small = false, large = false }) => (
    <svg className={`animate-spin ${large ? 'h-10 w-10' : small ? 'h-5 w-5 mr-2' : 'h-5 w-5'} text-white`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
);




// --- Simple DriveImage Component for Google Drive thumbnails ---
const DriveImageSimple = ({ file, token, activeMode }) => {
    const [imageUrl, setImageUrl] = useState(null);
    const [error, setError] = useState(false);
    const [retryCount, setRetryCount] = useState(0);

    useEffect(() => {
        // This variable will hold the URL for this effect run only
        let objectUrl = null;

        const loadImage = async () => {
            try {
                const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:3001' : '';
                let proxyUrl;
                let headers = {};

                if (activeMode === 'team') {
                    // Team mode uses an authenticated backend proxy
                    proxyUrl = `${baseUrl}/api/drive/team-thumbnail?url=${encodeURIComponent(file.thumbnailLink)}`;
                    const userToken = await auth.currentUser.getIdToken();
                    headers['Authorization'] = `Bearer ${userToken}`;
                } else {
                    // Personal mode uses the simple proxy with the Google token
                    proxyUrl = `${baseUrl}/api/drive-thumbnail?url=${encodeURIComponent(file.thumbnailLink)}&token=${encodeURIComponent(token || '')}`;
                }

                console.log(`Loading Drive image ${file.name} via proxy: ${proxyUrl}`);

                const response = await fetch(proxyUrl, { headers }); // <-- Pass headers

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const blob = await response.blob();
                // 1. Assign the new URL to our local variable
                objectUrl = URL.createObjectURL(blob);
                // 2. Set the state
                setImageUrl(objectUrl);
                setError(false);
                setRetryCount(0); // Reset retry count on success
            } catch (err) {
                console.error(`Failed to load Drive image ${file.name}:`, err);

                // Retry logic: try up to 2 times with exponential backoff
                if (retryCount < 2) {
                    console.log(`Retrying thumbnail load for ${file.name} (attempt ${retryCount + 1})`);
                    setTimeout(() => {
                        setRetryCount(prev => prev + 1);
                    }, 1000 * Math.pow(2, retryCount)); // 1s, 2s, 4s
                } else {
                    setError(true);
                }
            }
        };

        // Don't try to fetch if the token is missing
        const canFetch = (activeMode === 'personal' && token) || (activeMode === 'team');
        if (file.thumbnailLink && canFetch) {
            loadImage();
        } else if (!canFetch) {
            console.log(`DriveImageSimple: Skipping fetch (Mode: ${activeMode}, Token: ${!!token})`);
            setError(true);
        }

        // 3. The cleanup function
        return () => {
            // Only revoke the URL that was created by *this specific effect run*
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [file.thumbnailLink, token, file.name, retryCount, activeMode]); // <-- Add activeMode

    // Show a fallback if there's an error
    if (error || !imageUrl) {
        return (
            <img
                src="/logo192.png"
                alt={file.name}
                className="w-full h-48 object-cover rounded-lg bg-gray-900 pointer-events-none"
            />
        );
    }

    // Show the successfully loaded image
    return (
        <img
            src={imageUrl}
            alt={file.name}
            className="w-full h-48 object-cover rounded-lg bg-gray-900 pointer-events-none"
        />
    );
};

// ========================================================================
// ALL COMPONENT DEFINITIONS MOVED OUTSIDE OF App() TO FIX RE-RENDER BUG
// ========================================================================

const LoginModal = React.memo(({
    showLoginModal,
    setShowLoginModal,
    isAuthLoading,
    error,
    handleSignIn,
}) => {
    if (!showLoginModal) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-[#1a1a1a] p-8 rounded-2xl shadow-2xl border border-gray-700 max-w-lg w-full">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-white text-2xl font-bold">Sign In</h3>
                    <button
                        onClick={() => setShowLoginModal(false)}
                        className="text-gray-400 hover:text-white text-2xl"
                    >
                        ×
                    </button>
                </div>

                <div className="space-y-4">
                    <button
                        onClick={handleSignIn}
                        disabled={isAuthLoading}
                        className="w-full bg-[#FACC15] hover:bg-yellow-400 text-[#121212] font-bold py-4 px-8 rounded-lg text-lg transition-colors duration-300 disabled:bg-yellow-300 flex items-center justify-center">
                        {isAuthLoading ? <Spinner small /> : 'Sign in with Google'}
                    </button>
                    {error && (
                        <div className="text-red-400 text-center text-sm mt-2">
                            {error.includes('Failed to sign in') ? 'Failed to sign in. Please try again.' : error}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});

const LegalPage = ({ title, children, onBack }) => (
    <div className="w-full min-h-screen bg-[#121212] text-white" style={{ fontFamily: '"Plus Jakarta Sans", "Noto Sans", sans-serif' }}>
        <header className="bg-[#1a1a1a] border-b border-gray-700">
            <div className="container mx-auto px-6 py-4 flex justify-between items-center">
                <div className="flex items-center space-x-3">
                    <img
                        src="/InstantHeadshotPro logo.png"
                        alt="InstantHeadshot.pro"
                        className="h-8 w-auto bg-white p-1 rounded"
                    />
                    <h1 className="text-white text-xl font-bold leading-tight tracking-[-0.015em]">InstantHeadshot.pro</h1>
                </div>
                <button onClick={onBack} className="text-[#FACC15] hover:text-yellow-400 font-bold py-2 px-4 rounded-lg transition-colors">
                    &larr; Back
                </button>
            </div>
        </header>
        <main className="container mx-auto px-6 py-12">
            <div className="bg-[#1a1a1a] p-8 rounded-2xl shadow-2xl border border-gray-700 max-w-4xl mx-auto">
                <h2 className="text-3xl font-extrabold mb-8 text-white text-center">{title}</h2>
                <div className="prose prose-invert max-w-none text-gray-300 prose-headings:text-white prose-strong:text-white prose-li:text-gray-300 prose-p:text-gray-300 prose-p:leading-relaxed prose-p:mb-4 prose-ul:mb-6 prose-ol:mb-6 prose-li:mb-2 prose-h2:text-2xl prose-h2:font-bold prose-h2:mt-8 prose-h2:mb-4 prose-h3:text-xl prose-h3:font-semibold prose-h3:mt-6 prose-h3:mb-3">
                    {children}
                </div>
            </div>
        </main>
    </div>
);

const PrivacyPolicy = ({ onBack }) => (
    <LegalPage title="Privacy Policy" onBack={onBack}>
        <p className="text-center mb-8">Last Updated: November 4, 2025</p>

        <h2>1. Introduction</h2>
        <p>Welcome to InstantHeadshot.pro ("we," "our," or "us"). We are committed to protecting your privacy and providing transparency about how we handle your information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI headshot generation service.</p>

        <h2>2. Information We Collect</h2>
        <h3>2.1 Personal Information</h3>
        <ul>
            <li>Account Information: When you create an account using Google Sign-In or email authentication, we collect your email address and name.</li>
            <li>Payment Information: When you purchase credits, we collect payment information through our third-party payment processor (Stripe). We do not store your full payment card details.</li>
        </ul>

        <h3>2.2 Image Data</h3>
        <p>Important Privacy-First Principle: We operate on a privacy-first principle regarding your images:</p>
        <ul>
            <li>Uploaded Images: The selfies you upload for headshot generation are processed in real-time and are not stored on our servers after processing.</li>
            <li>Generated Images: The AI-generated headshots are available for immediate download and are not stored on our servers after your session.</li>
            <li>Temporary Processing: Images are temporarily processed during generation and are automatically deleted immediately after the generation process completes.</li>
        </ul>

        <h3>2.3 Usage Data</h3>
        <ul>
            <li>Technical Information: IP address, browser type, operating system, device information</li>
            <li>Usage Information: Feature usage, generation counts, session duration</li>
            <li>Analytics: Anonymous usage patterns to improve our service</li>
        </ul>

        <h3>2.4 Cookies and Tracking Technologies</h3>
        <p>We use minimal tracking technologies to enhance your experience:</p>
        <ul>
            <li>Firebase Authentication: Uses browser local storage to maintain your login state and session persistence</li>
            <li>Browser Storage: Local storage is used to temporarily store authentication tokens and user preferences</li>
        </ul>
        <p>We do not use traditional cookies for analytics or tracking purposes. You can clear browser storage through your browser settings, but this will log you out and reset your preferences.</p>

        <h2>3. How We Use Your Information</h2>
        <ul>
            <li>To provide and maintain our AI headshot generation service</li>
            <li>To process transactions and manage your credits</li>
            <li>To communicate with you about service updates, security alerts, and support messages</li>
            <li>To improve our service and develop new features</li>
            <li>To ensure compliance with our Terms of Service</li>
            <li>To protect against fraudulent or unauthorized use</li>
        </ul>

        <h2>4. Data Sharing and Disclosure</h2>
        <h3>4.1 Service Providers</h3>
        <p>We may share information with third-party vendors who perform services on our behalf:</p>
        <ul>
            <li>AI Processing: Your uploaded images are temporarily shared with Google Gemini API for AI processing. These images are not used to train AI models and are processed in real-time only.</li>
            <li>Payment Processing: Stripe processes your payments securely. We do not store your full payment card details.</li>
            <li>Authentication: Google Sign-In and Firebase Authentication handle authentication services.</li>
            <li>Data Storage: Firebase Firestore stores your account information and credit balance.</li>
            <li>Caching: Vercel KV provides temporary caching for generated images (15-minute retention).</li>
            <li>Hosting: Our service is hosted on Vercel's infrastructure.</li>
        </ul>

        <h3>4.2 Legal Requirements</h3>
        <p>We may disclose your information if required to do so by law or in response to valid requests by public authorities.</p>

        <h3>4.3 Business Transfers</h3>
        <p>In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.</p>

        <h2>5. Data Retention</h2>
        <ul>
            <li>Account Information: Retained while your account is active</li>
            <li>Uploaded Images: Not stored - deleted immediately after processing</li>
            <li>Generated Images: Not stored - available for immediate download only, cached for 15 minutes maximum</li>
            <li>Payment Records: Retained as required by law for financial reporting</li>
            <li>Usage Data: Retained for service improvement and analytics</li>
        </ul>

        <h2>6. Your Privacy Rights</h2>
        <h3>6.1 General Rights</h3>
        <ul>
            <li>Access and Correction: You can access and update your account information</li>
            <li>Account Deletion: You can request deletion of your account and associated data</li>
            <li>Data Portability: You can download your generated images immediately</li>
            <li>Opt-out: You can opt-out of marketing communications</li>
        </ul>

        <h3>6.2 Regional Privacy Disclosures</h3>
        <p>Depending on your location, you may have additional rights:</p>
        <ul>
            <li>European Economic Area (EEA) and UK: Rights under GDPR including access, rectification, erasure, restriction, and data portability</li>
            <li>California: Rights under CCPA/CPRA including the right to know, delete, and opt-out of "sale" of personal information</li>
            <li>Other Regions: Additional rights as provided by local privacy laws</li>
        </ul>
        <p>Data Controller: InstantHeadshot.pro is the data controller for your personal information.</p>
        <p>How to Exercise Your Rights: Contact us at support+privacy@instantheadshot.pro to exercise your privacy rights. We will respond to your request within 30 days.</p>

        <h2>7. Security</h2>
        <p>We implement appropriate technical and organizational security measures to protect your information, including encryption, access controls, secure data processing protocols, and regular security assessments.</p>

        <h2>8. International Data Transfers</h2>
        <p>Your data may be processed in countries outside your residence. We ensure appropriate safeguards are in place for such transfers, such as by relying on Standard Contractual Clauses (SCCs) as approved by the European Commission, or other appropriate legal mechanisms.</p>

        <h2>9. Children's Privacy</h2>
        <p>Our service is not intended for users under 16 years of age. We do not knowingly collect information from children under 16.</p>

        <h2>10. Changes to This Policy</h2>
        <p>We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last Updated" date. For material changes, we will make reasonable efforts to notify you, such as by sending an email to the address associated with your account or by displaying a prominent notice within our service.</p>

        <h2>11. Contact Us</h2>
        <p className="text-center mt-4">If you have any questions about this Privacy Policy, please contact us at:</p>
        <p className="text-center text-lg">support+privacy@instantheadshot.pro</p>
    </LegalPage>
);

const TermsOfService = ({ onBack }) => (
    <LegalPage title="Terms of Service" onBack={onBack}>
        <p className="text-center mb-8">Last Updated: November 4, 2025</p>

        <h2>1. Acceptance of Terms</h2>
        <p>By accessing or using InstantHeadshot.pro (the "Service"), you agree to be bound by these Terms of Service and our Privacy Policy. If you do not agree to these terms, you may not use our Service.</p>

        <h2>2. Eligibility</h2>
        <p>You must be at least 16 years old to use this Service. By using the Service, you represent and warrant that you meet this age requirement.</p>

        <h2>3. Account Registration and Security</h2>
        <h3>3.1 Account Creation</h3>
        <p>To access certain features, you must create an account using Google Sign-In or email authentication. You agree to provide accurate and complete information during registration.</p>

        <h3>3.2 Account Security</h3>
        <p>You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must notify us immediately of any unauthorized use of your account.</p>

        <h2>4. Service Description</h2>
        <p>InstantHeadshot.pro provides AI-powered professional headshot generation services. Our Service allows you to upload selfies and generate professional headshots in various styles including Corporate, Medical/Healthcare, and Academic categories with multiple aspect ratios and background options.</p>

        <h2>5. User Content</h2>
        <h3>5.1 Uploaded Images</h3>
        <p>You retain all ownership rights to the selfies and images you upload to the Service. By uploading images, you grant us a limited, non-exclusive license to process these images solely for the purpose of generating your headshots.</p>

        <h3>5.2 Content Restrictions</h3>
        <p>You agree not to upload any content that:</p>
        <ul>
            <li>Is illegal, obscene, defamatory, or infringes on intellectual property rights</li>
            <li>Contains personally identifiable information of others without consent</li>
            <li>Is harmful, threatening, or harassing</li>
            <li>Violates any applicable laws or regulations</li>
        </ul>

        <h2>6. Generated Content</h2>
        <h3>6.1 Ownership Rights</h3>
        <p>Important: You are granted full ownership and commercial rights to the headshots generated from your uploaded images. You may use these images for personal, professional, and commercial purposes without restriction.</p>

        <h3>6.2 AI Limitations</h3>
        <p>The Service uses artificial intelligence which may occasionally produce imperfect results, artifacts, or inaccuracies. We do not guarantee perfection in generated images and are not liable for any such imperfections.</p>

        <h2>7. Data Privacy and Processing</h2>
        <h3>7.1 Privacy-First Approach</h3>
        <p>Privacy-First Principle:</p>
        <ul>
            <li>Uploaded images are processed in real-time and are not stored on our servers</li>
            <li>Generated headshots are available for immediate download and are not stored</li>
            <li>All image processing occurs temporarily during generation sessions</li>
        </ul>

        <h3>7.2 Data Processing</h3>
        <p>Your uploaded images are temporarily processed by Google Gemini for AI generation. These images are not used to train AI models and are deleted immediately after processing.</p>

        <h2>8. Credits and Payments</h2>
        <h3>8.1 Credit System</h3>
        <p>The Service operates on a credit-based system. Each headshot generation consumes one credit as specified on our pricing page.</p>

        <h3>8.2 Purchases and Refunds</h3>
        <p>Credits are sold in packs and are non-refundable except as required by law. If a generation fails due to a technical error on our part, affected credits will be refunded.</p>

        <h3>8.3 Pricing Changes</h3>
        <p>We reserve the right to modify our pricing at any time. Price changes will not affect credits already purchased.</p>

        <h2>9. Prohibited Uses</h2>
        <p>You agree not to:</p>
        <ul>
            <li>Use the Service for any illegal purpose or in violation of any laws</li>
            <li>Attempt to reverse engineer, decompile, or disassemble any part of the Service</li>
            <li>Use automated systems to access the Service</li>
            <li>Interfere with or disrupt the Service or servers</li>
            <li>Use the Service to generate content for malicious purposes</li>
        </ul>

        <h2>10. Intellectual Property</h2>
        <p>The Service and its original content, features, and functionality are owned by InstantHeadshot.pro and are protected by international copyright, trademark, and other intellectual property laws.</p>

        <h2>11. Termination</h2>
        <p>We may terminate or suspend your account and access to the Service immediately, without prior notice, for conduct that we believe violates these Terms or is harmful to other users, us, or third parties, or for any other reason in our sole discretion.</p>

        <h2>12. Disclaimer of Warranties</h2>
        <p>The service is provided "as is" and "as available" without warranties of any kind, either express or implied. We do not warrant that the service will be uninterrupted, secure, or error-free.</p>

        <h2>13. Limitation of Liability</h2>
        <p>To the fullest extent permitted by law, in no event shall InstantHeadshot.pro be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or relating to your use of the service.</p>

        <h2>14. Indemnification</h2>
        <p>You agree to indemnify and hold harmless InstantHeadshot.pro and its affiliates from any claims, damages, losses, or expenses arising from your use of the Service or violation of these Terms.</p>

        <h2>15. Governing Law</h2>
        <p>These Terms shall be governed by the laws of the State of Delaware, without regard to its conflict of law provisions.</p>

        <h2>16. Changes to Terms</h2>
        <p>We reserve the right to modify these Terms at any time. We will provide notice of material changes by posting the updated Terms on our website and updating the "Last Updated" date.</p>

        <h2>17. Contact Information</h2>
        <p className="text-center mt-4">For questions about these Terms, please contact us at:</p>
        <p className="text-center text-lg">support+legal@instantheadshot.pro</p>
    </LegalPage>
);

// --- NEW & IMPROVED LANDING PAGE ---
const LandingPage = ({ setShowLoginModal }) => {
    const navigate = useNavigate();
    const location = useLocation();

    // Determine niche from URL path (e.g., /law-firms, /teams/law-firms)
    const nicheDataResponse = getNicheFromPath(location.pathname);
    const isNiche = !!nicheDataResponse;

    // Helper to build industry-specific term (e.g. Attorney, Realtor, Accountant) for dynamic Open Graph tags
    const getIndustryTerm = (key, category) => {
        const lowerKey = String(key || '').toLowerCase();
        if (lowerKey.includes('law') || lowerKey.includes('attorney')) return 'Attorney';
        if (lowerKey.includes('real') || lowerKey.includes('realtor')) return 'Realtor';
        if (lowerKey.includes('cpa') || lowerKey.includes('account')) return 'Accountant';
        if (category === 'medical') return 'Clinician';
        if (category === 'academic') return 'Academic';
        return 'Team Member';
    };

    const getIndustryDescription = (key, category) => {
        const lowerKey = String(key || '').toLowerCase();
        if (lowerKey.includes('law') || lowerKey.includes('attorney')) {
            return 'Test the workflow with 3 attorneys and see how consistent, firm-approved headshots can work for your law firm.';
        }
        if (lowerKey.includes('real') || lowerKey.includes('realtor')) {
            return 'Test the workflow with 3 realtors and see how consistent, brand-approved headshots can work for your agency.';
        }
        if (lowerKey.includes('cpa') || lowerKey.includes('account')) {
            return 'Test the workflow with 3 accountants and see how consistent, brand-approved headshots can work for your firm.';
        }
        if (category === 'medical') {
            return 'Test the workflow with 3 staff members and see how clinical trust and consistent headshots can work for your practice.';
        }
        if (category === 'academic') {
            return 'Test the workflow with 3 faculty members and see how consistent academic portraits can work for your department.';
        }
        return 'Test the workflow with 3 team members and see how brand-consistent headshots can work for your organization.';
    };

    // Default fallback content if not on a specialized landing page
    const defaultContent = {
        title: 'InstantHeadshot.pro | Virtual Photo Day for Modern Teams',
        description: 'Create consistent, brand-approved professional headshots for employees, new hires, and team pages without scheduling photographers or coordinating a traditional photo day.',
        heroTitle: 'Run a Virtual Photo Day for Your Team',
        heroSubtitle: 'Create consistent, brand-approved headshots for employees, new hires, and team pages — without scheduling photographers, chasing files, or coordinating a traditional photo day.',
        getStartedCategory: null,
        pilotButtonText: 'Start a Complimentary 3-Person Pilot',
        mismatchedSectionTitle: 'Fix the Mismatched Team Page',
        mismatchedSectionSubtitle: 'We call it the "Frankenstein Team Page": different crops, backgrounds, lighting, and image quality across one directory. Most growing businesses suffer from these visual inconsistencies — we bring brand consistency instantly.',
        faqs: [
            { question: "What is a 3-person Virtual Photo Day pilot?", answer: "The pilot lets your team test the full Virtual Photo Day workflow with up to 3 people. You can choose an approved look, invite team members, generate consistent professional headshots, and review the results before deciding whether to roll it out across the full team." },
            { question: "Who should participate in the pilot?", answer: "Typically, we recommend having 2 or 3 leaders or partners participate. This allows you to lock in your firm's brand templates and see how easy it is to standardize wardrobe, backgrounds, and framing before inviting the rest of your staff." },
            { question: "Do we need to schedule a photographer?", answer: "No. Your team members simply upload a single everyday smartphone selfie. Our system handles standardizing the background, lighting, and wardrobe automatically in 90 seconds, eliminating scheduling issues entirely." },
            { question: "Can we standardize the background, wardrobe, and crop?", answer: "Yes. In your workspace, you can select from classic studio backdrops or modern office settings, set a cohesive wardrobe style (such as business formal or business casual), and lock in standard crops for a unified directory." },
            { question: "What happens after the pilot?", answer: "Once you have verified the quality and visual consistency of your pilot headshots, you can choose a Team or Business pack to invite the rest of your team. Your customized Brand Templates and settings remain saved in your dashboard for future hires." }
        ]
    };

    const content = isNiche ? {
        title: nicheDataResponse.metaTitle,
        description: nicheDataResponse.metaDescription,
        heroTitle: nicheDataResponse.heroTitle,
        heroSubtitle: nicheDataResponse.heroSubtitle,
        getStartedCategory: nicheDataResponse.parentCategory,
        pilotButtonText: nicheDataResponse.pilotButtonText || 'Start a Complimentary 3-Person Pilot',
        mismatchedSectionTitle: nicheDataResponse.mismatchedSectionTitle || 'Fix the Mismatched Team Page',
        mismatchedSectionSubtitle: nicheDataResponse.mismatchedSectionSubtitle || 'We call it the "Frankenstein Team Page": different crops, backgrounds, lighting, and image quality across one directory. Most growing businesses suffer from these visual inconsistencies — we bring brand consistency instantly.',
        faqs: nicheDataResponse.faqs,
        ogTitle: nicheDataResponse.ogTitle || `Start a 3-${getIndustryTerm(nicheDataResponse.key, nicheDataResponse.parentCategory)} Virtual Photo Day Pilot`,
        ogDescription: nicheDataResponse.ogDescription || getIndustryDescription(nicheDataResponse.key, nicheDataResponse.parentCategory)
    } : {
        ...defaultContent,
        ogTitle: 'InstantHeadshot.pro | Virtual Photo Day for Modern Teams',
        ogDescription: 'Create consistent, brand-approved professional headshots for employees, new hires, and team pages without scheduling photographers or coordinating a traditional photo day.'
    };

    // Retrieve the Section Content based on Parent Category
    const sectionContent = getSectionContent(content.getStartedCategory);

    // Generate JSON-LD Schema
    const getSchema = () => {
        const baseSchema = {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "InstantHeadshot.pro",
            "applicationCategory": "BusinessApplication",
            "offers": {
                "@type": "Offer",
                "price": "49.00",
                "priceCurrency": "USD"
            }
        };

        const graph = [];

        if (isNiche) {
            graph.push({
                ...baseSchema,
                "name": `InstantHeadshot.pro - ${nicheDataResponse.heroTitle}`,
                "description": nicheDataResponse.metaDescription
            });
        } else {
            graph.push(baseSchema);
        }

        if (content.faqs && content.faqs.length > 0) {
            graph.push({
                "@type": "FAQPage",
                "mainEntity": content.faqs.map(faq => ({
                    "@type": "Question",
                    "name": faq.question,
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": faq.answer
                    }
                }))
            });
        }

        return JSON.stringify({
            "@context": "https://schema.org",
            "@graph": graph
        });
    };

    const handleGetStartedClick = () => { // eslint-disable-line no-unused-vars
        if (content.getStartedCategory) {
            // Store the intended category in localStorage so it can be picked up after login/signup
            localStorage.setItem('intendedCategory', content.getStartedCategory);
            navigate(`/?category=${content.getStartedCategory}`);
        }
        setShowLoginModal(true);
    };

    const FeatureIcon = ({ icon, title, description }) => (
        <div className="text-center p-6 bg-[#121212] rounded-xl border border-gray-700 transition-all duration-300 hover:border-gray-600">
            <div className="flex items-center justify-center h-16 w-16 rounded-full bg-gray-700 text-[#FACC15] mx-auto mb-4">
                {icon}
            </div>
            <h3 className="text-white text-xl font-bold mb-2">{title}</h3>
            <p className="text-gray-400">{description}</p>
        </div>
    );

    const canonicalUrl = `https://www.instantheadshot.pro${location.pathname === '/' ? '' : location.pathname}`;

    return (
        <div className="w-full min-h-screen bg-[#121212] text-white overflow-x-hidden" style={{ fontFamily: '"Plus Jakarta Sans", "Noto Sans", sans-serif' }}>
            <Helmet>
                <title>{content.title}</title>
                <meta name="description" content={content.description} />

                {/* Dynamic Canonical URL to prevent duplicate content consolidation */}
                <link rel="canonical" href={canonicalUrl} />

                {/* Open Graph / Facebook Meta Tags */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:title" content={content.ogTitle || content.title} />
                <meta property="og:description" content={content.ogDescription || content.description} />
                <meta property="og:image" content="https://www.instantheadshot.pro/InstantHeadshotpro%20opengraph.png" />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:site_name" content="InstantHeadshot.pro" />

                {/* Twitter Meta Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:url" content={canonicalUrl} />
                <meta name="twitter:title" content={content.ogTitle || content.title} />
                <meta name="twitter:description" content={content.ogDescription || content.description} />
                <meta name="twitter:image" content="https://www.instantheadshot.pro/InstantHeadshotpro%20opengraph.png" />

                <script type="application/ld+json">
                    {`${getSchema()}`}
                </script>
            </Helmet>
            {/* Header */}
            <header className="flex items-center p-4 justify-between bg-[#121212] sticky top-0 z-10 border-b border-gray-800/55">
                <div className="flex items-center flex-1 pl-4 space-x-3 cursor-pointer" onClick={() => navigate('/')}>
                    <img
                        src="/InstantHeadshotPro logo.png"
                        alt="InstantHeadshot.pro"
                        className="h-10 w-auto bg-white p-1 rounded"
                    />
                    <h2 className="text-white text-lg font-bold leading-tight tracking-[-0.015em] hidden sm:block">InstantHeadshot.pro</h2>
                </div>
                <div className="flex items-center justify-end space-x-4 pr-4">
                    <button
                        onClick={() => navigate('/pilot')}
                        className="bg-[#FACC15] hover:bg-yellow-400 text-black font-extrabold py-2 px-4 rounded-lg text-sm transition-all duration-300 shadow-md">
                        Start Pilot
                    </button>
                    <button
                        onClick={() => setShowLoginModal(true)}
                        className="text-gray-300 text-sm font-bold hover:text-white transition-colors">
                        Log In
                    </button>
                </div>
            </header>

            {/* Hero Section */}
            <main className="@container">
                <section className="relative flex min-h-[600px] flex-col gap-6 items-center justify-center p-6 text-center overflow-hidden">
                    {/* Video Background */}
                    <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover lg:object-top z-0">
                        <source src="/Hero section video.mp4" type="video/mp4" />
                    </video>
                    <div className="absolute inset-0 bg-black bg-opacity-60 z-1"></div>

                    {/* Content */}
                    <div className="relative z-2 flex flex-col gap-6 max-w-4xl">
                        <h1 className="text-white text-5xl font-black leading-tight tracking-[-0.033em] @[480px]:text-7xl drop-shadow-2xl">
                            {content.heroTitle}
                        </h1>

                        {/* UPDATED: B2B positioning for SMB teams */}
                        <div className="flex flex-col gap-5 max-w-4xl mx-auto w-full">
                            <p className="text-gray-100 text-lg font-medium leading-relaxed @[480px]:text-xl drop-shadow-md">
                                {content.heroSubtitle}
                            </p>

                            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 text-[#FACC15] font-bold text-sm sm:text-base">
                                <span className="flex items-center gap-1.5 bg-black/40 px-4 py-2 rounded-full backdrop-blur-md border border-[#FACC15]/30 shadow-lg">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                                    No weeks of planning
                                </span>
                                <span className="flex items-center gap-1.5 bg-black/40 px-4 py-2 rounded-full backdrop-blur-md border border-[#FACC15]/30 shadow-lg">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                                    No scheduling
                                </span>
                                <span className="flex items-center gap-1.5 bg-black/40 px-4 py-2 rounded-full backdrop-blur-md border border-[#FACC15]/30 shadow-lg">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                                    No office disruptions
                                </span>
                            </div>
                        </div>

                        <div className="flex flex-col items-center gap-6">
                            <div className="w-full max-w-md px-4 sm:px-0">
                                <button
                                    onClick={() => navigate('/pilot')}
                                    className="group flex w-full cursor-pointer items-center justify-center rounded-xl py-4 px-6 bg-[#FACC15] text-[#121212] text-md md:text-lg font-extrabold hover:bg-yellow-400 transition-all duration-300 ease-out shadow-[0_4px_20px_rgba(250,204,21,0.35)] hover:shadow-[0_8px_30px_rgba(250,204,21,0.6)] hover:scale-[1.02] active:scale-[0.98]">
                                    <span className="flex items-center gap-2 text-center justify-center">
                                        {content.pilotButtonText}
                                        <svg className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </span>
                                </button>
                            </div>

                            {/* TRUST BADGE */}
                            <div className="bg-green-900/80 border border-green-500/50 rounded-full px-5 py-2 flex items-center gap-2 shadow-[0_0_15px_rgba(34,197,94,0.3)] backdrop-blur-md">
                                <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                                <span className="text-green-100 text-sm font-bold tracking-wide uppercase">Privacy-First Architecture</span>
                            </div>
                        </div>
                    </div>
                </section>
                {/* Walkthrough Video Section */}
                <section id="demo" className="py-16 px-4 bg-[#121212]">
                    <div className="max-w-6xl mx-auto text-center">
                        <h2 className="text-white text-3xl font-bold leading-tight tracking-[-0.015em] mb-4">
                            See Your Virtual Photo Day in Action
                        </h2>
                        <p className="text-gray-300 text-lg font-normal leading-relaxed mb-8 max-w-2xl mx-auto">
                            Watch how effortless it is to roll out a unified, studio-grade visual identity across your entire organization in under two minutes.
                        </p>
                        <div className="relative max-w-4xl mx-auto rounded-2xl overflow-hidden shadow-2xl">
                            <div className="relative pb-[56.25%] h-0">
                                <iframe
                                    src="https://fast.wistia.net/embed/iframe/8ss19k4fkx?seo=false"
                                    title="InstantHeadshot.pro Walkthrough"
                                    allow="autoplay; fullscreen"
                                    allowtransparency="true"
                                    frameBorder="0"
                                    scrolling="no"
                                    className="absolute top-0 left-0 w-full h-full rounded-2xl"
                                ></iframe>
                            </div>
                            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none rounded-2xl"></div>
                        </div>
                        <div className="mt-8 flex flex-col sm:flex-row justify-center items-center gap-4 w-full px-4 sm:px-0 max-w-xl mx-auto">
                            <button
                                onClick={() => navigate('/pilot')}
                                className="group flex w-full sm:w-auto items-center justify-center rounded-xl py-3.5 px-8 bg-[#FACC15] text-[#121212] text-lg font-extrabold hover:bg-yellow-400 transition-all duration-300 ease-out shadow-[0_4px_15px_rgba(250,204,21,0.3)] hover:shadow-[0_6px_25px_rgba(250,204,21,0.5)] hover:scale-[1.02] active:scale-[0.98]"
                            >
                                <span className="flex items-center gap-2">
                                    {content.pilotButtonText}
                                    <svg className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                                    </svg>
                                </span>
                            </button>
                            <button
                                onClick={() => {
                                    const demoEl = document.getElementById('demo');
                                    if (demoEl) demoEl.scrollIntoView({ behavior: 'smooth' });
                                }}
                                className="w-full sm:w-auto bg-gray-800 hover:bg-gray-700 text-white font-bold py-3.5 px-8 rounded-xl text-lg transition-all duration-300 border border-gray-700"
                            >
                                See How the Workflow Works
                            </button>
                        </div>
                    </div>
                </section>

                {/* Comparison Section */}
                <section className="py-20 px-6 bg-[#1a1a1a]">
                    <div className="max-w-4xl mx-auto text-center">
                        <h2 className="text-white text-3xl font-bold leading-tight tracking-[-0.015em] mb-6">
                            {content.mismatchedSectionTitle}
                        </h2>
                        <p className="text-gray-300 text-lg font-normal leading-relaxed mb-12 max-w-3xl mx-auto">
                            {content.mismatchedSectionSubtitle}
                        </p>
                        <div className="grid md:grid-cols-2 gap-12 text-left">
                            <div>
                                <h3 className="text-red-500 text-2xl font-bold mb-4">Traditional Photo Day</h3>
                                <ul className="space-y-4 text-gray-300">
                                    <li className="flex items-start">
                                        <svg className="w-6 h-6 text-red-500 mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                                        <span><strong>High Friction:</strong> Takes weeks to schedule and coordinate.</span>
                                    </li>
                                    <li className="flex items-start">
                                        <svg className="w-6 h-6 text-red-500 mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                                        <span><strong>Expensive:</strong> Costs thousands of dollars for a single day.</span>
                                    </li>
                                    <li className="flex items-start">
                                        <svg className="w-6 h-6 text-red-500 mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                                        <span><strong>Inflexible:</strong> New hires break the visual consistency.</span>
                                    </li>
                                </ul>
                            </div>
                            <div>
                                <h3 className="text-[#FACC15] text-2xl font-bold mb-4">Virtual Photo Day</h3>
                                <ul className="space-y-4 text-gray-300">
                                    <li className="flex items-start">
                                        <svg className="w-6 h-6 text-green-500 mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                                        <span><strong>100% Automated:</strong> HR spends minutes, not weeks.</span>
                                    </li>
                                    <li className="flex items-start">
                                        <svg className="w-6 h-6 text-green-500 mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                                        <span><strong>Guaranteed Consistency:</strong> Admins lock in the template; the engine enforces the look.</span>
                                    </li>
                                    <li className="flex items-start">
                                        <svg className="w-6 h-6 text-green-500 mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                                        <span><strong>Frictionless Onboarding:</strong> Generate headshots for new hires on day one.</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Visual Proof Section */}
                <section className="py-12 px-4 bg-[#1a1a1a]">
                    <div className="text-center mb-8">
                        {/* UPDATED: SMB-Focused Unified Branding */}
                        <h2 className="text-white text-3xl font-bold leading-tight tracking-[-0.015em]">{sectionContent.unifiedLookTitle}</h2>
                        <p className="text-gray-300 text-lg font-normal leading-relaxed mt-4 max-w-3xl mx-auto">
                            {sectionContent.unifiedLookSubtitle}
                        </p>
                    </div>
                    <div className="flex overflow-x-auto [-ms-scrollbar-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden pb-4">
                        <div className="flex items-stretch p-4 gap-6">
                            {sectionContent.exampleImages.map((img, index) => (
                                <div key={index} className="flex h-full flex-1 flex-col gap-4 rounded-lg min-w-72">
                                    <img src={img.src} alt={img.alt} className="w-full bg-center bg-no-repeat aspect-square bg-cover rounded-xl flex flex-col object-cover" />
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* SOCIAL PROOF SECTION */}
                <section className="py-10 border-y border-gray-800 bg-[#121212]">
                    <div className="max-w-5xl mx-auto text-center px-4">
                        <p className="text-gray-400 text-sm md:text-base font-semibold uppercase tracking-[0.15em] mb-10">
                            Engineered Using Professional Studio Standards
                        </p>
                        <div className="flex flex-wrap justify-center items-center gap-x-12 gap-y-8 md:gap-x-20 opacity-60 hover:opacity-100 transition-opacity duration-500">
                            <span className="text-xl md:text-2xl font-extrabold text-white tracking-tight">
                                Commercial-Grade Fidelity
                            </span>
                            <span className="text-xl md:text-2xl font-extrabold text-white tracking-tight">
                                High-Key Studio Lighting
                            </span>
                            <span className="text-xl md:text-2xl font-extrabold text-white tracking-tight">
                                Professional Portrait Logic
                            </span>
                            <span className="text-xl md:text-2xl font-extrabold text-white tracking-tight">
                                Stateless Architecture
                            </span>
                        </div>
                    </div>
                </section>

                {/* Features Section */}
                <section className="py-20 bg-[#121212]" id="features">
                    <div className="text-center mb-12">
                        <h2 className="text-white text-3xl font-bold leading-tight tracking-[-0.015em]">{sectionContent.featuresTitle}</h2>
                        <p className="text-gray-300 text-lg font-normal leading-relaxed mt-4 max-w-3xl mx-auto">{sectionContent.featuresSubtitle}</p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
                        {sectionContent.features.map((feature, index) => (
                            <FeatureIcon
                                key={index}
                                icon={
                                    index === 0 ? <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-[#FACC15]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
                                        : index === 1 ? <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-[#FACC15]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                            : index === 2 ? <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-[#FACC15]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                                                : <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-[#FACC15]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v1h2a1 1 0 011 1v3a1 1 0 01-1 1h-2v1a2 2 0 01-2 2H7a2 2 0 01-2-2v-1H3a1 1 0 01-1-1V7a1 1 0 011-1h2V5z" /></svg>
                                }
                                title={feature.title}
                                description={feature.description}
                            />
                        ))}
                    </div>
                </section>



                {/* Pricing Section */}
                <section className="py-20 bg-[#1a1a1a]" id="pricing">
                    <div className="max-w-6xl mx-auto px-4">
                        <div className="text-center mb-12">
                            <h2 className="text-white text-3xl font-bold leading-tight tracking-[-0.015em]">Simple Pricing for Complete Brand Consistency</h2>

                            {/* PRICE ANCHOR */}
                            <p className="text-[#FACC15] text-sm font-bold uppercase tracking-wide mt-4">
                                NO SUBSCRIPTIONS • CREDITS NEVER EXPIRE
                            </p>
                            <p className="text-gray-300 text-lg font-normal leading-relaxed mt-4 max-w-3xl mx-auto">
                                Pay once, lock in your team's unified look, and scale your brand effortlessly.
                            </p>
                            <p className="text-[#FACC15] text-lg font-bold mt-4">
                                After your pilot, choose the rollout size that fits your team.
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">

                            {/* Package 1: The Solo */}
                            <div className="bg-[#121212] p-6 rounded-xl border border-gray-700 flex flex-col hover:border-gray-500 transition-colors">
                                <h3 className="text-[#FACC15] text-xl font-bold mb-2">The Solo</h3>
                                <p className="text-gray-400 text-sm mb-4">Get a studio-grade professional portrait instantly.</p>
                                <p className="text-white text-4xl font-extrabold mb-2">$49</p>

                                <ul className="text-gray-300 text-sm space-y-2 mb-6 border-t border-gray-800 pt-4">
                                    <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> 1 Portrait Credit</li>
                                    <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> 4 Premium Variations</li>
                                    <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> Commercial Usage Rights</li>
                                    <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> Stateless Privacy Processing</li>
                                </ul>

                                <div className="space-y-3 mb-4 mt-auto">
                                    <h4 className="text-white text-sm font-bold">Ideal for:</h4>
                                    <ul className="text-gray-400 text-xs space-y-1">
                                        <li>• Solo Founders</li>
                                        <li>• Independent Consultants</li>
                                    </ul>
                                </div>
                                <button onClick={() => setShowLoginModal(true)} className="w-full bg-gray-700 hover:bg-gray-600 text-white font-bold py-3 px-4 rounded-lg transition-colors">Get Started Now</button>
                            </div>

                            {/* Package 2: The Branding Kit - UPDATED */}
                            <div className="bg-[#121212] p-6 rounded-xl border border-gray-700 flex flex-col hover:border-gray-500 transition-colors">
                                <h3 className="text-[#FACC15] text-xl font-bold mb-2">Branding Kit</h3>
                                <p className="text-gray-400 text-sm mb-4">Multiple professional portraits for firm leadership.</p>
                                <p className="text-white text-4xl font-extrabold mb-2">$200</p>

                                <ul className="text-gray-300 text-sm space-y-2 mb-6 border-t border-gray-800 pt-4">
                                    <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> <strong>5 Portrait Credits</strong> ($40/img)</li>
                                    <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> Multiple Backdrop & Wardrobe Combos</li>
                                    <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> Commercial Usage Rights</li>
                                    <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> Stateless Privacy Processing</li>
                                </ul>

                                <div className="space-y-3 mb-4 mt-auto">
                                    <h4 className="text-white text-sm font-bold">Ideal for:</h4>
                                    <ul className="text-gray-400 text-xs space-y-1">
                                        <li>• Executive Speaker Kits</li>
                                        <li>• PR & Media Portfolios</li>
                                    </ul>
                                </div>
                                <button onClick={() => setShowLoginModal(true)} className="w-full bg-gray-700 hover:bg-gray-600 text-white font-bold py-3 px-4 rounded-lg transition-colors">Get Started Now</button>
                            </div>

                            {/* Package 3: The Team Pack - UPDATED */}
                            <div className="bg-[#121212] p-6 rounded-xl border-2 border-[#FACC15] flex flex-col relative">
                                <div className="absolute top-0 -translate-y-1/2 left-1/2 -translate-x-1/2 bg-[#FACC15] text-black px-3 py-1 text-xs font-bold uppercase rounded-full">Most Popular</div>
                                <h3 className="text-[#FACC15] text-xl font-bold mb-2">Team Pack</h3>
                                <p className="text-gray-400 text-sm mb-4">Unified branding for key teams and departments.</p>
                                <p className="text-white text-4xl font-extrabold mb-2">$380</p>

                                <ul className="text-gray-300 text-sm space-y-2 mb-6 border-t border-gray-800 pt-4">
                                    <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> <strong>10 Portrait Credits</strong> ($38/img)</li>
                                    <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> Shared Credit Pool</li>
                                    <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> Centralized Admin Dashboard</li>
                                    <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> Strict Template Enforcement</li>
                                    <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> 1-Click Automated Invites & Share Credit</li>
                                    <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> Commercial Usage Rights</li>
                                    <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> Stateless Privacy Processing</li>
                                </ul>

                                <div className="space-y-3 mb-4 mt-auto">
                                    <h4 className="text-white text-sm font-bold">Ideal for:</h4>
                                    <ul className="text-gray-400 text-xs space-y-1">
                                        <li>• New Hire Onboarding</li>
                                        <li>• Department Welcome Packages</li>
                                    </ul>
                                </div>
                                <button onClick={() => setShowLoginModal(true)} className="w-full bg-[#FACC15] hover:bg-yellow-400 text-black font-bold py-3 px-4 rounded-lg transition-colors">Get Started Now</button>
                            </div>

                            {/* Package 4: The Business Pack - UPDATED */}
                            <div className="bg-[#121212] p-6 rounded-xl border border-gray-700 flex flex-col hover:border-gray-500 transition-colors">
                                <h3 className="text-[#FACC15] text-xl font-bold mb-2">Business Pack</h3>
                                <p className="text-gray-400 text-sm mb-4">Complete visual infrastructure for scaling organizations.</p>
                                <p className="text-white text-4xl font-extrabold mb-2">$1,700</p>

                                <ul className="text-gray-300 text-sm space-y-2 mb-6 border-t border-gray-800 pt-4">
                                    <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> <strong>50 Portrait Credits</strong> ($34/img)</li>
                                    <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> Shared Credit Pool</li>
                                    <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> Centralized Admin Dashboard</li>
                                    <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> Strict Template Enforcement</li>
                                    <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> 1-Click Automated Invites & Share Credit</li>
                                    <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> Commercial Usage Rights</li>
                                    <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> Stateless Privacy Processing</li>
                                </ul>

                                <div className="space-y-3 mb-4 mt-auto">
                                    <h4 className="text-white text-sm font-bold">Ideal for:</h4>
                                    <ul className="text-gray-400 text-xs space-y-1">
                                        <li>• Company Website Overhauls</li>
                                        <li>• Legacy Team Brand Refreshes</li>
                                    </ul>
                                </div>
                                <button onClick={() => setShowLoginModal(true)} className="w-full bg-gray-700 hover:bg-gray-600 text-white font-bold py-3 px-4 rounded-lg transition-colors">Get Started Now</button>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Privacy Section */}
                <section className="py-20 bg-[#121212]">
                    <div className="max-w-4xl mx-auto text-center">
                        <h2 className="text-white text-3xl font-bold leading-tight tracking-[-0.015em] mb-6">Compliance-Ready Security & Stateless Privacy</h2>
                        <p className="text-gray-300 text-lg font-normal leading-relaxed mb-8 max-w-3xl mx-auto">
                            For regulated industries (Health, Finance, Legal), our zero-retention architecture is a feature, not an afterthought. We never store your data.
                        </p>
                        <div className="grid md:grid-cols-3 gap-8 mt-12">
                            <div className="text-center">
                                <div className="flex items-center justify-center h-16 w-16 rounded-full bg-gray-700 text-[#FACC15] mx-auto mb-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                </div>
                                <h3 className="text-white text-xl font-bold mb-2">Zero-Retention Policy</h3>
                                <p className="text-gray-400">Your uploaded photos and generated headshots are processed in memory and permanently deleted. We don't keep copies.</p>
                            </div>
                            <div className="text-center">
                                <div className="flex items-center justify-center h-16 w-16 rounded-full bg-gray-700 text-[#FACC15] mx-auto mb-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                    </svg>
                                </div>
                                <h3 className="text-white text-xl font-bold mb-2">Secure Processing</h3>
                                <p className="text-gray-400">All processing happens securely in real-time. Your images are never stored on our servers.</p>
                            </div>
                            <div className="text-center">
                                <div className="flex items-center justify-center h-16 w-16 rounded-full bg-gray-700 text-[#FACC15] mx-auto mb-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                    </svg>
                                </div>
                                <h3 className="text-white text-xl font-bold mb-2">Complete Ownership</h3>
                                <p className="text-gray-400">You own 100% of your generated headshots. Use them anywhere with full commercial rights.</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* FAQ Section */}
                <section className="py-20 max-w-3xl mx-auto" id="faq">
                    <h2 className="text-white text-3xl font-bold leading-tight tracking-[-0.015em] text-center mb-8">Frequently Asked Questions</h2>
                    <div className="space-y-4 text-left px-4">

                        {/* Niche-Specific FAQs rendered in the same accordion style */}
                        {content.faqs && content.faqs.length > 0 && content.faqs.map((faq, index) => (
                            <details key={`niche-faq-${index}`} className="bg-[#121212] p-5 rounded-lg border border-gray-700 cursor-pointer group">
                                <summary className="text-white text-lg font-medium leading-normal flex items-center justify-between gap-6 py-2 list-none">
                                    {faq.question}
                                    <svg className="text-white group-open:rotate-180 transition-transform duration-300" fill="currentColor" height="24px" viewBox="0 0 256 256" width="24px" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M213.66,101.66l-80,80a8,8,0,0,1-11.32,0l-80-80A8,8,0,0,1,53.66,90.34L128,164.69l74.34-74.35a8,8,0,0,1,11.32,11.32Z"></path>
                                    </svg>
                                </summary>
                                <p className="text-gray-400 text-base font-normal leading-relaxed pb-2 mt-2">{faq.answer}</p>
                            </details>
                        ))}

                        <details className="bg-[#121212] p-5 rounded-lg border border-gray-700 cursor-pointer group">
                            <summary className="text-white text-lg font-medium leading-normal flex items-center justify-between gap-6 py-2 list-none">
                                How do I invite my team?
                                <svg className="text-white group-open:rotate-180 transition-transform duration-300" fill="currentColor" height="24px" viewBox="0 0 256 256" width="24px" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M213.66,101.66l-80,80a8,8,0,0,1-11.32,0l-80-80A8,8,0,0,1,53.66,90.34L128,164.69l74.34-74.35a8,8,0,0,1,11.32,11.32Z"></path>
                                </svg>
                            </summary>
                            <p className="text-gray-400 text-base font-normal leading-relaxed pb-2 mt-2">It's a completely zero-touch system. As an Admin, you get a dashboard where you can easily send automated email invites to your team members, allocating credits directly to them.</p>
                        </details>

                        <details className="bg-[#121212] p-5 rounded-lg border border-gray-700 cursor-pointer group">
                            <summary className="text-white text-lg font-medium leading-normal flex items-center justify-between gap-6 py-2 list-none">
                                What exactly do my employees have to do?
                                <svg className="text-white group-open:rotate-180 transition-transform duration-300" fill="currentColor" height="24px" viewBox="0 0 256 256" width="24px" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M213.66,101.66l-80,80a8,8,0,0,1-11.32,0l-80-80A8,8,0,0,1,53.66,90.34L128,164.69l74.34-74.35a8,8,0,0,1,11.32,11.32Z"></path>
                                </svg>
                            </summary>
                            <p className="text-gray-400 text-base font-normal leading-relaxed pb-2 mt-2">They receive an email invite and land in a simple interface. No special equipment or lighting is needed. They simply upload a standard selfie taken with their smartphone in any casual environment. They review their generated options, select their favorite, and hit approve.</p>
                        </details>

                        <details className="bg-[#121212] p-5 rounded-lg border border-gray-700 cursor-pointer group">
                            <summary className="text-white text-lg font-medium leading-normal flex items-center justify-between gap-6 py-2 list-none">
                                Can employees change their background or outfit?
                                <svg className="text-white group-open:rotate-180 transition-transform duration-300" fill="currentColor" height="24px" viewBox="0 0 256 256" width="24px" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M213.66,101.66l-80,80a8,8,0,0,1-11.32,0l-80-80A8,8,0,0,1,53.66,90.34L128,164.69l74.34-74.35a8,8,0,0,1,11.32,11.32Z"></path>
                                </svg>
                            </summary>
                            <p className="text-gray-400 text-base font-normal leading-relaxed pb-2 mt-2">No. To ensure a cohesive corporate identity, the admin defines the specific backdrop and professional attire upfront. The system applies this standard to every team member automatically.</p>
                        </details>

                        <details className="bg-[#121212] p-5 rounded-lg border border-gray-700 cursor-pointer group">
                            <summary className="text-white text-lg font-medium leading-normal flex items-center justify-between gap-6 py-2 list-none">
                                How do the final photos get back to the company?
                                <svg className="text-white group-open:rotate-180 transition-transform duration-300" fill="currentColor" height="24px" viewBox="0 0 256 256" width="24px" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M213.66,101.66l-80,80a8,8,0,0,1-11.32,0l-80-80A8,8,0,0,1,53.66,90.34L128,164.69l74.34-74.35a8,8,0,0,1,11.32,11.32Z"></path>
                                </svg>
                            </summary>
                            <p className="text-gray-400 text-base font-normal leading-relaxed pb-2 mt-2">Once an employee approves their headshot, it is instantly centralized and live on your admin dashboard, ready to be downloaded for the company website or internal directories.</p>
                        </details>

                        <details className="bg-[#121212] p-5 rounded-lg border border-gray-700 cursor-pointer group">
                            <summary className="text-white text-lg font-medium leading-normal flex items-center justify-between gap-6 py-2 list-none">
                                What happens to the selfies after the headshot is generated?
                                <svg className="text-white group-open:rotate-180 transition-transform duration-300" fill="currentColor" height="24px" viewBox="0 0 256 256" width="24px" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M213.66,101.66l-80,80a8,8,0,0,1-11.32,0l-80-80A8,8,0,0,1,53.66,90.34L128,164.69l74.34-74.35a8,8,0,0,1,11.32,11.32Z"></path>
                                </svg>
                            </summary>
                            <p className="text-gray-400 text-base font-normal leading-relaxed pb-2 mt-2">The entire process is stateless. Your data is never stored, your images are never used for training, and all assets are purged immediately after the session to maintain absolute privacy.</p>
                        </details>

                        <details className="bg-[#121212] p-5 rounded-lg border border-gray-700 cursor-pointer group">
                            <summary className="text-white text-lg font-medium leading-normal flex items-center justify-between gap-6 py-2 list-none">
                                Do my employees need to create new accounts or passwords?
                                <svg className="text-white group-open:rotate-180 transition-transform duration-300" fill="currentColor" height="24px" viewBox="0 0 256 256" width="24px" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M213.66,101.66l-80,80a8,8,0,0,1-11.32,0l-80-80A8,8,0,0,1,53.66,90.34L128,164.69l74.34-74.35a8,8,0,0,1,11.32,11.32Z"></path>
                                </svg>
                            </summary>
                            <p className="text-gray-400 text-base font-normal leading-relaxed pb-2 mt-2">No. For maximum security and a frictionless experience, our platform utilizes Google Single Sign-On (SSO). Your team members simply log in using their existing Google Workspace or Gmail account with one click—no new passwords to manage or remember.</p>
                        </details>
                    </div>
                </section>
            </main>

            {/* Final CTA Section */}
            <section className="py-24 px-4 relative overflow-hidden">
                <div className="max-w-4xl mx-auto bg-[#121212] border-2 border-[#FACC15] rounded-3xl p-12 md:p-16 text-center shadow-2xl relative z-10">
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
                        Ready to See This With Your Own Team?
                    </h2>
                    <p className="text-lg md:text-xl text-gray-300 mb-10 max-w-2xl mx-auto leading-relaxed">
                        Start a complimentary pilot and test the Virtual Photo Day workflow before rolling it out company-wide.
                    </p>
                    <button
                        onClick={() => navigate('/pilot')}
                        className="group inline-flex items-center justify-center rounded-xl py-4 px-10 bg-[#FACC15] text-[#121212] text-lg font-extrabold hover:bg-yellow-400 transition-all duration-300 ease-out shadow-[0_4px_20px_rgba(250,204,21,0.35)] hover:shadow-[0_8px_30px_rgba(250,204,21,0.6)] hover:scale-[1.02] active:scale-[0.98]"
                    >
                        <span className="flex items-center gap-2">
                            {content.pilotButtonText}
                            <svg className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                            </svg>
                        </span>
                    </button>
                </div>
            </section>

            {/* Footer */}
            <footer className="flex flex-col gap-6 px-5 py-10 text-center bg-[#1a1a1a]">
                <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
                    <button onClick={() => navigate('/terms')} className="text-gray-400 text-base font-normal leading-normal hover:text-white transition-colors">Terms of Service</button>
                    <button onClick={() => navigate('/privacy')} className="text-gray-400 text-base font-normal leading-normal hover:text-white transition-colors">Privacy Policy</button>
                </div>
                <p className="text-gray-500 text-base font-normal leading-normal">&copy; {new Date().getFullYear()} InstantHeadshot.pro. All rights reserved.</p>
            </footer>
        </div>
    );
};

// --- UPDATED Invite Member Modal Component ---
const InviteMemberModal = ({ show, onClose, onInvite, organizationId, isInviting, teamTemplates = [] }) => {
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteCreditLimit, setInviteCreditLimit] = useState(5);
    const [selectedTemplateId, setSelectedTemplateId] = useState('');

    if (!show) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        onInvite(organizationId, inviteEmail, inviteCreditLimit, selectedTemplateId || null);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
            <div className="bg-[#1a1a1a] p-8 rounded-2xl shadow-2xl border border-gray-700 max-w-lg w-full">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-white text-2xl font-bold">Invite New Team Member</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">×</button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                            Member's Email Address
                        </label>
                        <input
                            id="email"
                            type="email"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            placeholder="member@example.com"
                            className="w-full px-4 py-3 bg-[#121212] border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FACC15]"
                            required
                        />
                        <p className="text-xs text-gray-400 mt-1">The user must have a (free) InstantHeadshot.pro account.</p>
                    </div>

                    <div>
                        <label htmlFor="credits" className="block text-sm font-medium text-gray-300 mb-1">
                            Credit Allotment
                        </label>
                        <input
                            id="credits"
                            type="number"
                            value={inviteCreditLimit}
                            onChange={(e) => setInviteCreditLimit(Number(e.target.value))}
                            className="w-full px-4 py-3 bg-[#121212] border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FACC15]"
                            min="0"
                            required
                        />
                        <p className="text-xs text-gray-400 mt-1">Set a limit on how many credits this member can use from the shared pool.</p>
                    </div>

                    {/* --- NEW: Template Assignment --- */}
                    <div>
                        <label htmlFor="template" className="block text-sm font-medium text-gray-300 mb-1">
                            Template Assignment (Optional)
                        </label>
                        <select
                            id="template"
                            value={selectedTemplateId}
                            onChange={(e) => setSelectedTemplateId(e.target.value)}
                            className="w-full px-4 py-3 bg-[#121212] border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#FACC15]"
                        >
                            <option value="">No template restriction</option>
                            {teamTemplates.map(template => (
                                <option key={template.id} value={template.id}>
                                    {template.name} ({template.categoryId})
                                </option>
                            ))}
                        </select>
                        <p className="text-xs text-gray-400 mt-1">
                            {selectedTemplateId ?
                                'Member will be restricted to this template' :
                                'Member can use any template or create custom headshots'
                            }
                        </p>
                    </div>

                    <div className="flex justify-end pt-4">
                        <button
                            type="submit"
                            disabled={isInviting}
                            className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 px-6 rounded-lg transition-colors disabled:bg-gray-600 disabled:text-gray-400 flex items-center justify-center text-base"
                        >
                            {isInviting ? <Spinner small /> : 'Send Invite'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- UPDATED TeamAdminPanel Component ---
const TeamAdminPanel = ({
    organization,
    membership,
    teamCreditsAvailable,
    teamTemplates,
    setTeamTemplates,
    setIsTemplateEditorActive, // <-- ADD THIS
    user, // <-- ADD THIS
    handleAuthSuccess, // <-- ADD THIS
    handleAuthError, // <-- ADD THIS
    showToast // <-- ADD THIS PROP
}) => {
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [isInviting, setIsInviting] = useState(false);
    const [inviteError, setInviteError] = useState(null);
    const [teamMembers, setTeamMembers] = useState([]);
    const [pendingInvites, setPendingInvites] = useState([]);
    // --- ADD REMOVE MEMBER STATE ---
    const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
    const [memberToRemove, setMemberToRemove] = useState(null);
    const [isRemoving, setIsRemoving] = useState(false);
    // --- ADD EDIT CREDIT LIMIT STATE ---
    const [isEditCreditModalOpen, setIsEditCreditModalOpen] = useState(false);
    const [memberToEdit, setMemberToEdit] = useState(null);
    const [isUpdatingCredit, setIsUpdatingCredit] = useState(false);
    // --- ADD SEARCH STATE ---
    const [memberSearch, setMemberSearch] = useState('');
    // --- ADD THIS NEW STATE ---
    const [driveStatus, setDriveStatus] = useState({
        connected: !!organization?.googleDriveAdminEmail,
        email: organization?.googleDriveAdminEmail || ''
    });
    const [isAuthorizing, setIsAuthorizing] = useState(false);

    const isTeamAdmin = membership?.role === 'admin';

    // --- ADD THIS LINE TO FILTER YOURSELF OUT ---
    const membersToDisplay = teamMembers.filter(member => member.role !== 'admin');

    // --- ADD SEARCH FILTER LOGIC ---
    const filteredMembers = membersToDisplay.filter(member =>
        member.name?.toLowerCase().includes(memberSearch.toLowerCase()) ||
        member.email?.toLowerCase().includes(memberSearch.toLowerCase())
    );


    // Pass auth handlers to the hook
    const { requestAuthCode } = useGoogleIdentity({
        onSuccess: (authCode) => {
            handleAuthSuccess(authCode);
            setIsAuthorizing(false); // <-- ADD THIS
        },
        onError: (err) => {
            handleAuthError(err);
            setIsAuthorizing(false); // <-- ADD THIS
        }
    });

    // --- ADD/MODIFY THIS useEffect ---
    // This will update the status if the org doc changes (e.g., from setup)
    useEffect(() => {
        if (organization?.googleDriveAdminEmail) {
            setDriveStatus({
                connected: true,
                email: organization.googleDriveAdminEmail
            });
        }
    }, [organization]);

    // --- NEW: Handle template assignment to member ---
    const handleTemplateAssignment = async (memberId, templateId) => {
        try {
            const token = await user.getIdToken();
            const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:3001' : '';
            const response = await fetch(`${baseUrl}/api/assign-template-to-member`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    memberId: memberId,
                    templateId: templateId || null
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to assign template');
            }

            // Update local state to reflect the change
            setTeamMembers(prev => prev.map(member =>
                member.id === memberId
                    ? { ...member, assignedTemplateId: templateId || null }
                    : member
            ));

            console.log(`✅ Template ${templateId || 'removed'} assigned to member ${memberId}`);
        } catch (error) {
            console.error('Error assigning template to member:', error);
            // TODO: Show error message to user
        }
    };


    // --- NEW: Delete template function ---
    const handleDeleteTemplate = async (templateId) => {
        if (!window.confirm('Are you sure you want to delete this template? This action cannot be undone.')) {
            return;
        }

        try {
            const token = await user.getIdToken();
            const backendUrl = window.location.hostname === 'localhost' ? 'http://localhost:3001' : '';

            const response = await fetch(`${backendUrl}/api/delete-template`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    templateId: templateId
                })
            });

            const data = await response.json();

            if (!response.ok) {
                showToast(data.error || 'Failed to delete template.', 'error');
                throw new Error(data.error || 'Failed to delete template.');
            }
            showToast('Template deleted successfully!', 'success');
            // Success - template will be removed via real-time listener

        } catch (error) {
            console.error("Template deletion error:", error);
            showToast(error.message, 'error');
        }
    };

    // --- UPDATED: Fetch all team members AND their profiles ---
    useEffect(() => {
        if (!organization?.id) return; // Don't fetch if we don't have an org ID

        const membersQuery = query(
            collection(db, 'memberships'),
            where('organizationId', '==', organization.id)
        );

        const unsubscribe = onSnapshot(membersQuery, async (querySnapshot) => {
            // Create an array of promises to fetch each member's profile
            const membersPromises = querySnapshot.docs.map(async (memberDoc) => {
                const memberData = memberDoc.data();
                const userId = memberData.userId;

                try {
                    // Fetch the corresponding profile doc
                    const profileRef = doc(db, 'profiles', userId);
                    // We need getDoc, which is already imported in App.js
                    const profileSnap = await getDoc(profileRef);

                    if (profileSnap.exists()) {
                        // Combine membership data + profile data
                        return {
                            id: memberDoc.id, // Use membership doc id as key
                            ...memberData,
                            email: profileSnap.data().email,
                            name: profileSnap.data().name // (if name exists)
                        };
                    } else {
                        // Profile doesn't exist? Fallback
                        return { id: memberDoc.id, ...memberData, email: "Profile not found" };
                    }
                } catch (error) {
                    console.error("Error fetching profile for member:", userId, error);
                    return { id: memberDoc.id, ...memberData, email: "Error loading profile" };
                }
            });

            // Wait for all profile fetches to complete
            const membersList = await Promise.all(membersPromises);
            setTeamMembers(membersList);

        }, (error) => {
            console.error("Error fetching team members:", error);
        });

        return () => unsubscribe();
    }, [organization?.id]); // Re-run if org ID changes

    // --- NEW: Fetch pending invitations ---
    useEffect(() => {
        if (!organization?.id || !isTeamAdmin) return;

        const invitesQuery = query(
            collection(db, 'invites'),
            where('organizationId', '==', organization.id),
            where('status', '==', 'pending')
        );

        const unsubscribe = onSnapshot(invitesQuery, (querySnapshot) => {
            const invitesList = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setPendingInvites(invitesList);
        }, (error) => {
            console.error("Error fetching pending invites:", error);
        });

        return () => unsubscribe();
    }, [organization?.id, isTeamAdmin]);

    // --- NEW: Cancel invitation function ---
    const cancelInvitation = async (inviteId) => {
        try {
            const token = await user.getIdToken();
            const backendUrl = window.location.hostname === 'localhost' ? 'http://localhost:3001' : '';

            const response = await fetch(`${backendUrl}/api/cancel-invite`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    inviteId: inviteId
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to cancel invitation.');
            }

            // Success - the invite will be removed from pendingInvites via the real-time listener
        } catch (error) {
            console.error("Cancel invitation error:", error);
            setInviteError(error.message);
        }
    };

    if (!organization || !membership) {
        return null; // Don't render if not on a team
    }

    // --- UPDATED: handleInviteSubmit function with template support ---
    const handleInviteSubmit = async (organizationId, inviteEmail, creditLimit, templateId = null) => {
        setIsInviting(true);
        setInviteError(null);

        try {
            const token = await user.getIdToken();
            const backendUrl = window.location.hostname === 'localhost' ? 'http://localhost:3001' : '';

            const response = await fetch(`${backendUrl}/api/invite-member`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    organizationId: organizationId,
                    inviteEmail: inviteEmail,
                    creditLimit: creditLimit,
                    assignedTemplateId: templateId
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to send invite.');
            }

            setIsInviteModalOpen(false); // Close modal on success
            showToast(`Invite sent to ${inviteEmail}!`, 'success');

        } catch (error) {
            console.error("Invite error:", error);
            setInviteError(error.message);
        } finally {
            setIsInviting(false);
        }
    };

    // --- NEW: Remove member function ---
    const handleConfirmRemove = async (memberId) => {
        if (!memberId) return;

        setIsRemoving(true);

        try {
            const token = await user.getIdToken();
            const backendUrl = window.location.hostname === 'localhost' ? 'http://localhost:3001' : '';

            const response = await fetch(`${backendUrl}/api/remove-member`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    memberId: memberId, // This is the ID of the membership doc
                    organizationId: organization.id
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to remove member.');
            }

            // Success! Close the modal.
            setIsRemoveModalOpen(false);
            setMemberToRemove(null);
            showToast('Member removed successfully!', 'success');
            // The real-time listener will automatically remove the user from the UI.

        } catch (error) {
            console.error("Remove member error:", error);
            // TODO: Show this error in the modal
        } finally {
            setIsRemoving(false);
        }
    };

    // --- NEW: Update credit limit function ---
    const handleUpdateCreditLimit = async (newLimit) => {
        if (!memberToEdit) return;

        setIsUpdatingCredit(true);
        try {
            const token = await user.getIdToken();
            const backendUrl = window.location.hostname === 'localhost' ? 'http://localhost:3001' : '';

            const response = await fetch(`${backendUrl}/api/update-member-limit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    memberId: memberToEdit.id,
                    organizationId: organization.id,
                    newLimit: parseInt(newLimit)
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to update credit limit');
            }

            // Update local state to reflect the new credit limit
            setTeamMembers(prev => prev.map(member =>
                member.id === memberToEdit.id
                    ? { ...member, allottedCredits: parseInt(newLimit) }
                    : member
            ));
            setIsEditCreditModalOpen(false);
            setMemberToEdit(null);
            showToast('Credit limit updated successfully!', 'success');

        } catch (error) {
            console.error('Error updating credit limit:', error);
            showToast(`Failed to update credit limit: ${error.message}`, 'error');
        } finally {
            setIsUpdatingCredit(false);
        }
    };

    // --- This is the UPDATED RENDER logic ---
    return (
        <>
            {/* --- ADD REMOVE MEMBER CONFIRMATION MODAL --- */}
            {isRemoveModalOpen && memberToRemove && (
                <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
                    <div className="bg-[#1a1a1a] p-8 rounded-2xl shadow-2xl border border-red-700 max-w-lg w-full">
                        <h3 className="text-white text-2xl font-bold mb-4">Are you sure?</h3>
                        <p className="text-gray-300 mb-6">
                            Are you sure you want to remove <strong className="text-white">{memberToRemove.name || memberToRemove.email}</strong> from the team?
                            <br />
                            This will revoke their access to team credits and templates. This action cannot be undone.
                        </p>
                        <div className="flex justify-end space-x-4">
                            <button
                                onClick={() => setIsRemoveModalOpen(false)}
                                className="px-6 py-3 bg-gray-600 hover:bg-gray-500 text-white font-bold rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleConfirmRemove(memberToRemove.id)}
                                disabled={isRemoving}
                                className="px-6 py-3 bg-red-600 hover:bg-red-500 text-white font-bold rounded-lg transition-colors disabled:bg-gray-600 disabled:text-gray-400"
                            >
                                {isRemoving ? 'Removing...' : 'Confirm Remove'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- ADD EDIT CREDIT LIMIT MODAL --- */}
            {isEditCreditModalOpen && memberToEdit && (
                <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
                    <div className="bg-[#1a1a1a] p-8 rounded-2xl shadow-2xl border border-blue-700 max-w-lg w-full">
                        <h3 className="text-white text-2xl font-bold mb-4">Update Credit Limit</h3>
                        <p className="text-gray-300 mb-4">
                            Update credit limit for <strong className="text-white">{memberToEdit.name || memberToEdit.email}</strong>
                        </p>
                        <div className="mb-6">
                            <label className="block text-gray-300 text-sm font-medium mb-2">
                                New Credit Limit
                            </label>
                            <input
                                type="number"
                                min="0"
                                max="1000"
                                defaultValue={memberToEdit.allottedCredits || 0}
                                className="w-full px-4 py-3 bg-[#2a2a2a] border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                                placeholder="Enter credit limit"
                                id="creditLimitInput"
                            />
                        </div>
                        <div className="flex justify-end space-x-4">
                            <button
                                onClick={() => setIsEditCreditModalOpen(false)}
                                className="px-6 py-3 bg-gray-600 hover:bg-gray-500 text-white font-bold rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    const input = document.getElementById('creditLimitInput');
                                    const newLimit = input?.value || '0';
                                    handleUpdateCreditLimit(newLimit);
                                }}
                                disabled={isUpdatingCredit}
                                className="px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-lg transition-colors disabled:bg-gray-600 disabled:text-gray-400"
                            >
                                {isUpdatingCredit ? 'Updating...' : 'Update Limit'}
                            </button>
                        </div>
                    </div>
                </div>
            )}


            <InviteMemberModal
                show={isInviteModalOpen}
                onClose={() => setIsInviteModalOpen(false)}
                onInvite={handleInviteSubmit}
                organizationId={organization.id} // Pass the org ID
                isInviting={isInviting}
                teamTemplates={teamTemplates}
            />


            {/* Main panel content - now full width */}
            <div className="bg-[#1a1a1a] p-6 rounded-xl border border-gray-700 space-y-6">

                {inviteError && <div className="bg-red-900 border border-red-700 text-red-100 px-4 py-3 rounded-lg relative" role="alert">{inviteError}</div>}

                {/* --- ZERO STATE ONBOARDING GUIDE --- */}
                {isTeamAdmin && teamTemplates.length === 0 && !driveStatus.connected && (
                    <div className="bg-gradient-to-r from-blue-900/40 to-purple-900/40 border border-blue-500/50 rounded-xl p-6 relative overflow-hidden">
                        <div className="absolute -right-10 -top-10 opacity-10">
                            <svg width="150" height="150" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm-3 17v-10l9 5.146-9 4.854z" /></svg>
                        </div>
                        <h2 className="text-2xl font-bold text-white mb-2 relative z-10">👋 Welcome to your Team Workspace!</h2>
                        <p className="text-blue-200 mb-6 font-medium relative z-10">To initialize your team's professional headshot engine, follow these 3 quick steps:</p>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative z-10">
                            <div className="bg-[#121212]/80 p-5 rounded-lg border border-blue-800 relative backdrop-blur-sm">
                                <div className="absolute -top-3 -left-3 bg-[#FACC15] text-[#121212] w-8 h-8 flex items-center justify-center rounded-full font-bold shadow-lg">1</div>
                                <h3 className="text-white font-bold mb-2 ml-2 mt-1">Connect Drive</h3>
                                <p className="text-gray-400 text-sm">Gain total data ownership. Sync your Google Drive so your team's generated photos physically back up to a central folder you control.</p>
                            </div>

                            <div className="bg-[#121212]/80 p-5 rounded-lg border border-blue-800 relative backdrop-blur-sm">
                                <div className="absolute -top-3 -left-3 bg-[#FACC15] text-[#121212] w-8 h-8 flex items-center justify-center rounded-full font-bold shadow-lg">2</div>
                                <h3 className="text-white font-bold mb-2 ml-2 mt-1">Create Template</h3>
                                <p className="text-gray-400 text-sm">
                                    {(organization?.niche || '').toLowerCase().includes('medical') || (organization?.niche || '').toLowerCase().includes('health') || (organization?.niche || '').toLowerCase().includes('spa')
                                        ? "Lock in your brand identity. Upload your clinic's custom logo to seamlessly unify your team's medical scrubs."
                                        : "Lock in your brand identity. Upload your firm's logo or select a specific background preset (like a modern office) to unify your team."}
                                </p>
                            </div>

                            <div className="bg-[#121212]/80 p-5 rounded-lg border border-blue-800 relative backdrop-blur-sm">
                                <div className="absolute -top-3 -left-3 bg-[#FACC15] text-[#121212] w-8 h-8 flex items-center justify-center rounded-full font-bold shadow-lg">3</div>
                                <h3 className="text-white font-bold mb-2 ml-2 mt-1">Invite Members</h3>
                                <p className="text-gray-400 text-sm">Send secure invites to your coworkers. You can permanently bind them to a specific template and legally limit the exact number of credits they burn.</p>
                            </div>
                        </div>
                    </div>
                )}
                {/* --- END ZERO STATE ONBOARDING GUIDE --- */}

                {/* --- SECTION 1: TEAM INFO & ACTIONS --- */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-[#2a2a2a] p-4 rounded-lg">
                        <h4 className="text-sm font-semibold text-gray-400 mb-1">Team Name</h4>
                        <p className="text-white font-medium">{organization.orgName}</p>
                    </div>
                    <div className="bg-[#2a2a2a] p-4 rounded-lg">
                        <h4 className="text-sm font-semibold text-gray-400 mb-1">Team Shared Pool</h4>
                        <p className="text-blue-400 font-bold text-lg">{organization.sharedCredits || 0} Credits</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* --- NEW OUTLINE BUTTON STYLE --- */}
                    <button
                        onClick={() => setIsInviteModalOpen(true)}
                        className="w-full bg-transparent border border-blue-500 text-blue-400 hover:bg-blue-500 hover:text-white font-bold py-3 px-4 rounded-lg transition-colors"
                    >
                        Invite Team Members
                    </button>
                    {/* --- NEW OUTLINE BUTTON STYLE --- */}
                    <button
                        onClick={() => setIsTemplateEditorActive(true)}
                        disabled={!organization}
                        className="w-full bg-transparent border border-[#FACC15] text-[#FACC15] hover:bg-[#FACC15] hover:text-black font-bold py-3 px-4 rounded-lg transition-colors"
                    >
                        Create Template
                    </button>
                </div>

                {/* --- SECTION 2: INTEGRATIONS --- */}
                <div className="border-t border-gray-700 pt-4 mt-4">
                    <h4 className="text-lg font-semibold text-white mb-3">Integrations</h4>
                    <div className="bg-[#2a2a2a] p-4 rounded-lg border border-gray-600">
                        <div className="flex justify-between items-center">
                            <div>
                                <h5 className="font-bold text-white">Google Drive</h5>
                                {driveStatus.connected ? (
                                    <p className="text-sm text-green-400">
                                        Connected as: {driveStatus.email}
                                    </p>
                                ) : (
                                    <p className="text-sm text-gray-400">
                                        Not Connected
                                    </p>
                                )}
                            </div>
                            {!driveStatus.connected && (
                                <button
                                    onClick={requestAuthCode}
                                    disabled={isAuthorizing}
                                    className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-2 px-4 rounded-lg transition-colors disabled:opacity-50"
                                >
                                    {isAuthorizing ? <Spinner small /> : 'Connect Drive'}
                                </button>
                            )}
                            {/* TODO: Add a "Disconnect" button here */}
                        </div>
                        <p className="text-xs text-gray-400 mt-2">
                            Connect your team's Google Drive to automatically save all
                            generated headshots to a central, shared folder.
                        </p>
                    </div>
                </div>

                {/* --- REORDERED SECTION 3: Team Templates --- */}
                <div className="border-t border-gray-700 pt-4 mt-4">
                    <h4 className="text-lg font-semibold text-white mb-3">Team Templates ({teamTemplates.length})</h4>
                    {teamTemplates.length === 0 ? (
                        <div className="bg-[#2a2a2a] p-4 rounded-lg text-center">
                            <p className="text-gray-400 text-sm">No templates created yet.</p>
                            <p className="text-gray-500 text-xs mt-1">Create your first template to restrict member options.</p>
                        </div>
                    ) : (
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                            {teamTemplates.map(template => (
                                <div key={template.id} className="bg-[#2a2a2a] p-3 rounded-lg border border-gray-600">
                                    <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                            <h5 className="text-white font-medium text-sm">{template.name}</h5>
                                            {template.description && (
                                                <p className="text-gray-400 text-xs mt-1">{template.description}</p>
                                            )}
                                            <div className="flex flex-wrap gap-2 mt-2">
                                                <span className="bg-gray-700 text-gray-300 text-xs px-2 py-1 rounded">
                                                    {template.categoryId}
                                                </span>
                                                <span className="bg-gray-700 text-gray-300 text-xs px-2 py-1 rounded">
                                                    {template.presetId}
                                                </span>
                                                <span className="bg-gray-700 text-gray-300 text-xs px-2 py-1 rounded">
                                                    {template.aspectRatio}
                                                </span>
                                                {template.logoUrl && (
                                                    <span className="bg-green-700 text-green-300 text-xs px-2 py-1 rounded">
                                                        Has Logo
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleDeleteTemplate(template.id)}
                                            className="ml-2 px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-500 flex-shrink-0"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* --- REORDERED SECTION 3: Pending Invites --- */}
                {pendingInvites.length > 0 && (
                    <div className="border-t border-gray-700 pt-4 mt-4">
                        <h4 className="text-lg font-semibold text-white mb-3">Pending Invitations ({pendingInvites.length})</h4>
                        <div className="space-y-3 max-h-60 overflow-y-auto">
                            {pendingInvites.map(invite => (
                                <div key={invite.id} className="bg-yellow-900/20 border border-yellow-700 p-3 rounded-lg">
                                    <div className="flex justify-between items-center">
                                        <span className="text-white font-medium text-sm truncate">
                                            {invite.email}
                                        </span>
                                        <button
                                            onClick={() => cancelInvitation(invite.id)}
                                            className="px-2 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-500"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                    <p className="text-xs text-yellow-400 mt-1">
                                        Invited: {invite.createdAt ? new Date(invite.createdAt.seconds * 1000).toLocaleDateString() : 'Unknown date'}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* --- REORDERED SECTION 4: Team Members (Filtered) --- */}
                <div className="border-t border-gray-700 pt-4 mt-4">
                    {/* --- UPDATED COUNT --- */}
                    <h4 className="text-lg font-semibold text-white mb-3">Team Members ({membersToDisplay.length})</h4>

                    {/* --- ADD SEARCH INPUT --- */}
                    <div className="mb-4">
                        <input
                            type="text"
                            placeholder="Search members by name or email..."
                            value={memberSearch}
                            onChange={(e) => setMemberSearch(e.target.value)}
                            className="w-full bg-[#2a2a2a] border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-[#FACC15]"
                        />
                    </div>

                    {/* --- ADD EMPTY STATE --- */}
                    {filteredMembers.length === 0 ? (
                        <div className="bg-[#2a2a2a] p-4 rounded-lg text-center">
                            <p className="text-gray-400 text-sm">No members have joined yet.</p>
                            <p className="text-gray-500 text-xs mt-1">
                                Use the "Invite Team Members" button to add people.
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                            {/* --- UPDATED MAP --- */}
                            {filteredMembers.map(member => (
                                <div key={member.id} className="bg-[#2a2a2a] p-3 rounded-lg">
                                    <div className="flex justify-between items-center">
                                        <div className="flex items-center gap-2 truncate">
                                            <span className="text-white font-medium text-sm truncate">
                                                {member.name || member.email}
                                            </span>
                                            {member.name && member.email && (
                                                <span className="text-gray-400 text-xs truncate">
                                                    ({member.email})
                                                </span>
                                            )}
                                        </div>
                                        {/* --- NEW WRAPPER FOR ROLE AND REMOVE BUTTON --- */}
                                        <div className="flex items-center space-x-2">
                                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0 bg-gray-600 text-gray-200`}>
                                                {member.role}
                                            </span>
                                            {/* --- NEW EDIT LIMIT BUTTON --- */}
                                            <button
                                                onClick={() => {
                                                    setMemberToEdit(member);
                                                    setIsEditCreditModalOpen(true);
                                                }}
                                                className="text-xs text-blue-500 hover:text-blue-400 font-medium"
                                                title={`Edit credit limit for ${member.name || member.email}`}
                                            >
                                                Edit Limit
                                            </button>
                                            {/* --- END EDIT LIMIT BUTTON --- */}
                                            {/* --- NEW REMOVE BUTTON --- */}
                                            <button
                                                onClick={() => {
                                                    setMemberToRemove(member);
                                                    setIsRemoveModalOpen(true);
                                                }}
                                                className="text-xs text-red-500 hover:text-red-400 font-medium"
                                                title={`Remove ${member.name || member.email}`}
                                            >
                                                Remove
                                            </button>
                                            {/* --- END REMOVE BUTTON --- */}
                                        </div>
                                        {/* --- END WRAPPER --- */}
                                    </div>

                                    <div className="mt-2 space-y-2">
                                        <p className="text-xs text-gray-400">
                                            Used: {member.usedCredits} / {member.allottedCredits} credits
                                        </p>
                                        {/* --- THIS IS THE FIX for the "Dead" Dropdown --- */}
                                        <div className="flex items-center space-x-2">
                                            <label className="text-xs text-gray-300">Template:</label>
                                            {teamTemplates.length > 0 ? (
                                                <select
                                                    value={member.assignedTemplateId || ''}
                                                    onChange={(e) => handleTemplateAssignment(member.id, e.target.value)}
                                                    className="text-xs bg-[#1a1a1a] border border-gray-600 rounded px-2 py-1 text-white"
                                                >
                                                    <option value="">No Template</option>
                                                    {teamTemplates.map(template => (
                                                        <option key={template.id} value={template.id}>
                                                            {template.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <span className="text-xs text-gray-500">No templates available</span>
                                            )}
                                        </div>
                                        {/* --- END FIX --- */}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>


                {/* Member-specific view */}
                {membership.role === 'member' && (
                    <div className="border-t border-gray-700 pt-4 space-y-3">
                        <p className="text-sm text-gray-300">
                            Your Allotted Credits: <span className="font-bold text-white">{membership.allottedCredits || 0}</span>
                        </p>
                        <p className="text-sm text-gray-300">
                            Your Used Credits: <span className="font-bold text-white">{membership.usedCredits || 0}</span>
                        </p>
                        <p className="text-lg text-[#FACC15]">
                            Your Available Credits: <span className="font-bold text-white">{teamCreditsAvailable || 0}</span>
                        </p>
                    </div>
                )}
            </div>
        </>
    );
};


const Dashboard = React.memo(({
    sessionGeneratedImages,
    generationAttempts,
    handleCreditDeduction,
    imageExpirations,
    onSessionExpired,
    profile,
    activeMode,
    setActiveMode,
    membership,
    organization,
    teamCreditsAvailable,
    pricingView,
    setPricingView,
    refreshDriveGallery,
    teamTemplates,
    setTeamTemplates,
    setIsTemplateEditorActive,
    user,
    handleStripePayment,
    isProcessingPayment,
    setError,
    setShowHeadshotGenerator,
    googleDriveToken,
    handleSaveToDrive,
    error,
    handleAuthSuccess,
    handleAuthError,

    // --- ADD THIS PROP ---
    showToast,

    // --- ADD THESE MISSING PROPS ---
    isFetchingDrive,
    driveImages,
    generatedImages,
    isLoading,
    handleGenerateBio,
    isTextLoading,
    linkedInBio,
    // --- END OF ADDITIONS ---

    // --- ADD MODE PERSISTENCE PROP ---
    userManuallyChangedMode
}) => {
    // Remove local state - use the global state from App component
    const [currentTime, setCurrentTime] = useState(Date.now()); // Local ticker for real-time updates

    // --- ADD THIS NEW STATE FOR TABBED INTERFACE ---
    const [adminView, setAdminView] = useState('gallery');

    // Automatically switch to Team Admin tab exactly once if they log into an empty Team Workspace
    useEffect(() => {
        if (activeMode === 'team' && membership?.role === 'admin' && teamTemplates?.length === 0) {
            setAdminView('team');
        }
    }, [activeMode, membership?.role, teamTemplates?.length]); // Strict dependency limits the override
    // --- END NEW STATE ---

    // Local ticker to force re-renders for download button disabling and stuck state detection
    useEffect(() => {
        if (sessionGeneratedImages.length === 0) return; // Don't run timer if no images

        // This timer just forces this component to re-render to check expiration
        const timer = setInterval(() => {
            const now = Date.now();
            setCurrentTime(now);

            // --- 2. ADD STUCK STATE DETECTION ---
            if (generationAttempts >= 4 && sessionGeneratedImages.length > 0) {
                const allExpired = sessionGeneratedImages.every(img => { // <-- img is object
                    const expTime = imageExpirations[img.id]; // <-- Check by img.id
                    return !expTime || now > expTime;
                });

                if (allExpired) {
                    console.log("Dashboard: Detected all images expired.");
                    onSessionExpired();
                    clearInterval(timer); // Stop this timer
                }
            }
            // --- END OF STUCK STATE DETECTION ---

        }, 1000); // Check every second

        return () => clearInterval(timer);
    }, [sessionGeneratedImages, generationAttempts, imageExpirations, onSessionExpired]); // <-- 3. ADD DEPENDENCIES

    // TEAM-AWARE CREDIT CHECK HELPER (useCallback for stable reference)
    const hasSufficientCredits = useCallback(() => {
        if (activeMode === 'team') {
            return teamCreditsAvailable >= 1;
        } else {
            return profile?.credits >= 1;
        }
    }, [activeMode, teamCreditsAvailable, profile?.credits]);

    const getAvailableCredits = useCallback(() => {
        if (activeMode === 'team') {
            return teamCreditsAvailable;
        } else {
            return profile?.credits || 0;
        }
    }, [activeMode, teamCreditsAvailable, profile?.credits]);

    // --- VIRAL PLG STATE ---
    const [showSignatureModal, setShowSignatureModal] = useState(false);
    const [showLinkedInModal, setShowLinkedInModal] = useState(false);

    const handleStartHeadshotGeneration = () => {
        if (!hasSufficientCredits()) {
            setError("You need at least 1 credit to generate headshots.");
            return;
        }
        setShowHeadshotGenerator(true);
    };

    // NEW: Handler for downloading from the gallery
    const handleGalleryDownload = async (image) => { // <-- 1. Receive 'image' object
        // Mark image as downloaded regardless of source

        // If Google Drive is connected, save to Drive instead of local download
        if (googleDriveToken) {
            const success = await handleSaveToDrive(image); // <-- 2. Pass 'image' object
            if (success) {
                // Refresh the Drive gallery to show the newly saved image
                await refreshDriveGallery();
                return;
            }
        }

        // Fallback to local download if Drive save fails or Drive not connected
        // Only deduct credit if the image is from the current session
        // Images in history are already "paid for"
        if (sessionGeneratedImages.some(sessionImg => sessionImg.id === image.id)) { // <-- 3. Check by id
            if (hasSufficientCredits()) {
                // First refresh the Drive gallery to ensure it's up to date
                if (googleDriveToken) {
                    await refreshDriveGallery();
                }
                // Then use secure download flow to get the clean image
                const success = await handleSaveToDrive(image); // <-- 4. Pass 'image' object
                if (!success) {
                    setError("Failed to download image. Please try again.");
                }
            } else {
                setError("You need at least 1 credit to download images.");
                return;
            }
        } else {
            // For history images, just download without credit deduction
            const link = document.createElement('a');
            link.href = image.url; // <-- 5. Use image.url
            link.download = `headshot-${Date.now()}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    // --- ADD THIS FUNCTION ---
    const handleSignOutEverywhere = async () => {
        if (!window.confirm("Are you sure? This will sign you out of all devices immediately.")) return;

        try {
            const token = await auth.currentUser.getIdToken();
            // Use your dynamic baseUrl logic here
            const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:3001' : '';

            await fetch(`${baseUrl}/api/auth/revoke-all-sessions`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            alert("Success. You will be signed out now.");
            auth.signOut(); // Sign out locally immediately
        } catch (err) {
            console.error(err);
            alert("Failed to sign out everywhere.");
        }
    };
    // --- END ADDITION ---

    return (
        <div className="w-full min-h-screen bg-[#121212] text-white">
            <header className="flex items-center p-4 justify-between bg-[#121212] sticky top-0 z-10 border-b border-gray-800">
                {/* Left Side: Logo */}
                <div className="flex items-center flex-1 pl-4 space-x-3">
                    <img
                        src="/InstantHeadshotPro logo.png"
                        alt="InstantHeadshot.pro"
                        className="h-10 w-auto bg-white p-1 rounded"
                    />
                    {/* Visible on all devices */}
                    <h2 className="text-white text-lg font-bold leading-tight tracking-[-0.015em]">
                        InstantHeadshot.pro
                    </h2>
                </div>

                {/* Right Side: Actions */}
                <div className="flex items-center gap-3 sm:space-x-4">
                    <span className="font-semibold text-gray-300 hidden lg:block text-sm">{user?.email}</span>

                    {/* "Sign Out Everywhere" - Icon on Mobile, Text on Desktop */}
                    <button
                        onClick={handleSignOutEverywhere}
                        className="text-red-400 hover:text-red-300 transition-colors flex items-center gap-2"
                        title="Revoke access on all other devices"
                    >
                        {/* Mobile Icon (Devices with X) */}
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 sm:hidden">
                            <path d="M10.5 18.75a.75.75 0 000 1.5h3a.75.75 0 000-1.5h-3z" />
                            <path fillRule="evenodd" d="M8.625 1.5a2.25 2.25 0 00-2.25 2.25v16.5A2.25 2.25 0 008.625 22.5h6.75a2.25 2.25 0 002.25-2.25V3.75A2.25 2.25 0 0015.375 1.5h-6.75zM10 3.75a.75.75 0 01.75-.75h2.5a.75.75 0 010 1.5h-2.5a.75.75 0 01-.75-.75zm9.22 4.22a.75.75 0 011.06 0l1.5 1.5a.75.75 0 01-1.06 1.06l-1.5-1.5a.75.75 0 010-1.06zM2.22 7.97a.75.75 0 011.06 0l1.5 1.5a.75.75 0 01-1.06 1.06l-1.5-1.5a.75.75 0 010-1.06z" clipRule="evenodd" />
                        </svg>
                        {/* Desktop Text */}
                        <span className="hidden sm:inline text-sm font-bold leading-normal tracking-[0.015em]">Sign Out Everywhere</span>
                    </button>

                    {/* "Sign Out" - Icon on Mobile, Text on Desktop */}
                    <button
                        onClick={() => auth.signOut()}
                        className="text-[#FACC15] hover:text-yellow-400 transition-colors flex items-center gap-2"
                        title="Sign Out"
                    >
                        {/* Mobile Icon (Logout) */}
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 sm:hidden">
                            <path fillRule="evenodd" d="M7.5 3.75A1.5 1.5 0 006 5.25v13.5a1.5 1.5 0 001.5 1.5h6a1.5 1.5 0 001.5-1.5V15a.75.75 0 011.5 0v3.75a3 3 0 01-3 3h-6a3 3 0 01-3-3V5.25a3 3 0 013-3h6a3 3 0 013 3V9A.75.75 0 0115 9V5.25a1.5 1.5 0 00-1.5-1.5h-6zm10.72 4.72a.75.75 0 011.06 0l3 3a.75.75 0 010 1.06l-3 3a.75.75 0 11-1.06-1.06l1.72-1.72H9a.75.75 0 010-1.5h10.94l-1.72-1.72a.75.75 0 010-1.06z" clipRule="evenodd" />
                        </svg>
                        {/* Desktop Text */}
                        <span className="hidden sm:inline text-base font-bold leading-normal tracking-[0.015em]">Sign Out</span>
                    </button>
                </div>
            </header>
            <main className="container mx-auto px-6 py-8">
                {/* --- NEW: Mobile-friendly User Greeting --- */}
                <div className="mb-6 flex justify-between items-end border-b border-gray-800 pb-4">
                    <div>
                        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
                        <p className="text-gray-400 text-sm mt-1">
                            Logged in as <span className="text-[#FACC15]">{user?.email}</span>
                        </p>
                    </div>
                </div>
                {/* --- END NEW --- */}

                {error && <div className="bg-red-900 border border-red-700 text-red-100 px-4 py-3 rounded-lg relative mb-6" role="alert">{error}</div>}
                {!hasSufficientCredits() && (
                    <div className="bg-red-900 border border-red-700 text-red-100 px-4 py-3 rounded-lg relative mb-6" role="alert">
                        You need at least 1 credit to generate headshots
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Left Column: Controls */}
                    <div className="lg-col-span-1 bg-[#1a1a1a] p-6 rounded-xl border border-gray-700 space-y-6 h-fit">

                        {/* --- AVAILABLE CREDITS DISPLAY (NEW) --- */}
                        <div className="space-y-3">
                            <h4 className="text-sm font-semibold text-white mb-2">Available Credits</h4>
                            {/* MODIFICATION: Changed from space-y-2 */}
                            <div className="flex space-x-2">
                                {/* Always show Personal Credits */}
                                {/* MODIFICATION: Added flex-1 */}
                                <div className="flex-1 flex justify-between items-center bg-[#2a2a2a] p-3 rounded-lg border border-gray-700">
                                    <span className="text-base font-medium text-gray-300">Personal Pool</span>
                                    <span className="text-lg font-bold text-[#FACC15]">
                                        {profile?.credits || 0}
                                    </span>
                                </div>

                                {/* Conditionally show Team Credits */}
                                {organization && (
                                    /* MODIFICATION: Added flex-1 */
                                    <div className="flex-1 flex justify-between items-center bg-[#2a2a2a] p-3 rounded-lg border border-gray-700">
                                        <span className="text-base font-medium text-gray-300">Team Pool</span>
                                        <span className="text-lg font-bold text-blue-400">
                                            {teamCreditsAvailable}
                                        </span>
                                    </div>
                                )}
                            </div>
                            {/* NEW: Show active credits being used */}
                            <div className="bg-[#2a2a2a] p-3 rounded-lg border border-[#FACC15]">
                                <div className="flex justify-between items-center">
                                    <span className="text-base font-medium text-white">Active Credits</span>
                                    <span className="text-lg font-bold text-[#FACC15]">
                                        {getAvailableCredits()}
                                    </span>
                                </div>
                                <p className="text-xs text-gray-400 mt-1">
                                    {activeMode === 'team' ? 'Using team credits' : 'Using personal credits'}
                                </p>
                            </div>
                        </div>
                        {/* --- END AVAILABLE CREDITS (NEW) --- */}

                        {/* Active Mode Switcher */}
                        {organization && (
                            <div className="bg-[#2a2a2a] rounded-lg p-3 border border-gray-600">
                                <h4 className="text-sm font-semibold text-white mb-2">Active Credit Pool</h4>
                                <div className="flex space-x-2">
                                    <button
                                        onClick={() => {
                                            setActiveMode('personal');
                                            userManuallyChangedMode.current = true;
                                            setAdminView('gallery');
                                        }}
                                        className={`flex-1 py-2 px-3 rounded text-sm font-medium transition-colors ${activeMode === 'personal'
                                            ? 'bg-[#FACC15] text-black'
                                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                            }`}
                                    >
                                        Personal
                                    </button>
                                    <button
                                        onClick={() => {
                                            setActiveMode('team');
                                            userManuallyChangedMode.current = true;
                                        }}
                                        className={`flex-1 py-2 px-3 rounded text-sm font-medium transition-colors ${activeMode === 'team'
                                            ? 'bg-blue-500 text-white'
                                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                            }`}
                                    >
                                        Team
                                    </button>
                                </div>
                                <p className="text-xs text-gray-400 mt-2">
                                    Currently using: <span className="font-semibold text-white">
                                        {activeMode === 'team' ? 'Team Pool' : 'Personal Wallet'}
                                    </span>
                                </p>
                            </div>
                        )}


                        {/* Stripe Payment Options */}
                        <div className="space-y-4">
                            <h4 className="text-sm font-semibold text-white">Purchase Credits</h4>

                            {/* --- THIS IS THE FIX --- */}
                            {/* Show purchase toggles ONLY if user is NOT a team member, OR they are an ADMIN */}
                            {(membership?.role !== 'member') ? (
                                <>
                                    {/* --- NEW PRICING TOGGLE (Overrides disabled for solo users) --- */}
                                    <div className="flex bg-[#121212] border border-gray-700 rounded-lg p-1">
                                        <button
                                            onClick={() => setPricingView('individual')}
                                            disabled={membership && activeMode === 'team'} // <--- FIX APPLIED
                                            className={`w-1/2 py-2 px-3 rounded-md text-sm font-bold transition-all ${pricingView === 'individual' ? 'bg-[#FACC15] text-black' : 'text-gray-400 hover:text-white'
                                                } ${membership && activeMode === 'team' ? 'opacity-50 cursor-not-allowed' : ''}`} // <--- FIX APPLIED
                                        >
                                            For Myself
                                        </button>
                                        <button
                                            onClick={() => setPricingView('team')}
                                            disabled={membership && activeMode === 'personal'} // <--- FIX APPLIED
                                            className={`w-1/2 py-2 px-3 rounded-md text-sm font-bold transition-all ${pricingView === 'team' ? 'bg-[#FACC15] text-black' : 'text-gray-400 hover:text-white'
                                                } ${membership && activeMode === 'personal' ? 'opacity-50 cursor-not-allowed' : ''}`} // <--- FIX APPLIED
                                        >
                                            For my Team / Business
                                        </button>
                                    </div>
                                    {/* --- END PRICING TOGGLE --- */}

                                    <div className="space-y-3">
                                        {/* --- INDIVIDUAL PACKS (Using new keys) --- */}
                                        {pricingView === 'individual' && (
                                            <>
                                                <button
                                                    onClick={() => handleStripePayment('solo')}
                                                    disabled={isProcessingPayment}
                                                    className="w-full bg-transparent border border-[#FACC15] hover:bg-[#FACC15] hover:text-black text-[#FACC15] font-bold py-3 px-4 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-base">
                                                    {isProcessingPayment ? <Spinner small /> : 'The Solo - 1 Credit - $49'}
                                                </button>

                                                <button
                                                    onClick={() => handleStripePayment('brandingKit')}
                                                    disabled={isProcessingPayment}
                                                    className="w-full bg-transparent border border-[#FACC15] hover:bg-[#FACC15] hover:text-black text-[#FACC15] font-bold py-3 px-4 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-base">
                                                    {isProcessingPayment ? <Spinner small /> : 'The Branding Kit - 5 Credits - $200'}
                                                </button>
                                            </>
                                        )}

                                        {/* --- TEAM PACKS (Using new keys) --- */}
                                        {pricingView === 'team' && (
                                            <>
                                                <button
                                                    onClick={() => handleStripePayment('teamPack')}
                                                    disabled={isProcessingPayment}
                                                    className="w-full bg-transparent border border-blue-500 hover:bg-blue-500 text-blue-400 hover:text-white font-bold py-3 px-4 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-base">
                                                    {isProcessingPayment ? <Spinner small /> : 'The Team Pack - 10 Credits - $380'}
                                                </button>

                                                <button
                                                    onClick={() => handleStripePayment('businessPack')}
                                                    disabled={isProcessingPayment}
                                                    className="w-full bg-transparent border border-blue-500 hover:bg-blue-500 text-blue-400 hover:text-white font-bold py-3 px-4 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-base">
                                                    {isProcessingPayment ? <Spinner small /> : 'The Business Pack - 50 Credits - $1,700'}
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </>
                            ) : (
                                // --- This is what a non-admin team member sees ---
                                // MODIFIED: Only show purchase buttons if they are in 'personal' mode
                                <div className="space-y-3">
                                    {activeMode === 'personal' ? (
                                        <>
                                            <div className="mb-2">
                                                <span className="text-xs uppercase tracking-wider text-gray-500 font-bold">Personal Credits</span>
                                            </div>
                                            <button
                                                onClick={() => handleStripePayment('solo')}
                                                disabled={isProcessingPayment}
                                                className="w-full bg-transparent border border-[#FACC15] hover:bg-[#FACC15] hover:text-black text-[#FACC15] font-bold py-3 px-4 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-base">
                                                {isProcessingPayment ? <Spinner small /> : 'The Solo - 1 Credit - $49'}
                                            </button>

                                            <button
                                                onClick={() => handleStripePayment('brandingKit')}
                                                disabled={isProcessingPayment}
                                                className="w-full bg-transparent border border-[#FACC15] hover:bg-[#FACC15] hover:text-black text-[#FACC15] font-bold py-3 px-4 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-base">
                                                {isProcessingPayment ? <Spinner small /> : 'The Branding Kit - 5 Credits - $200'}
                                            </button>
                                        </>
                                    ) : (
                                        // Empty state for Team Mode
                                        <div className="p-4 bg-[#2a2a2a] rounded-lg border border-gray-700 text-center">
                                            <p className="text-sm text-gray-400">
                                                You are currently using <span className="text-blue-400 font-bold">Team Credits</span>.
                                            </p>
                                            <button
                                                onClick={() => {
                                                    setActiveMode('personal');
                                                    userManuallyChangedMode.current = true;
                                                    localStorage.setItem('modeLocked', 'true');
                                                }}
                                                className="text-xs text-[#FACC15] hover:text-yellow-300 underline mt-2"
                                            >
                                                Switch to Personal Mode to buy credits
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                            {/* --- END FIX --- */}
                        </div>

                        {/* ... (Omitted Test Payment buttons) ... */}

                        <div className="border-t border-gray-700 pt-6">
                            <h3 className="text-lg font-semibold text-white mb-2">Create Professional Headshots</h3>
                            <div className="bg-yellow-900/20 border border-yellow-600 rounded-lg p-3 mb-4">
                                <div className="flex items-start gap-2">
                                    <span className="text-yellow-400 font-bold text-sm">💡</span>
                                    <div className="text-sm">
                                        <p className="text-yellow-100 font-medium"><span className="font-bold">4 retakes per credit</span> - download your favorite to save</p>
                                        <p className="text-yellow-200 text-xs mt-1 font-bold">1 download = 1 credit</p>
                                    </div>
                                </div>
                            </div>

                            {sessionGeneratedImages.length > 0 ? (
                                <button
                                    onClick={handleStartHeadshotGeneration}
                                    disabled={!hasSufficientCredits() || generationAttempts >= 4}
                                    className="w-full bg-[#FACC15] hover:bg-yellow-400 text-[#121212] font-bold py-3 px-4 rounded-lg text-lg transition-colors disabled:bg-gray-600 disabled:text-gray-400 disabled:cursor-not-allowed">
                                    {generationAttempts >= 4 ? '❌ Session Complete' : '🔄 Continue Session'}
                                </button>
                            ) : (
                                <button
                                    onClick={handleStartHeadshotGeneration}
                                    disabled={!hasSufficientCredits()}
                                    className="w-full bg-[#FACC15] hover:bg-yellow-400 text-[#121212] font-bold py-3 px-4 rounded-lg text-lg transition-colors disabled:bg-gray-600 disabled:text-gray-400 disabled:cursor-not-allowed">
                                    🚀 Create Headshots Now
                                </button>
                            )}
                        </div>

                    </div>

                    {/* Right Column: Gallery & Tools */}
                    <div className="lg:col-span-2 space-y-8">

                        {/* --- NEW: TAB NAVIGATION --- */}
                        <div className="flex space-x-2 border-b border-gray-700">
                            <button
                                onClick={() => setAdminView('gallery')}
                                className={`py-3 px-5 font-bold text-lg transition-colors ${adminView === 'gallery'
                                    ? 'text-[#FACC15] border-b-2 border-[#FACC15]'
                                    : 'text-gray-400 hover:text-white'
                                    }`}
                            >
                                Headshot Gallery
                            </button>

                            {/* Conditionally show Team Admin tab */}
                            {membership?.role === 'admin' && activeMode === 'team' && (
                                <button
                                    onClick={() => setAdminView('team')}
                                    className={`py-3 px-5 font-bold text-lg transition-colors ${adminView === 'team'
                                        ? 'text-[#FACC15] border-b-2 border-[#FACC15]'
                                        : 'text-gray-400 hover:text-white'
                                        }`}
                                >
                                    Team Admin
                                </button>
                            )}
                        </div>
                        {/* --- END: TAB NAVIGATION --- */}


                        {/* --- NEW: CONDITIONAL CONTENT --- */}
                        {adminView === 'gallery' || activeMode !== 'team' ? (
                            <>
                                {/* This is your existing Gallery content */}
                                <div className="bg-[#1a1a1a] p-6 rounded-xl border border-gray-700">
                                    <h2 className="text-2xl font-bold text-white mb-6">Your Headshot Gallery</h2>

                                    {/* NEW: Display current session images */}
                                    {sessionGeneratedImages.length > 0 && (
                                        <div className="mb-8 p-6 border border-[#FACC15] bg-[#1a1a1a] rounded-xl">
                                            <div className="mb-4">
                                                <div className="flex items-center justify-between mb-2">
                                                    <h3 className="text-lg font-semibold text-[#FACC15]">Current Session</h3>
                                                    <div className="bg-[#FACC15] text-[#121212] px-3 py-1 rounded-full text-sm font-bold">
                                                        {4 - generationAttempts} retakes left
                                                    </div>
                                                </div>
                                                <div className="bg-yellow-900/20 border border-yellow-600 rounded-lg p-3 text-sm">
                                                    <div className="flex items-start gap-2">
                                                        <span className="text-yellow-400 font-bold">⚠️</span>
                                                        <div>
                                                            <p className="text-yellow-100 font-medium">Download your favorite headshots to save them permanently</p>
                                                            <p className="text-yellow-200 text-xs mt-1">Each download uses 1 credit from your account</p>
                                                            <div className="mt-2 p-2 bg-yellow-800/30 rounded border border-yellow-500">
                                                                <p className="text-yellow-100 text-xs font-medium">⏰ Time-Sensitive Download</p>
                                                                <p className="text-yellow-200 text-xs mt-1">
                                                                    Your generated images will be automatically deleted in: <span className="font-bold text-yellow-300">15:00</span>
                                                                </p>
                                                                <p className="text-yellow-200 text-xs mt-1">
                                                                    For privacy protection, we don't store your images permanently. Please download within 15 minutes.
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="columns-1 sm:columns-2 lg:columns-2 gap-2">
                                                {sessionGeneratedImages.map((img, index) => ( // <-- img is object
                                                    <div key={img.id} className="relative group break-inside-avoid mb-2"> {/* <-- 1. Use img.id as key */}
                                                        <img
                                                            src={img.url} // <-- 2. Use img.url
                                                            alt={`Session headshot ${index + 1}`}
                                                            className="w-full rounded-lg object-contain cursor-default pointer-events-none"
                                                        />
                                                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                                                            <button
                                                                onClick={() => handleGalleryDownload(img)} // <-- 3. Pass 'img' object
                                                                // ADD THIS 'disabled' CHECK
                                                                disabled={!hasSufficientCredits() || !imageExpirations[img.id] || currentTime > imageExpirations[img.id]} // <-- 4. Check by img.id
                                                                className="bg-black bg-opacity-70 px-4 py-2 rounded-lg border-2 border-[#FACC15] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                            >
                                                                <span className="bg-[#FACC15] text-[#121212] px-3 py-1 rounded-lg font-bold text-sm">Download (1 Credit)</span>
                                                            </button>
                                                        </div>

                                                        {/* USE THE NEW TIMER COMPONENT */}
                                                        {imageExpirations[img.id] && ( // <-- 5. Check by img.id
                                                            <ImageTimer expirationTime={imageExpirations[img.id]} /> // <-- 6. Pass by img.id
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}


                                    {/* Google Drive Gallery */}
                                    <div className="mb-8 p-6 border border-[#FACC15] bg-[#1a1a1a] rounded-xl">
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-lg font-semibold text-[#FACC15]">
                                                {activeMode === 'team' ? 'Team Drive Gallery' : 'Personal Drive Gallery'}
                                            </h3>
                                            {isFetchingDrive && <Spinner small />}
                                        </div>

                                        {/* --- THIS IS THE FIX --- */}
                                        {(() => {
                                            const isGalleryConnected = (activeMode === 'personal' && googleDriveToken) || (activeMode === 'team' && organization?.googleDriveFolderId);
                                            return (
                                                <>
                                                    {!isGalleryConnected && (
                                                        <div className="text-center py-6 px-6 border-2 border-dashed border-gray-600 rounded-lg bg-[#1a1a1a]">
                                                            <p className="text-sm text-gray-300 mb-4">
                                                                {activeMode === 'personal'
                                                                    ? "Connect Google Drive to see your purchase history."
                                                                    : "Your Team's Google Drive is not connected."
                                                                }
                                                            </p>
                                                            <p className="text-xs text-gray-400">
                                                                {activeMode === 'personal'
                                                                    ? "This is the only way to securely back up your headshots."
                                                                    : "Contact your admin to enable the shared team gallery."
                                                                }
                                                            </p>
                                                        </div>
                                                    )}

                                                    {isGalleryConnected && isFetchingDrive && <p className="text-gray-400">Loading gallery...</p>}

                                                    {isGalleryConnected && !isFetchingDrive && driveImages.length === 0 && (
                                                        <div className="text-center py-10 px-6 border-2 border-dashed border-gray-600 rounded-lg bg-[#121212]">
                                                            <h4 className="text-lg font-semibold text-white mb-2">
                                                                {activeMode === 'team' ? "Team Gallery is Empty" : "Gallery is Empty"}
                                                            </h4>
                                                            <p className="text-sm text-gray-400 mb-4 max-w-sm mx-auto">
                                                                Your saved headshots will appear here automatically after you download them.
                                                            </p>
                                                            <button
                                                                onClick={refreshDriveGallery}
                                                                disabled={isFetchingDrive}
                                                                className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg transition-colors text-sm disabled:opacity-50"
                                                            >
                                                                Refresh Gallery
                                                            </button>
                                                        </div>
                                                    )}

                                                    {isGalleryConnected && !isFetchingDrive && driveImages.length > 0 && (
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                                            {driveImages.map(file => (
                                                                <div key={file.id} className="relative group break-inside-avoid mb-2">
                                                                    <DriveImageSimple file={file} token={googleDriveToken} activeMode={activeMode} />
                                                                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                                                                        <button
                                                                            onClick={() => {
                                                                                const link = document.createElement('a');
                                                                                link.href = file.webContentLink;
                                                                                link.target = '_blank';
                                                                                link.rel = 'noopener noreferrer';
                                                                                link.download = file.name;
                                                                                document.body.appendChild(link);
                                                                                link.click();
                                                                                document.body.removeChild(link);
                                                                            }}
                                                                            className="bg-black bg-opacity-70 px-4 py-2 rounded-lg border-2 border-[#FACC15] cursor-pointer"
                                                                        >
                                                                            <span className="bg-[#FACC15] text-[#121212] px-3 py-1 rounded-lg font-bold text-sm">Download</span>
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </>
                                            );
                                        })()}
                                        {/* --- END FIX --- */}
                                    </div>
                                </div>

                                {generatedImages.length > 0 && !isLoading && (
                                    <div className="bg-[#1a1a1a] p-6 rounded-xl border border-gray-700">
                                        <h2 className="text-2xl font-bold text-white mb-4">Upgrade Your Profile</h2>
                                        <p className="text-gray-400 mb-4">Use your new headshot style to create a compelling LinkedIn bio.</p>
                                        <button onClick={handleGenerateBio} disabled={isTextLoading} className="bg-[#FACC15] hover:bg-yellow-400 text-[#121212] font-bold py-2 px-4 rounded-lg transition-colors disabled:bg-gray-600 disabled:text-gray-400 flex items-center">
                                            {isTextLoading && !linkedInBio ? <Spinner small /> : '✨ Draft my LinkedIn Bio'}
                                        </button>
                                        {linkedInBio && (
                                            <div className="mt-4">
                                                <textarea
                                                    readOnly
                                                    value={linkedInBio}
                                                    className="w-full p-3 border border-gray-600 rounded-lg bg-[#121212] text-white"
                                                    rows="6"
                                                />
                                                <button onClick={() => navigator.clipboard.writeText(linkedInBio)} className="mt-2 text-sm text-[#FACC15] hover:text-yellow-400">
                                                    Copy to Clipboard
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* --- NEW VIRAL PLG SECTION --- */}
                                {(generatedImages?.length > 0 || sessionGeneratedImages?.length > 0 || driveImages?.length > 0) && (
                                    <div className="bg-[#1a1a1a] p-6 rounded-xl border border-gray-700 mt-6">
                                        <h2 className="text-2xl font-bold text-[#FACC15] mb-4">Turn Your Team into a Billboard</h2>
                                        <p className="text-gray-400 mb-6">Boost your personal and network brand equity with your new headshots.</p>

                                        <div className="flex flex-col sm:flex-row gap-4">
                                            {/* Email Signature Button */}
                                            <button
                                                onClick={() => setShowSignatureModal(true)}
                                                className="flex-1 bg-transparent border border-[#FACC15] hover:bg-[#FACC15] hover:text-[#121212] text-[#FACC15] font-bold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2">
                                                📧 Create Email Signature
                                            </button>
                                            {/* LinkedIn Share Button */}
                                            <button
                                                onClick={() => setShowLinkedInModal(true)}
                                                className="flex-1 bg-[#0a66c2] hover:bg-[#004182] text-white font-bold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2">
                                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" /></svg>
                                                Share to LinkedIn
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {/* --- END VIRAL PLG SECTION --- */}

                            </>
                        ) : (
                            // Render the Team Admin Panel here instead
                            <TeamAdminPanel
                                organization={organization}
                                membership={membership}
                                teamCreditsAvailable={teamCreditsAvailable}
                                teamTemplates={teamTemplates}
                                setTeamTemplates={setTeamTemplates}
                                setIsTemplateEditorActive={setIsTemplateEditorActive}
                                user={user}
                                handleAuthSuccess={handleAuthSuccess}
                                handleAuthError={handleAuthError}
                                showToast={showToast}
                            />
                        )}
                        {/* --- END: CONDITIONAL CONTENT --- */}
                    </div>
                </div>
            </main>

            {/* VIRAL PLG MODAL RENDER */}
            <EmailSignatureModal
                isOpen={showSignatureModal}
                onClose={() => setShowSignatureModal(false)}
                userName={profile?.name || user?.email?.split('@')[0]}
                userTitle={profile?.title}
                availableImages={[...(sessionGeneratedImages || []), ...(driveImages || [])]}
            />
            <LinkedInShareModal
                isOpen={showLinkedInModal}
                onClose={() => setShowLinkedInModal(false)}
                organization={organization}
            />
        </div>
    );
});

// --- NEW COMPONENT: EmailSignatureModal ---
const EmailSignatureModal = ({ isOpen, onClose, userName, userTitle, availableImages }) => {
    // Add local state for robust interactive editing
    const [name, setName] = useState(userName || "Your Name");
    const [title, setTitle] = useState(userTitle || "Professional Title");
    const [company, setCompany] = useState("Your Company");
    const [phone, setPhone] = useState("+1 (555) 000-0000");
    const [website, setWebsite] = useState("www.yourwebsite.com");
    const [brandColor, setBrandColor] = useState("#FACC15");
    const [selectedImageUrl, setSelectedImageUrl] = useState("");
    const [imageShape, setImageShape] = useState("round");


    // Sync state if props change when opening
    useEffect(() => {
        if (isOpen) {
            setName(userName || "Your Name");
            setTitle(userTitle || "Professional Title");
            if (availableImages && availableImages.length > 0) {
                // Prioritize thumbnailLink or url
                const bestImage = availableImages[0].url || availableImages[0].thumbnailLink || availableImages[0].webContentLink;
                setSelectedImageUrl(bestImage || "");
            }
        }
    }, [isOpen, userName, userTitle, availableImages]);

    if (!isOpen) return null;

    // Use actual selected image if available. If none exists, fallback to colored UI Avatar.
    const imageUrl = selectedImageUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'YN')}&background=${brandColor.replace('#', '')}&color=121212&size=100`;

    const signatureHtml = `
<table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, sans-serif; max-width: 450px; padding: 10px 0;">
  <tr>
    <td style="padding-right: 20px; border-right: 2px solid ${brandColor}; text-align: center; vertical-align: middle;">
      <img src="${imageUrl}" alt="Profile" style="width: 90px; height: 90px; border-radius: ${imageShape === 'round' ? '50%' : '8px'}; object-fit: cover;" />
    </td>
    <td style="padding-left: 20px; vertical-align: middle;">
      <h3 style="margin: 0; color: #121212; font-size: 18px; font-weight: bold;">${name}</h3>
      <p style="margin: 2px 0 8px; color: ${brandColor}; font-weight: bold; font-size: 13px;">${title}</p>
      <p style="margin: 2px 0 8px; color: #666; font-size: 12px;"><strong>${company}</strong></p>
      <p style="margin: 0 0 2px; color: #666; font-size: 12px;">📞 ${phone}</p>
      <p style="margin: 0 0 0px; font-size: 12px;"><a href="https://${website}" style="color: #666; text-decoration: none;">🌐 ${website.replace('https://', '').replace('http://', '')}</a></p>
    </td>
  </tr>
  <tr>
    <td colspan="2" style="padding-top: 15px;">
      <p style="margin: 0; font-size: 11px;"><a href="https://www.instantheadshot.pro" style="color: #999; text-decoration: none;">Headshot Generated by <span style="color: ${brandColor}; font-weight: bold;">InstantHeadshot.pro</span></a></p>
    </td>
  </tr>
</table>
`.trim();

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 p-4">
            <div className="bg-[#1a1a1a] border border-gray-700 rounded-xl max-w-2xl w-full p-6 relative max-h-[95vh] flex flex-col">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white text-xl z-10">✕</button>
                <div className="flex-shrink-0">
                    <h2 className="text-2xl font-bold text-white mb-2">Build Your Signature</h2>
                    <p className="text-gray-400 mb-4 text-sm">Customize your exact professional details and bind your firm's brand color. Copy this block directly into Gmail or Outlook.</p>
                </div>

                {/* Interactive Inputs Scrollable Area */}
                <div className="flex-grow overflow-y-auto pr-2 custom-scrollbar space-y-4">
                    {/* NEW: IMAGE SELECTOR */}
                    <div className="mb-2">
                        <label className="block text-xs font-bold text-gray-400 mb-2">Select Image for Signature</label>
                        {availableImages?.length > 0 ? (
                            <div className="flex gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: 'thin', scrollbarColor: '#4b5563 transparent' }}>
                                {availableImages.map((img, idx) => {
                                    const rawUrl = img.url || img.thumbnailLink || img.webContentLink;
                                    if (!rawUrl) return null;
                                    return (
                                        <div
                                            key={img.id || idx}
                                            onClick={() => setSelectedImageUrl(rawUrl)}
                                            className={`relative flex-shrink-0 cursor-pointer overflow-hidden rounded-full w-14 h-14 border-2 transition-all ${selectedImageUrl === rawUrl ? 'border-[#FACC15] scale-105 shadow-[0_0_10px_#FACC15]' : 'border-gray-700 opacity-60 hover:opacity-100'}`}
                                        >
                                            <img src={rawUrl} className="w-full h-full object-cover" alt="headshot choice" referrerPolicy="no-referrer" />
                                        </div>
                                    );
                                })}
                                {/* Always offer the fallback Avatar option */}
                                <div
                                    onClick={() => setSelectedImageUrl("")}
                                    className={`relative flex-shrink-0 cursor-pointer overflow-hidden rounded-full w-14 h-14 border-2 transition-all flex items-center justify-center bg-[#121212] ${selectedImageUrl === "" ? 'border-[#FACC15] scale-105 shadow-[0_0_10px_#FACC15]' : 'border-gray-700 opacity-60 hover:opacity-100'}`}
                                >
                                    <span className="text-xs text-center text-gray-500 font-bold leading-tight">No<br />Photo</span>
                                </div>
                            </div>
                        ) : (
                            <p className="text-sm text-gray-500 italic">No headshots generated yet. Default template applied.</p>
                        )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-400 mb-1">Full Name</label>
                            <input type="text" className="w-full bg-[#121212] border border-gray-600 rounded px-3 py-2 text-white text-sm"
                                value={name} onChange={(e) => setName(e.target.value)} />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-400 mb-1">Job Title</label>
                            <input type="text" className="w-full bg-[#121212] border border-gray-600 rounded px-3 py-2 text-white text-sm"
                                value={title} onChange={(e) => setTitle(e.target.value)} />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-400 mb-1">Company Name</label>
                            <input type="text" className="w-full bg-[#121212] border border-gray-600 rounded px-3 py-2 text-white text-sm"
                                value={company} onChange={(e) => setCompany(e.target.value)} />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-400 mb-1">Phone Number</label>
                            <input type="text" className="w-full bg-[#121212] border border-gray-600 rounded px-3 py-2 text-white text-sm"
                                value={phone} onChange={(e) => setPhone(e.target.value)} />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-400 mb-1">Website URL</label>
                            <input type="text" className="w-full bg-[#121212] border border-gray-600 rounded px-3 py-2 text-white text-sm"
                                value={website} onChange={(e) => setWebsite(e.target.value)} />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-400 mb-1">Brand Hex Color</label>
                            <div className="flex bg-[#121212] border border-gray-600 rounded overflow-hidden">
                                <input type="color" className="w-10 h-10 border-0 p-0 cursor-pointer"
                                    value={brandColor} onChange={(e) => setBrandColor(e.target.value)} />
                                <input type="text" className="w-full bg-transparent px-3 py-2 text-white text-sm focus:outline-none"
                                    value={brandColor} onChange={(e) => setBrandColor(e.target.value)} />
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-400 mb-1">Image Shape</label>
                            <div className="flex bg-[#121212] border border-gray-600 rounded overflow-hidden">
                                <button
                                    className={`flex-1 py-2 text-sm font-bold ${imageShape === 'round' ? 'bg-[#333] text-white' : 'text-gray-500 hover:bg-[#222]'}`}
                                    onClick={() => setImageShape('round')}
                                >Circle</button>
                                <button
                                    className={`flex-1 py-2 text-sm font-bold border-l border-gray-600 ${imageShape === 'square' ? 'bg-[#333] text-white' : 'text-gray-500 hover:bg-[#222]'}`}
                                    onClick={() => setImageShape('square')}
                                >Square</button>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg overflow-hidden flex justify-center mt-4 border border-gray-300">
                        <div dangerouslySetInnerHTML={{ __html: signatureHtml }} />
                    </div>

                    <div>
                        <h3 className="text-white font-semibold mb-2 mt-4">Raw HTML:</h3>
                        <textarea
                            readOnly
                            className="w-full h-24 bg-[#121212] border border-gray-700 text-gray-300 font-mono text-xs p-3 rounded"
                            value={signatureHtml}
                        />
                    </div>
                </div>

                <div className="flex justify-end gap-3 mt-4 flex-shrink-0">
                    <button onClick={onClose} className="bg-gray-700 text-white font-bold py-2 px-6 rounded hover:bg-gray-600 transition-colors">
                        Close
                    </button>
                    <button onClick={() => {
                        navigator.clipboard.writeText(signatureHtml);
                        alert("HTML signature copied to clipboard!");
                    }} className="bg-[#FACC15] text-black font-bold py-2 px-6 rounded hover:bg-yellow-400 transition-colors">
                        Copy HTML
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- NEW COMPONENT: LinkedInShareModal ---
const LinkedInShareModal = ({ isOpen, onClose, organization }) => {
    const [selectedOption, setSelectedOption] = useState(0);

    if (!isOpen) return null;

    const niche = (organization?.niche || '').toLowerCase();
    const isMedical = niche.includes('medical') || niche.includes('health') || niche.includes('spa');

    const options = [
        {
            title: "Option 1 (Leadership Announcement)",
            copy: `Just standardized our team’s headshots—in under 2 minutes per person.\n\nNo scheduling. No back-and-forth. No inconsistent results across offices.\n\nWe’re using InstantHeadshot.pro to streamline onboarding and keep every profile client-ready from day one.\n\nFor ${isMedical ? 'clinical teams' : 'professional services teams'}, this solves a surprisingly expensive problem:\n→ Delayed onboarding\n→ Mismatched branding across LinkedIn & internal systems\n→ Ongoing coordination with photographers\n\nNow it’s handled instantly—with consistent, high-quality results.\n\n📸 [Attach your downloaded headshot here]\n\nCurious how other ${isMedical ? 'clinics' : 'firms'} are handling this?\n\n#${isMedical ? 'HealthcareStrategy' : 'ProfessionalServices'} #Onboarding #BrandConsistency #AIHeadshots`
        },
        {
            title: "Option 2 (Curiosity / Peer Outreach)",
            copy: `Updated my headshot in 90 seconds.\n\nThen realized—this shouldn’t just be for me.\n\nIf every new hire can get a ${isMedical ? 'patient-ready' : 'client-ready'}, brand-consistent headshot on day one…\nyou remove one more friction point from onboarding.\n\nThat’s exactly what InstantHeadshot.pro enables.\n\n📸 [Attach your downloaded headshot here]\n\nWould this work for your team?\n\n#AIHeadshots #${isMedical ? 'Healthcare' : 'ProfessionalServices'} #GrowthOps`
        },
        {
            title: "Option 3 (Short & Direct)",
            copy: `New headshot in 90 seconds.\n\nNow imagine doing this for every new hire—instantly, consistently, and without coordination.\n\nThat’s the real unlock with InstantHeadshot.pro.\n\n📸 [Attach your downloaded headshot here]\n\n#Onboarding #BrandConsistency #AIHeadshots`
        },
        {
            title: "Option 4 (New Team Member Introduction)",
            copy: `I'm thrilled to share that I've joined the incredible team here!\n\nAs part of my day-one onboarding, I got this new, professional headshot generated in just 90 seconds using our workspace on InstantHeadshot.pro.\n\nIt feels great to hit the ground running with a client-ready profile from the very beginning.\n\n📸 [Attach your downloaded headshot here]\n\n#NewBeginnings #AIHeadshots #${isMedical ? 'Healthcare' : 'ProfessionalServices'}`
        }
    ];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 p-4">
            <div className="bg-[#1a1a1a] border border-[#0a66c2] rounded-xl max-w-3xl w-full p-6 relative max-h-[95vh] flex flex-col shadow-2xl">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white text-xl z-10">✕</button>
                <div className="flex-shrink-0 mb-4 flex items-center gap-3">
                    <svg className="w-8 h-8 text-[#0a66c2]" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" /></svg>
                    <h2 className="text-2xl font-bold text-white">Share to LinkedIn</h2>
                </div>
                <p className="text-gray-400 mb-6 text-sm flex-shrink-0">
                    Select a post template tailored to your industry.
                    <span className="text-[#0a66c2] font-semibold block mt-1">Note: Your post text will be automatically copied to your clipboard to prevent LinkedIn truncation limits. Just paste (Ctrl+V) if needed!</span>
                </p>

                <div className="flex-grow overflow-y-auto mb-4 space-y-4 pr-2" style={{ scrollbarWidth: 'thin', scrollbarColor: '#4b5563 transparent' }}>
                    {options.map((opt, idx) => (
                        <div
                            key={idx}
                            onClick={() => setSelectedOption(idx)}
                            className={`p-4 rounded-xl cursor-pointer border-2 transition-all ${selectedOption === idx ? 'border-[#0a66c2] bg-[#0a66c2]/10 shadow-[0_0_15px_rgba(10,102,194,0.3)]' : 'border-gray-700 bg-[#121212] hover:border-gray-500 hover:shadow-lg'}`}
                        >
                            <h3 className="text-white font-bold mb-3 flex items-center justify-between">
                                {opt.title}
                                {selectedOption === idx && <span className="bg-[#0a66c2] text-white text-[10px] px-2 py-1 rounded tracking-widest uppercase font-bold">Selected</span>}
                            </h3>
                            <textarea
                                readOnly
                                value={opt.copy}
                                rows={7}
                                className="w-full bg-transparent border-0 text-gray-300 text-sm focus:outline-none resize-none cursor-pointer"
                            />
                        </div>
                    ))}
                </div>

                <div className="flex-shrink-0 flex gap-3 pt-4 border-t border-gray-700">
                    <button onClick={onClose} className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-bold py-3 px-4 rounded-lg transition-colors">
                        Cancel
                    </button>
                    <button
                        onClick={() => {
                            navigator.clipboard.writeText(options[selectedOption].copy);
                            const text = encodeURIComponent(options[selectedOption].copy);
                            window.open(`https://www.linkedin.com/feed/?shareActive=true&text=${text}`, '_blank');
                        }}
                        className="flex-2 bg-[#0a66c2] hover:bg-[#004182] text-white font-bold py-3 px-8 rounded-lg transition-colors flex items-center justify-center gap-2 w-full"
                    >
                        Copy & Open LinkedIn
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- IMPROVED: Branded Payment Success Page ---
const PaymentSuccess = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get('session_id') || 'Unknown';

    // Format the session ID for display (e.g., cs_test_...1234)
    const shortSessionId = sessionId.length > 10
        ? `${sessionId.substring(0, 8)}...${sessionId.substring(sessionId.length - 4)}`
        : sessionId;

    return (
        <div className="w-full min-h-screen bg-[#121212] flex items-center justify-center p-4" style={{ fontFamily: '"Plus Jakarta Sans", "Noto Sans", sans-serif' }}>
            <div className="bg-[#1a1a1a] p-8 rounded-2xl shadow-2xl border border-gray-700 max-w-md w-full text-center relative overflow-hidden">
                {/* Background Glow Effect */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-32 bg-[#FACC15] opacity-10 blur-3xl rounded-full pointer-events-none"></div>

                {/* Success Icon */}
                <div className="mx-auto w-20 h-20 bg-green-900/30 rounded-full flex items-center justify-center mb-6 border border-green-500/30">
                    <svg className="w-10 h-10 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
                    </svg>
                </div>

                <h2 className="text-3xl font-bold text-white mb-2">Payment Successful</h2>
                <p className="text-gray-400 mb-8">Your credits have been securely deposited.</p>

                {/* Receipt / Transaction Details */}
                <div className="bg-[#121212] rounded-lg p-4 mb-8 border border-gray-800 text-left">
                    <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-3">Transaction Receipt</p>

                    <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-400 text-sm">Status</span>
                        <span className="text-green-400 text-sm font-bold flex items-center gap-1">
                            ● Completed
                        </span>
                    </div>

                    <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-400 text-sm">Date</span>
                        <span className="text-white text-sm font-medium">
                            {new Date().toLocaleDateString()}
                        </span>
                    </div>

                    <div className="flex justify-between items-center">
                        <span className="text-gray-400 text-sm">Reference</span>
                        <span className="text-gray-300 text-xs font-mono bg-gray-800 px-2 py-1 rounded" title={sessionId}>
                            {shortSessionId}
                        </span>
                    </div>
                </div>

                <button
                    onClick={() => {
                        // Clear URL params and navigate
                        window.history.replaceState({}, document.title, '/');
                        navigate('/');
                    }}
                    className="w-full bg-[#FACC15] hover:bg-yellow-400 text-[#121212] font-bold py-4 px-6 rounded-xl transition-all duration-200 text-lg shadow-lg shadow-yellow-900/20">
                    Go to Dashboard
                </button>
            </div>
        </div>
    );
};

// --- IMPROVED: Branded Payment Cancel Page ---
const PaymentCancel = () => {
    const navigate = useNavigate();

    return (
        <div className="w-full min-h-screen bg-[#121212] flex items-center justify-center p-4" style={{ fontFamily: '"Plus Jakarta Sans", "Noto Sans", sans-serif' }}>
            <div className="bg-[#1a1a1a] p-8 rounded-2xl shadow-2xl border border-gray-700 max-w-md w-full text-center">

                {/* Cancel Icon */}
                <div className="mx-auto w-20 h-20 bg-red-900/20 rounded-full flex items-center justify-center mb-6 border border-red-500/20">
                    <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                </div>

                <h2 className="text-2xl font-bold text-white mb-4">Payment Cancelled</h2>
                <p className="text-gray-400 mb-8 leading-relaxed">
                    No worries—you haven't been charged. You can try again whenever you're ready.
                </p>

                <button
                    onClick={() => {
                        window.history.replaceState({}, document.title, '/');
                        navigate('/');
                    }}
                    className="w-full bg-gray-700 hover:bg-gray-600 text-white font-bold py-4 px-6 rounded-xl transition-all duration-200">
                    Return to Dashboard
                </button>
            </div>
        </div>
    );
};

// --- NEW COMPONENT: AcceptInvitePage ---
const AcceptInvitePage = ({ user, setShowLoginModal }) => {
    const [message, setMessage] = useState('Verifying your invitation, please wait...');
    const [error, setInviteError] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const processInvite = async () => {
            const params = new URLSearchParams(location.search);

            // --- THIS IS THE FIX ---
            // Try to get token from URL. If it's missing (due to auth redirect), get from localStorage.
            let inviteId = params.get('token');
            if (!inviteId) {
                inviteId = localStorage.getItem('pendingInviteToken');
            }
            // --- END FIX ---

            if (!inviteId) {
                setInviteError('No invite token found. This link is invalid or has expired.');
                return;
            }

            if (!user) {
                // User is not logged in. Save the token and show the modal.
                localStorage.setItem('pendingInviteToken', inviteId);
                setMessage('Please sign in to accept your invitation.');
                setShowLoginModal(true);
                return;
            }

            // --- User is now logged in AND we have an inviteId ---

            // Clear the token from storage, we don't need it anymore
            localStorage.removeItem('pendingInviteToken');

            // User is logged in, try to accept the invite
            try {
                const token = await auth.currentUser.getIdToken();
                const backendUrl = window.location.hostname === 'localhost' ? 'http://localhost:3001' : '';

                const response = await fetch(`${backendUrl}/api/accept-invite`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ token: inviteId }) // inviteId is now correctly sourced
                });

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.error || 'Failed to accept invite.');
                }

                // SUCCESS!
                setMessage('Success! Welcome to the team. Redirecting you to your dashboard...');
                setTimeout(() => {
                    navigate('/'); // Redirect to the dashboard
                }, 2000);

            } catch (err) {
                console.error('Accept invite error:', err);
                setInviteError(err.message);
            }
        };

        processInvite();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, location.search, navigate, setShowLoginModal]); // Dependencies are correct

    return (
        <div className="w-full min-h-screen bg-gray-900 flex items-center justify-center p-4">
            <div className="bg-[#1a1a1a] p-8 rounded-2xl shadow-2xl border border-gray-700 max-w-lg w-full text-center">
                <h2 className="text-2xl font-bold text-white mb-6">Accept Team Invitation</h2>

                {/* This is the state that SHOULD show when you are logged out */}
                {!error && (
                    <div className="flex flex-col items-center">
                        <Spinner large />
                        <p className="text-gray-300 mt-4">{message}</p>
                    </div>
                )}

                {/* This is the state you are seeing, which only appears on a REAL error */}
                {error && (
                    <div className="text-center">
                        {/* We can add the Red X here to match your screenshot if you like */}
                        <span className="text-red-500 text-5xl font-bold">✗</span>
                        <h3 className="text-red-400 text-xl font-bold my-4">Invitation Error</h3>
                        <p className="text-red-400 mb-6">{error}</p>
                        <button
                            onClick={() => navigate('/')}
                            className="bg-[#FACC15] hover:bg-yellow-400 text-[#121212] font-bold py-3 px-6 rounded-lg transition-colors"
                        >
                            Go to Dashboard
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

// --- NEW COMPONENT: PilotRequestPage ---
const PilotRequestPage = ({ 
    user, 
    db, 
    showToast, 
    setShowLoginModal, 
    navigate,
    membership,
    organization,
    activeMode,
    setActiveMode
}) => {
    const [fullName, setFullName] = useState(user?.displayName || '');
    const [email, setEmail] = useState(user?.email || '');
    const [companyName, setCompanyName] = useState('');
    const [companyWebsite, setCompanyWebsite] = useState('');
    const [teamSize, setTeamSize] = useState('10-24');
    const [industry, setIndustry] = useState('Law / Legal');
    const [useCase, setUseCase] = useState('team_page_refresh');
    const [teamPageUrl, setTeamPageUrl] = useState('');
    const [teamSizeNum, setTeamSizeNum] = useState('15');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [provisionedCredits, setProvisionedCredits] = useState(3);
    const [isEnterpriseDisqualified, setIsEnterpriseDisqualified] = useState(false);
    const [error, setError] = useState('');

    const getIndustryPilotTerm = () => {
        const ind = (industry || '').toLowerCase();
        if (ind.includes('law') || ind.includes('legal')) return '3-Attorney';
        if (ind.includes('accounting') || ind.includes('finance') || ind.includes('cpa')) return '3-CPA';
        if (ind.includes('consulting') || ind.includes('professional')) return '3-Consultant';
        if (ind.includes('healthcare') || ind.includes('medical')) return '3-Staff';
        if (ind.includes('real estate') || ind.includes('realty')) return '3-Agent';
        if (ind.includes('tech') || ind.includes('startup')) return '3-Employee';
        return '3-Person';
    };

    // Pre-populate fields if user logged in or from query parameters
    useEffect(() => {
        if (user) {
            if (!fullName && user.displayName) setFullName(user.displayName);
            if (!email && user.email) setEmail(user.email);
        }

        const params = new URLSearchParams(window.location.search);
        const urlVertical = params.get('vertical');
        const urlNiche = params.get('niche');
        const urlIndustry = params.get('industry');
        
        let detectedIndustry = '';
        if (urlVertical) {
            const v = urlVertical.toLowerCase();
            if (v === 'law' || v === 'legal') detectedIndustry = 'Law / Legal';
            else if (v === 'cpa' || v === 'accounting') detectedIndustry = 'Accounting / CPA';
            else if (v === 'consulting') detectedIndustry = 'Consulting';
            else if (v === 'medical' || v === 'healthcare') detectedIndustry = 'Medical / Healthcare';
        } else if (urlNiche) {
            const n = urlNiche.toLowerCase();
            if (n.includes('law') || n.includes('attorney') || n.includes('legal')) detectedIndustry = 'Law / Legal';
            else if (n.includes('cpa') || n.includes('account')) detectedIndustry = 'Accounting / CPA';
            else if (n.includes('consult')) detectedIndustry = 'Consulting';
            else if (n.includes('medical') || n.includes('dental') || n.includes('doctor') || n.includes('health')) detectedIndustry = 'Medical / Healthcare';
        } else if (urlIndustry) {
            const ind = urlIndustry.toLowerCase();
            if (ind.includes('law') || ind.includes('legal') || ind.includes('attorney')) detectedIndustry = 'Law / Legal';
            else if (ind.includes('cpa') || ind.includes('accounting') || ind.includes('finance')) detectedIndustry = 'Accounting / CPA';
            else if (ind.includes('consult')) detectedIndustry = 'Consulting';
        }
        
        if (detectedIndustry) {
            setIndustry(detectedIndustry);
        }
    }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleRequestPilot = async (e) => {
        e.preventDefault();
        
        if (!email || !fullName || !companyName || !companyWebsite || !teamSizeNum) {
            showToast("Please fill in all required fields", "error");
            return;
        }

        setIsSubmitting(true);
        setError('');

        const personalDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com', 'aol.com', 'live.com', 'msn.com'];
        const emailDomain = email.split('@')[1]?.toLowerCase() || '';
        const isPersonalEmail = personalDomains.includes(emailDomain);
        
        const strategicNiches = ['law', 'legal', 'consulting', 'accounting', 'healthcare', 'medical'];
        const selectedNicheLower = industry.toLowerCase();
        const isStrategicNiche = strategicNiches.some(n => selectedNicheLower.includes(n));
        
        const sizeNumber = parseInt(teamSizeNum) || 0;
        
        let isStrategic = false;
        let isHighQuality = false;
        let credits = 1;
        let qualificationStatus = 'low_quality';
        
        // SOC2 Exclusions check (>1000 employees is Enterprise opportunity)
        const isEnterprise = sizeNumber > 1000 || teamSize === '1000+';
        
        if (isEnterprise) {
            qualificationStatus = 'disqualified_enterprise';
            credits = 0;
        } else if (isStrategicNiche && sizeNumber >= 25) {
            isStrategic = true;
            credits = 3;
            qualificationStatus = 'strategic';
        } else if (!isPersonalEmail && companyWebsite && sizeNumber >= 10) {
            isHighQuality = true;
            credits = 3;
            qualificationStatus = 'high_quality';
        } else {
            credits = 1;
            qualificationStatus = 'low_quality';
        }

        try {
            const leadRef = doc(collection(db, 'leads_bridge'));
            const leadId = leadRef.id;
            
            const leadDocData = {
                leadType: "inbound_pilot",
                source: "linkedin_profile",
                Firm_Name: companyName,
                Firm_Website: companyWebsite,
                Niche: industry,
                Team_Size: sizeNumber,
                useCase: useCase,
                fullName: fullName,
                workEmail: email,
                teamPageUrl: teamPageUrl || "",
                complimentaryCredits: credits,
                pilotStatus: isEnterprise ? "disqualified_enterprise" : "requested",
                workspaceProvisioned: false,
                strategic: isStrategic,
                highQuality: isHighQuality,
                qualificationStatus: qualificationStatus,
                createdAt: new Date()
            };

            await setDoc(leadRef, leadDocData);
            localStorage.setItem('gh_lid', leadId);
            
            setProvisionedCredits(credits);
            setIsEnterpriseDisqualified(isEnterprise);

            // If user is ALREADY logged in, let's provision them instantly!
            if (user && credits > 0) {
                console.log("User is already logged in. Provisioning pilot workspace immediately...");
                const orgRef = doc(collection(db, 'organizations'));
                await setDoc(orgRef, {
                    ownerId: user.uid,
                    orgName: `${companyName}'s Team`,
                    sharedCredits: credits,
                    createdAt: new Date(),
                    niche: industry,
                    teamSizeOriginal: sizeNumber,
                    source: 'SDR_Identity_Bridge',
                    status: 'active',
                    leadType: "inbound_pilot"
                });

                await setDoc(doc(db, 'memberships', user.uid), {
                    userId: user.uid,
                    organizationId: orgRef.id,
                    role: 'admin',
                    status: 'active',
                    allottedCredits: 99999,
                    usedCredits: 0,
                    joinedAt: new Date()
                });

                const profileRef = doc(db, 'profiles', user.uid);
                await setDoc(profileRef, {
                    activeOrganizationId: orgRef.id,
                    activeMode: 'team'
                }, { merge: true });

                await updateDoc(doc(db, 'leads_bridge', leadId), {
                    signed_up: true,
                    convertedAt: new Date(),
                    workspaceProvisioned: true
                });

                localStorage.removeItem('gh_lid');
                setActiveMode('team');
            }

            setFormSubmitted(true);
            showToast("Pilot request submitted successfully!", "success");
        } catch (err) {
            console.error("Error submitting pilot request:", err);
            setError(err.message || "Failed to submit request.");
            showToast("Failed to request pilot. Please check your connection.", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleLaunchWorkspace = () => {
        if (!user) {
            setShowLoginModal(true);
        } else {
            // Already logged in and provisioned
            navigate('/');
        }
    };

    const useCasesList = [
        { value: 'new_hire', label: 'New-hire onboarding' },
        { value: 'team_page_refresh', label: 'Team page refresh' },
        { value: 'directory', label: 'Law firm / professional services directory' },
        { value: 'linkedin_standardization', label: 'Sales team LinkedIn standardization' },
        { value: 'distributed_team', label: 'Distributed team portraits' },
        { value: 'event', label: 'Event or conference headshots' }
    ];

    if (formSubmitted) {
        return (
            <div className="w-full min-h-screen bg-[#0d0d0d] text-white flex flex-col font-sans">
                <Helmet>
                    <title>Start a 3-Person Virtual Photo Day Pilot | InstantHeadshot.pro</title>
                    <meta name="description" content="Request a complimentary 3-person pilot and test the Virtual Photo Day workflow with your own team. Create consistent, brand-approved headshots without scheduling photographers." />
                    
                    {/* CRITICAL FIX: Explicitly set the canonical path to stop homepage text hijacking */}
                    <link rel="canonical" href="https://www.instantheadshot.pro/pilot" />

                    {/* Open Graph Meta Tags */}
                    <meta property="og:title" content="Start a 3-Person Virtual Photo Day Pilot" />
                    <meta property="og:description" content="Test the workflow with 3 team members and see how brand-consistent headshots can work for your organization." />
                    <meta property="og:type" content="website" />
                    <meta property="og:url" content="https://www.instantheadshot.pro/pilot" />
                    <meta property="og:image" content="https://www.instantheadshot.pro/InstantHeadshotpro%20opengraph.png" />
                    <meta property="og:image:width" content="1200" />
                    <meta property="og:image:height" content="630" />
                    <meta property="og:site_name" content="InstantHeadshot.pro" />
                    
                    {/* Twitter Meta Tags */}
                    <meta name="twitter:card" content="summary_large_image" />
                    <meta name="twitter:url" content="https://www.instantheadshot.pro/pilot" />
                    <meta name="twitter:title" content="Start a 3-Person Virtual Photo Day Pilot" />
                    <meta name="twitter:description" content="Test the workflow with 3 team members and see how brand-consistent headshots can work for your organization." />
                    <meta name="twitter:image" content="https://www.instantheadshot.pro/InstantHeadshotpro%20opengraph.png" />
                </Helmet>
                {/* Minimal Header */}
                <header className="flex items-center p-4 justify-between bg-[#121212] border-b border-gray-800">
                    <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
                        <img src="/InstantHeadshotPro logo.png" alt="Logo" className="h-9 w-auto bg-white p-1 rounded" />
                        <span className="text-white text-lg font-bold">InstantHeadshot.pro</span>
                    </div>
                </header>

                <main className="flex-1 flex items-center justify-center p-4 md:p-8">
                    <div className="max-w-2xl w-full bg-[#161616]/95 border border-gray-800 rounded-2xl p-8 md:p-12 shadow-2xl relative overflow-hidden">
                        {/* Glow effect */}
                        <div className="absolute -top-40 -right-40 w-80 h-80 bg-yellow-500/10 rounded-full blur-3xl"></div>
                        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl"></div>

                        {isEnterpriseDisqualified ? (
                            <div className="text-center relative z-10">
                                <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-yellow-950/50 border border-yellow-700/50 text-[#FACC15] mb-6">
                                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                </div>
                                <h2 className="text-3xl font-extrabold mb-4 tracking-tight">Request Received for Manual Review</h2>
                                <p className="text-gray-400 text-lg leading-relaxed mb-6">
                                    Thank you for your interest! InstantHeadshot.pro is customized for small and medium-sized teams (5-1000 employees) with a privacy-first, zero-retention architecture. Because we do not support enterprise-level SOC2 or HIPAA compliance, we cannot automatically provision a pilot for larger organizations.
                                </p>
                                <p className="text-gray-400 text-lg leading-relaxed mb-8">
                                    Our team will review your request manually and reach out if we can accommodate your use case.
                                </p>
                                <button
                                    onClick={() => navigate('/')}
                                    className="bg-gray-800 hover:bg-gray-700 text-white font-bold py-3 px-8 rounded-xl transition-all duration-300">
                                    Return to Home
                                </button>
                            </div>
                        ) : (
                            <div className="relative z-10">
                                <div className="text-center mb-8">
                                    <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-green-950/50 border border-green-700/50 text-green-400 mb-6">
                                        <svg className="w-8 h-8 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <h2 className="text-3xl md:text-4xl font-extrabold mb-2 tracking-tight">Your 3-Person Pilot Is Ready</h2>
                                    <p className="text-gray-400 text-lg">Your pilot workspace has been prepared for <strong className="text-white">{companyName}</strong>.</p>
                                </div>

                                <div className="bg-[#1e1e1e] border border-gray-800 rounded-xl p-6 mb-8">
                                    <div className="flex justify-between items-center border-b border-gray-800 pb-4 mb-4">
                                        <div>
                                            <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Workspace Title</p>
                                            <p className="text-white font-bold text-lg">{companyName} Pilot Workspace</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Available Credits</p>
                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-yellow-400/10 text-[#FACC15] border border-yellow-400/20">
                                                {provisionedCredits} Complimentary Credits
                                            </span>
                                        </div>
                                    </div>

                                    <div>
                                        <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-4">Your Virtual Photo Day Workflow</p>
                                        <div className="space-y-4">
                                            <div className="flex items-start">
                                                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-yellow-400/20 text-[#FACC15] flex items-center justify-center font-bold text-xs mt-0.5">1</div>
                                                <div className="ml-3">
                                                    <p className="text-sm font-semibold text-white">Choose your approved team look</p>
                                                    <p className="text-xs text-gray-400">Set the background, wardrobe style, and visual guidelines for consistent professional headshots.</p>
                                                </div>
                                            </div>
                                            <div className="flex items-start">
                                                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-yellow-400/20 text-[#FACC15] flex items-center justify-center font-bold text-xs mt-0.5">2</div>
                                                <div className="ml-3">
                                                    <p className="text-sm font-semibold text-white">Invite up to 3 team members</p>
                                                    <p className="text-xs text-gray-400">Send secure invites so selected team members can submit their photos.</p>
                                                </div>
                                            </div>
                                            <div className="flex items-start">
                                                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-yellow-400/20 text-[#FACC15] flex items-center justify-center font-bold text-xs mt-0.5">3</div>
                                                <div className="ml-3">
                                                    <p className="text-sm font-semibold text-white">Generate consistent professional headshots</p>
                                                    <p className="text-xs text-gray-400">Team members upload a single smartphone selfie, and the system creates professional, brand-consistent headshot options.</p>
                                                </div>
                                            </div>
                                            <div className="flex items-start">
                                                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-yellow-400/20 text-[#FACC15] flex items-center justify-center font-bold text-xs mt-0.5">4</div>
                                                <div className="ml-3">
                                                    <p className="text-sm font-semibold text-white">Review and deploy</p>
                                                    <p className="text-xs text-gray-400">Review, download, and use the final images across your website, LinkedIn profiles, and internal directories.</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="text-center">
                                    <button
                                        onClick={handleLaunchWorkspace}
                                        className="w-full bg-[#FACC15] hover:bg-yellow-400 text-black font-extrabold py-4 px-8 rounded-xl text-lg transition-all duration-300 shadow-xl shadow-yellow-500/10 hover:shadow-yellow-500/20 hover:-translate-y-0.5">
                                        Launch My Pilot Workspace
                                    </button>
                                    {!user && <p className="text-xs text-gray-500 mt-3">You’ll sign in securely with Google to launch your workspace.</p>}
                                </div>
                            </div>
                        )}
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="w-full min-h-screen bg-[#0d0d0d] text-white flex flex-col font-sans">
            <Helmet>
                <title>Start a {getIndustryPilotTerm()} Virtual Photo Day Pilot | InstantHeadshot.pro</title>
                <meta name="description" content={`Request a complimentary ${getIndustryPilotTerm().toLowerCase()} pilot and test the Virtual Photo Day workflow with your own team. Create consistent, brand-approved headshots without scheduling photographers.`} />
                
                {/* CRITICAL FIX: Explicitly set the canonical path to stop homepage text hijacking */}
                <link rel="canonical" href="https://www.instantheadshot.pro/pilot" />

                {/* Open Graph Meta Tags */}
                <meta property="og:title" content={`Start a ${getIndustryPilotTerm()} Virtual Photo Day Pilot`} />
                <meta property="og:description" content={`Test the workflow with 3 team members and see how brand-consistent headshots can work for your organization.`} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://www.instantheadshot.pro/pilot" />
                <meta property="og:image" content="https://www.instantheadshot.pro/InstantHeadshotpro%20opengraph.png" />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:site_name" content="InstantHeadshot.pro" />
                
                {/* Twitter Meta Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:url" content="https://www.instantheadshot.pro/pilot" />
                <meta name="twitter:title" content={`Start a ${getIndustryPilotTerm()} Virtual Photo Day Pilot`} />
                <meta name="twitter:description" content={`Test the workflow with 3 team members and see how brand-consistent headshots can work for your organization.`} />
                <meta name="twitter:image" content="https://www.instantheadshot.pro/InstantHeadshotpro%20opengraph.png" />
            </Helmet>
            {/* Navigation Header */}
            <header className="flex items-center p-4 justify-between bg-[#121212] border-b border-gray-800 sticky top-0 z-50">
                <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
                    <img src="/InstantHeadshotPro logo.png" alt="Logo" className="h-9 w-auto bg-white p-1 rounded" />
                    <span className="text-white text-lg font-bold hidden sm:block">InstantHeadshot.pro</span>
                </div>
                <div className="flex items-center space-x-4">
                    <button
                        onClick={() => navigate('/')}
                        className="text-gray-400 hover:text-white text-sm font-semibold transition-colors">
                        Home
                    </button>
                    <button
                        onClick={() => setShowLoginModal(true)}
                        className="text-gray-400 hover:text-white text-sm font-semibold transition-colors">
                        Sign In
                    </button>
                </div>
            </header>

            {/* Main Area */}
            <main className="flex-1 flex flex-col items-center py-12 px-4 md:px-8 max-w-6xl mx-auto w-full">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 w-full mt-4">
                    {/* Left Column: Copy & Trust Builders */}
                    <div className="lg:col-span-5 flex flex-col justify-center space-y-8">
                        <div>
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-yellow-400/10 text-[#FACC15] border border-yellow-400/20 mb-4">
                                Complimentary {getIndustryPilotTerm()} Pilot
                            </span>
                            <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight leading-tight text-white">
                                Start a Complimentary {getIndustryPilotTerm()} Virtual Photo Day Pilot
                            </h1>
                            <p className="text-gray-400 mt-6 text-lg leading-relaxed">
                                See how your team can create consistent, brand-approved professional headshots without scheduling photographers, chasing files, or coordinating a traditional photo day.
                            </p>
                        </div>

                        {/* Value Propositions */}
                        <div className="space-y-6">
                            <div className="flex items-start">
                                <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-gray-900 border border-gray-800 flex items-center justify-center text-[#FACC15] font-bold text-lg">
                                    🛡️
                                </div>
                                <div className="ml-4">
                                    <h3 className="text-md font-bold text-white">Privacy-First by Design</h3>
                                    <p className="text-sm text-gray-400 mt-1">
                                        Source photos are processed securely and are not stored long-term. Your pilot workspace is built for professional teams that care about employee privacy.
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start">
                                <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-gray-900 border border-gray-800 flex items-center justify-center text-[#FACC15] font-bold text-lg">
                                    ⚖️
                                </div>
                                <div className="ml-4">
                                    <h3 className="text-md font-bold text-white">Fix the Mismatched Team Page</h3>
                                    <p className="text-sm text-gray-400 mt-1">
                                        Standardize backgrounds, wardrobe style, lighting, and crop so every employee looks like part of the same professional team.
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start">
                                <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-gray-900 border border-gray-800 flex items-center justify-center text-[#FACC15] font-bold text-lg">
                                    ⚡
                                </div>
                                <div className="ml-4">
                                    <h3 className="text-md font-bold text-white">Simple Team Workflow</h3>
                                    <p className="text-sm text-gray-400 mt-1">
                                        Invite up to 3 team members, let them upload a single smartphone selfie, and review consistent professional headshots from one pilot workspace.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="p-4 rounded-xl bg-gray-900/50 border border-gray-800/80 backdrop-blur text-xs text-gray-500">
                            * No credit card required. Qualified teams receive 3 complimentary pilot credits to test the workflow.
                        </div>
                    </div>

                    {/* Right Column: Form */}
                    <div className="lg:col-span-7 flex items-center justify-center">
                        <div className="w-full bg-[#161616]/80 backdrop-blur-md border border-gray-800 rounded-2xl p-6 md:p-10 shadow-2xl relative">
                            {/* Accent indicator */}
                            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-500 to-yellow-400 rounded-t-2xl"></div>

                            <h2 className="text-2xl font-bold mb-8 text-white flex items-center">
                                <span className="bg-yellow-400 text-black h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold mr-3">✓</span>
                                Request Your {getIndustryPilotTerm()} Pilot
                            </h2>

                            <form onSubmit={handleRequestPilot} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Full Name</label>
                                        <input
                                            type="text"
                                            value={fullName}
                                            onChange={(e) => setFullName(e.target.value)}
                                            required
                                            placeholder="John Doe"
                                            className="w-full bg-gray-900 border border-gray-800 rounded-lg py-3 px-4 text-white placeholder-gray-600 focus:outline-none focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-all text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Work Email</label>
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                            placeholder="john@firmname.com"
                                            className="w-full bg-gray-900 border border-gray-800 rounded-lg py-3 px-4 text-white placeholder-gray-600 focus:outline-none focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-all text-sm"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Company Name</label>
                                        <input
                                            type="text"
                                            value={companyName}
                                            onChange={(e) => setCompanyName(e.target.value)}
                                            required
                                            placeholder="Example LLP"
                                            className="w-full bg-gray-900 border border-gray-800 rounded-lg py-3 px-4 text-white placeholder-gray-600 focus:outline-none focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-all text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Company Website</label>
                                        <input
                                            type="url"
                                            value={companyWebsite}
                                            onChange={(e) => setCompanyWebsite(e.target.value)}
                                            required
                                            placeholder="https://example.com"
                                            className="w-full bg-gray-900 border border-gray-800 rounded-lg py-3 px-4 text-white placeholder-gray-600 focus:outline-none focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-all text-sm"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Industry / Category</label>
                                        <select
                                            value={industry}
                                            onChange={(e) => setIndustry(e.target.value)}
                                            className="w-full bg-gray-900 border border-gray-800 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-yellow-400 transition-all text-sm"
                                        >
                                            <option value="Law / Legal">Law / Legal</option>
                                            <option value="Consulting / Professional Services">Consulting / Professional Services</option>
                                            <option value="Accounting / Finance">Accounting / Finance</option>
                                            <option value="Healthcare / Medical">Healthcare / Medical</option>
                                            <option value="Real Estate">Real Estate</option>
                                            <option value="Technology / Startups">Technology / Startups</option>
                                            <option value="Other">Other</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Number of People on Team</label>
                                        <input
                                            type="number"
                                            value={teamSizeNum}
                                            onChange={(e) => {
                                                setTeamSizeNum(e.target.value);
                                                const val = parseInt(e.target.value) || 0;
                                                if (val < 10) setTeamSize('5-9');
                                                else if (val < 25) setTeamSize('10-24');
                                                else if (val < 100) setTeamSize('25-99');
                                                else if (val < 1000) setTeamSize('100-999');
                                                else setTeamSize('1000+');
                                            }}
                                            required
                                            min="1"
                                            placeholder="Approx. team size"
                                            className="w-full bg-gray-900 border border-gray-800 rounded-lg py-3 px-4 text-white placeholder-gray-600 focus:outline-none focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-all text-sm"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Primary Use Case</label>
                                    <select
                                        value={useCase}
                                        onChange={(e) => setUseCase(e.target.value)}
                                        className="w-full bg-gray-900 border border-gray-800 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-yellow-400 transition-all text-sm"
                                    >
                                        {useCasesList.map(uc => (
                                            <option key={uc.value} value={uc.value}>{uc.label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Optional: Current Team Page URL</label>
                                    <input
                                        type="url"
                                        value={teamPageUrl}
                                        onChange={(e) => setTeamPageUrl(e.target.value)}
                                        placeholder="https://example.com/about-us"
                                        className="w-full bg-gray-900 border border-gray-800 rounded-lg py-3 px-4 text-white placeholder-gray-600 focus:outline-none focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-all text-sm"
                                    />
                                </div>

                                {error && <p className="text-red-400 text-xs font-semibold">{error}</p>}

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full bg-[#FACC15] hover:bg-yellow-400 text-black font-extrabold py-4 px-8 rounded-xl text-md transition-all duration-300 shadow-xl shadow-yellow-500/10 hover:shadow-yellow-500/20 disabled:opacity-50 disabled:cursor-not-allowed">
                                    {isSubmitting ? "Requesting Workspace..." : `Request My ${getIndustryPilotTerm()} Pilot`}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </main>

            {/* Minimal Footer */}
            <footer className="border-t border-gray-800 py-8 bg-[#090909] text-center text-xs text-gray-600 mt-12">
                <p>© {new Date().getFullYear()} InstantHeadshot.pro. All rights reserved.</p>
                <p className="mt-2 text-gray-700">Zero-retention architecture processing in-memory with stateless Vercel KV.</p>
            </footer>
        </div>
    );
};

// --- Main App Content with Router ---
const AppContent = React.memo(({
    user,
    profile,
    // page, // <-- No longer needed, logic is internal
    isAuthLoading,
    showHeadshotGenerator,
    // generatedImage, // <-- Not used
    userManuallyChangedMode,
    sessionGeneratedImages,
    generationAttempts,
    onBack,
    onCreditDeduction,
    onSessionExpired,
    imageExpirations,
    activeMode,
    setActiveMode,
    membership,
    organization,
    teamCreditsAvailable,
    pricingView,
    setPricingView,
    setShowLoginModal,
    refreshDriveGallery,
    isTemplateEditorActive,
    handleEditorBack,
    handleSaveToDrive,
    teamTemplates,
    setTeamTemplates,
    setIsTemplateEditorActive,
    // generationAttempts_prop, // <-- Prop name collision, use 'generationAttempts'
    setGenerationAttempts,
    // sessionGeneratedImages_prop, // <-- Prop name collision, use 'sessionGeneratedImages'
    setSessionGeneratedImages,
    // imageExpirations_prop, // <-- Prop name collision, use 'imageExpirations'
    setImageExpirations,
    currentPreviewImage,
    setCurrentPreviewImage,
    uploadedImage,
    setUploadedImage,
    uploadedImagePreview,
    setUploadedImagePreview,
    selfieValidation,
    setSelfieValidation,
    logoValidation,
    setLogoValidation,
    detectedGender,
    setDetectedGender,
    selectedCategoryId,
    setSelectedCategoryId,
    selectedPresetId,
    setSelectedPresetId,
    selectedAspectRatio,
    setSelectedAspectRatio,
    selectedCorporateWardrobeId,
    setSelectedCorporateWardrobeId,
    selectedCorporateBackgroundId,
    setSelectedCorporateBackgroundId,
    corporateBackgroundMode,
    setCorporateBackgroundMode,
    uniformMode,
    setUniformMode,
    backgroundMode,
    setBackgroundMode,
    selectedUniformPresetId,
    setSelectedUniformPresetId,
    selectedBackgroundPresetId,
    setSelectedBackgroundPresetId,
    academicBackgroundMode,
    setAcademicBackgroundMode,
    backgroundColor,
    setBackgroundColor,
    uploadedLogo,
    setUploadedLogo,
    uploadedLogoPreview,
    setUploadedLogoPreview,
    colorToneOverride,
    setColorToneOverride,
    uniformColorOverride,
    setUniformColorOverride,
    blur,
    setBlur,
    brightness,
    setBrightness,
    tintColor,
    setTintColor,
    tintStrength,
    setTintStrength,
    vignette,
    setVignette,
    keepPerspective,
    setKeepPerspective,
    logoPosition,
    setLogoPosition,
    logoOpacity,
    setLogoOpacity,
    logoConsent,
    setLogoConsent,
    logoPlacement,
    setLogoPlacement,
    templateName,
    setTemplateName,
    templateDescription,
    setTemplateDescription,
    handleStripePayment,
    isProcessingPayment,
    setError,
    setShowHeadshotGenerator,
    googleDriveToken,
    error,
    handleAuthSuccess,
    handleAuthError,

    // --- ADD THESE MISSING PROPS ---
    isFetchingDrive,
    driveImages,
    generatedImages,
    isLoading,
    handleGenerateBio,
    isTextLoading,
    linkedInBio,

    // --- ADD THESE TWO LINES ---
    db,
    storage,

    // --- ADD THESE PROPS ---
    toast,
    showToast
    // --- END ADD ---
}) => {
    const navigate = useNavigate();
    const location = useLocation();

    // --- UTM TRACKING ---
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const source = searchParams.get('utm_source');
        const campaign = searchParams.get('utm_campaign');
        const content = searchParams.get('utm_content');

        let hasUtm = false;
        if (source) { localStorage.setItem('gh_referral_source', source); hasUtm = true; }
        if (campaign) { localStorage.setItem('gh_referral_campaign', campaign); hasUtm = true; }
        if (content) { localStorage.setItem('gh_referral_content', content); hasUtm = true; }

        if (hasUtm) {
            localStorage.setItem('gh_referral_date', new Date().toISOString());
        }
    }, [location.search]);
    // --- END UTM TRACKING ---

    // --- FIX FOR ISSUE 1: Load assigned team template when generator opens ---
    useEffect(() => {
        // When the generator opens
        if (showHeadshotGenerator && activeMode === 'team' && membership?.assignedTemplateId) {
            const assignedTemplate = teamTemplates.find(t => t.id === membership.assignedTemplateId);

            if (assignedTemplate) {
                console.log("Applying assigned team template:", assignedTemplate.name);

                // Apply all settings from the template, providing defaults for any missing fields
                // (in case the template was saved before the new fields were added)

                setSelectedCategoryId(assignedTemplate.categoryId || 'corporate');
                setSelectedPresetId(assignedTemplate.presetId || null);
                setSelectedAspectRatio(assignedTemplate.aspectRatio || '1:1');

                // Corporate
                if (assignedTemplate.categoryId === 'corporate') {
                    setSelectedCorporateWardrobeId(assignedTemplate.corporateWardrobeId || 'business_formal');

                    // --- THIS IS THE FIX ---
                    const templateMode = assignedTemplate.corporateBackgroundMode || 'preset';
                    setCorporateBackgroundMode(templateMode);

                    if (templateMode === 'preset') {
                        // If mode is preset, set the ID from the template
                        setSelectedCorporateBackgroundId(assignedTemplate.corporateBackgroundId || 'blurred_office');
                    } else {
                        // If mode is color, set the ID to null (it won't be used)
                        setSelectedCorporateBackgroundId(null);
                    }
                    // --- END FIX ---
                }

                // Medical
                if (assignedTemplate.categoryId === 'medical') {
                    setUniformMode(assignedTemplate.uniformMode || 'preset');
                    setBackgroundMode(assignedTemplate.backgroundMode || 'preset');
                    setSelectedUniformPresetId(assignedTemplate.uniformPresetId || 'medical_scrubs');
                    setSelectedBackgroundPresetId(assignedTemplate.backgroundPresetId || 'clinic_lobby');
                    setUniformColorOverride(assignedTemplate.uniformColorOverride || '');
                }

                // Academic
                if (assignedTemplate.categoryId === 'academic') {
                    setAcademicBackgroundMode(assignedTemplate.academicBackgroundMode || 'preset');
                    setSelectedBackgroundPresetId(assignedTemplate.academicBackgroundPresetId || 'university_library');
                }

                // --- INSERT THIS NEW BLOCK HERE ---
                // Handle Logo Loading from Template
                if (assignedTemplate.logoUrl) {
                    // 1. Show the visual preview immediately
                    setUploadedLogoPreview(assignedTemplate.logoUrl);

                    // 2. Validate it immediately so the UI looks good
                    setLogoValidation({ status: 'valid', message: 'Loaded from template' });

                    // 3. Auto-consent for team templates (admin already provided consent)
                    setLogoConsent(true);

                    // 4. Fetch the file blob so the generator can use it
                    const loadLogoFile = async () => {
                        try {
                            const response = await fetch(assignedTemplate.logoUrl);
                            const blob = await response.blob();
                            // Create a file object from the blob
                            const file = new File([blob], "template_logo.png", { type: blob.type });
                            setUploadedLogo(file);
                            console.log("Template logo loaded and converted to File object");
                        } catch (error) {
                            console.error("Failed to load template logo file:", error);
                            // Optional: Show a toast error here
                        }
                    };
                    loadLogoFile();
                } else {
                    // Reset if no logo in template
                    setUploadedLogo(null);
                    setUploadedLogoPreview(null);
                }
                // --- END NEW BLOCK ---

                // Shared settings
                setBackgroundColor(assignedTemplate.backgroundColor || '#e0e0e0');
                setLogoPlacement(assignedTemplate.logoPlacement || 'uniform');
                setLogoOpacity(assignedTemplate.logoOpacity || 100);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showHeadshotGenerator, membership, teamTemplates, activeMode]); // Add all dependencies

    // Internal page state managed by the router
    const [currentPage, setCurrentPage] = useState('landing');

    // Update page state based on URL and handle payment redirects
    useEffect(() => {
        const path = location.pathname;
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get('session_id');
        const inviteToken = urlParams.get('token');

        if (path === '/privacy') {
            setCurrentPage('privacy');
        } else if (path === '/terms') {
            setCurrentPage('terms');

            // --- ADD THIS BLOCK ---
        } else if (path === '/accept-invite' && inviteToken) {
            setCurrentPage('accept-invite');
            // --- END ADD ---

        } else if (path === '/pilot' || path === '/virtual-photo-day-pilot') {
            setCurrentPage('pilot');
        } else if (path === '/admin/growth') {
            setCurrentPage('admin-growth');
        } else if (path === '/success' && sessionId) {
            // Wait for authentication to complete before processing payment
            if (!isAuthLoading && user) {
                // Auth is complete and user is logged in, process the payment
                console.log('User is ready, processing payment success for session:', sessionId);
                // handlePaymentSuccess(sessionId); // This is now called from App
                setCurrentPage('success');
                // --- DELETE OR COMMENT OUT THIS LINE ---
                // window.history.replaceState({ }, document.title, '/success');
                // ---------------------------------------
            } else if (!isAuthLoading && !user) {
                // Auth is complete but no user is logged in. This is an error.
                console.error('Payment success redirect but no user logged in.');
                navigate('/'); // Redirect to home
            }
            // If isAuthLoading is true, do nothing yet.
            // The effect will re-run when user or isAuthLoading changes,
            // and the sessionId will still be in the URL.
        } else if (path === '/cancel' && sessionId) {
            setCurrentPage('cancel');
            // This one is fine to clear immediately
            window.history.replaceState({}, document.title, '/cancel');
        } else if (path === '/success' && !sessionId) {
            // If success page without session_id, redirect to home
            navigate('/');
        } else if (path === '/cancel' && !sessionId) {
            // If cancel page without session_id, redirect to home
            navigate('/');
        } else if (
            path === '/' ||
            getNicheFromPath(path) !== null || // Dynamic Support for /niche or /teams/niche
            path.startsWith('/teams/') // Keep generic /teams/* resolving to landing
        ) {
            if (!user && !isAuthLoading) { // Only go to landing if not logged in and auth check is done
                setCurrentPage('landing');
            } else if (user) { // If user is logged in, stay/go to dashboard
                setCurrentPage('dashboard');

                // --- NEW LOGIC FOR SEO ENTRY POINTS ---
                // If they logged in and had an intended category, make sure we use it
                const intendedCategory = localStorage.getItem('intendedCategory');
                const urlCat = urlParams.get('category');
                const targetCategory = urlCat || intendedCategory;

                if (targetCategory && !showHeadshotGenerator && !isTemplateEditorActive) {
                    // Pre-select the generator category if they came from an industry page
                    setSelectedCategoryId(targetCategory);
                    // Clear the stored category so we don't force it every time they visit the dashboard later
                    localStorage.removeItem('intendedCategory');
                    // Remove category param from URL so refresh doesn't trigger it again
                    if (urlCat) {
                        const newUrl = new URL(window.location.href);
                        newUrl.searchParams.delete('category');
                        window.history.replaceState({}, document.title, newUrl.pathname + newUrl.search);
                    }
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, user, isAuthLoading]); // Added user and isAuthLoading dependency

    // Handle back navigation for legal pages
    const handleBackToHome = () => {
        navigate('/');
        setCurrentPage('landing');
    };

    if (isAuthLoading) {
        return (
            <div className="w-full min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-white text-xl flex items-center">
                    <Spinner large />
                    <span className="ml-4">Authenticating...</span>
                </div>
            </div>
        );
    }

    if (user && currentPage === 'dashboard') { // Ensure dashboard is shown only when intended
        if (showHeadshotGenerator || isTemplateEditorActive) {
            // console.log('DEBUG: App.js - Rendering HeadshotGenerator, showHeadshotGenerator:', showHeadshotGenerator, 'isTemplateEditorActive:', isTemplateEditorActive);
            return (
                <HeadshotGenerator
                    key="headshot-generator"
                    // --- ADD THESE ---
                    mode={isTemplateEditorActive ? 'editor' : 'generate'}
                    db={db}
                    storage={storage}
                    user={user}
                    organization={organization}
                    // --- MODIFY THIS ---
                    onBack={isTemplateEditorActive ? handleEditorBack : onBack}
                    // --- END MODIFICATIONS ---
                    profile={profile}
                    onCreditDeduction={onCreditDeduction}
                    onSaveToDrive={handleSaveToDrive}
                    onSessionExpired={onSessionExpired} // <-- ADD THIS

                    // --- ADD THIS PROP ---
                    showToast={showToast}
                    // --- END ADD ---

                    // Pass all team state
                    activeMode={activeMode}
                    membership={membership}
                    teamCreditsAvailable={teamCreditsAvailable}
                    teamTemplates={teamTemplates}

                    // --- ADD THESE TWO LINES ---
                    setIsTemplateEditorActive={setIsTemplateEditorActive}
                    setTeamTemplates={setTeamTemplates}
                    // --- END OF FIX ---

                    // Pass all state and setters as props
                    generationAttempts={generationAttempts}
                    setGenerationAttempts={setGenerationAttempts}
                    sessionGeneratedImages={sessionGeneratedImages}
                    setSessionGeneratedImages={setSessionGeneratedImages}
                    imageExpirations={imageExpirations}
                    setImageExpirations={setImageExpirations}
                    currentPreviewImage={currentPreviewImage}
                    setCurrentPreviewImage={setCurrentPreviewImage}
                    uploadedImage={uploadedImage}
                    setUploadedImage={setUploadedImage}
                    uploadedImagePreview={uploadedImagePreview}
                    setUploadedImagePreview={setUploadedImagePreview}
                    selfieValidation={selfieValidation}
                    setSelfieValidation={setSelfieValidation}
                    // REMOVED: backgroundValidation props
                    // REMOVED: corporateBackgroundValidation props
                    // REMOVED: referenceHeadshotValidation props
                    logoValidation={logoValidation}
                    setLogoValidation={setLogoValidation}
                    detectedGender={detectedGender}
                    setDetectedGender={setDetectedGender}
                    selectedCategoryId={selectedCategoryId}
                    setSelectedCategoryId={setSelectedCategoryId}
                    selectedPresetId={selectedPresetId}
                    setSelectedPresetId={setSelectedPresetId}
                    selectedAspectRatio={selectedAspectRatio}
                    setSelectedAspectRatio={setSelectedAspectRatio}

                    // Corporate state
                    selectedCorporateWardrobeId={selectedCorporateWardrobeId}
                    setSelectedCorporateWardrobeId={setSelectedCorporateWardrobeId}
                    selectedCorporateBackgroundId={selectedCorporateBackgroundId}
                    setSelectedCorporateBackgroundId={setSelectedCorporateBackgroundId}
                    corporateBackgroundMode={corporateBackgroundMode}
                    setCorporateBackgroundMode={setCorporateBackgroundMode}
                    // REMOVED: uploadedCorporateBackground props
                    // REMOVED: uploadedCorporateBackgroundPreview props
                    // REMOVED: uploadedReferenceHeadshot props
                    // REMOVED: uploadedReferenceHeadshotPreview props
                    // REMOVED: corporateConsent props

                    // Medical/Institutional state
                    uniformMode={uniformMode}
                    setUniformMode={setUniformMode}
                    backgroundMode={backgroundMode}
                    setBackgroundMode={setBackgroundMode}
                    selectedUniformPresetId={selectedUniformPresetId}
                    setSelectedUniformPresetId={setSelectedUniformPresetId}
                    selectedBackgroundPresetId={selectedBackgroundPresetId}
                    setSelectedBackgroundPresetId={setSelectedBackgroundPresetId}

                    // Academic state
                    academicBackgroundMode={academicBackgroundMode}
                    setAcademicBackgroundMode={setAcademicBackgroundMode}
                    backgroundColor={backgroundColor}
                    setBackgroundColor={setBackgroundColor}
                    // REMOVED: uploadedBackground props
                    // REMOVED: uploadedBackgroundPreview props
                    uploadedLogo={uploadedLogo}
                    setUploadedLogo={setUploadedLogo}
                    uploadedLogoPreview={uploadedLogoPreview}
                    setUploadedLogoPreview={setUploadedLogoPreview}
                    colorToneOverride={colorToneOverride}
                    setColorToneOverride={setColorToneOverride}
                    uniformColorOverride={uniformColorOverride}
                    setUniformColorOverride={setUniformColorOverride}

                    // Advanced settings
                    blur={blur}
                    setBlur={setBlur}
                    brightness={brightness}
                    setBrightness={setBrightness}
                    tintColor={tintColor}
                    setTintColor={setTintColor}
                    tintStrength={tintStrength}
                    setTintStrength={setTintStrength}
                    vignette={vignette}
                    setVignette={setVignette}
                    keepPerspective={keepPerspective}
                    setKeepPerspective={setKeepPerspective}
                    logoPosition={logoPosition}
                    setLogoPosition={setLogoPosition}
                    logoOpacity={logoOpacity}
                    setLogoOpacity={setLogoOpacity}
                    // REMOVED: consent props
                    logoConsent={logoConsent}
                    setLogoConsent={setLogoConsent}
                    logoPlacement={logoPlacement}
                    setLogoPlacement={setLogoPlacement}

                    // Template editor state
                    templateName={templateName}
                    setTemplateName={setTemplateName}
                    templateDescription={templateDescription}
                    setTemplateDescription={setTemplateDescription}
                />
            );
        }
        // MODIFIED: Pass session images to Dashboard to be displayed
        return (
            <div>
                <Dashboard
                    sessionGeneratedImages={sessionGeneratedImages}
                    generationAttempts={generationAttempts}
                    handleCreditDeduction={onCreditDeduction}
                    imageExpirations={imageExpirations}
                    onSessionExpired={onSessionExpired} // <-- ADD THIS

                    // --- ADD THIS PROP ---
                    showToast={showToast}
                    // --- END ADD ---

                    // --- PASS THE PROPS TO DASHBOARD ---
                    profile={profile}
                    activeMode={activeMode}
                    setActiveMode={setActiveMode} // Pass setter to Dashboard
                    membership={membership}
                    organization={organization}
                    teamCreditsAvailable={teamCreditsAvailable}

                    // --- ADD THESE NEW PROPS ---
                    pricingView={pricingView}
                    setPricingView={setPricingView}
                    refreshDriveGallery={refreshDriveGallery} // <-- PASS PROP HERE

                    // --- PASS TEAM PROPS FOR ADMIN PANEL ---
                    teamTemplates={teamTemplates}
                    setTeamTemplates={setTeamTemplates}
                    setIsTemplateEditorActive={setIsTemplateEditorActive}
                    user={user}

                    // --- PASS PROPS FOR GENERATOR ---
                    handleStripePayment={handleStripePayment}
                    isProcessingPayment={isProcessingPayment}
                    setError={setError}
                    setShowHeadshotGenerator={setShowHeadshotGenerator}
                    googleDriveToken={googleDriveToken}
                    handleSaveToDrive={handleSaveToDrive}
                    error={error}

                    // --- PASS DRIVE AUTH HANDLERS ---
                    handleAuthSuccess={handleAuthSuccess}
                    handleAuthError={handleAuthError}

                    // --- ADD THESE MISSING PROPS ---
                    isFetchingDrive={isFetchingDrive}
                    driveImages={driveImages}
                    generatedImages={generatedImages}
                    handleGenerateBio={handleGenerateBio}
                    isTextLoading={isTextLoading}
                    linkedInBio={linkedInBio}
                    // --- ADD MODE PERSISTENCE PROP ---
                    userManuallyChangedMode={userManuallyChangedMode}
                // --- END OF ADDITIONS ---
                />
            </div>
        );
    }

    // Render other pages based on state/URL
    switch (currentPage) {
        case 'privacy':
            return <PrivacyPolicy onBack={handleBackToHome} />;
        case 'terms':
            return <TermsOfService onBack={handleBackToHome} />;

        // --- ADD THIS CASE ---
        case 'accept-invite':
            return <AcceptInvitePage user={user} setShowLoginModal={setShowLoginModal} />;
        // --- END ADD ---
        case 'pilot':
            return (
                <PilotRequestPage
                    user={user}
                    db={db}
                    showToast={showToast}
                    setShowLoginModal={setShowLoginModal}
                    navigate={navigate}
                    membership={membership}
                    organization={organization}
                    activeMode={activeMode}
                    setActiveMode={setActiveMode}
                />
            );
        case 'admin-growth':
            return <GrowthDashboard />;
        case 'success':
            return <PaymentSuccess />;
        case 'cancel':
            return <PaymentCancel />;
        case 'landing':
        default:
            // If user is somehow logged in but page is landing, redirect to dashboard
            if (user) {
                setCurrentPage('dashboard');
                return null; // Prevent rendering LandingPage briefly
            }
            return <LandingPage setShowLoginModal={setShowLoginModal} />;
    }
});


// --- Main App Component ---
function App() {
    // --- ADD THIS REF FOR MODE PERSISTENCE ---
    const userManuallyChangedMode = useRef(false);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const lid = params.get('lid');
        const bump = params.get('bump');
        
        if (lid) {
            localStorage.setItem('gh_lid', lid);
            try {
                // Use setDoc with merge:true so it never fails even if the doc was somehow not created by the server
                if (bump) {
                    setDoc(doc(db, 'leads_bridge', lid), { bumpClicked: true, bumpClickedAt: new Date() }, { merge: true }).catch(() => { });
                } else {
                    setDoc(doc(db, 'leads_bridge', lid), { clicked: true, clickedAt: new Date() }, { merge: true }).catch(() => { });
                }
            } catch (err) { }
        }

        // Auto-scroll for SPA anchor links
        if (window.location.hash === '#demo') {
            setTimeout(() => {
                const element = document.getElementById('demo');
                if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 800); // Allow time for DOM to render
        }
    }, []);
    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState(null);
    // const [uploadedFiles, setUploadedFiles] = useState([]); // FIX: Commented out unused state
    const [generatedImages] = useState([]);
    // const [isLoading, setIsLoading] = useState(false); // FIX: This was unused
    const [isAuthLoading, setIsAuthLoading] = useState(true);
    const [isTextLoading, setIsTextLoading] = useState(false);
    const [error, setError] = useState('');
    const [customPrompt] = useState(''); // FIX: Removed unused 'setCustomPrompt'
    const [linkedInBio, setLinkedInBio] = useState('');
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const [showHeadshotGenerator, setShowHeadshotGenerator] = useState(false);

    const [isTemplateEditorActive, setIsTemplateEditorActive] = useState(false); // Add this

    // --- TEAM-AWARE STATE MANAGEMENT ---
    const [activeMode, setActiveMode] = useState('personal'); // 'personal' or 'team'

    // Correct team state variables
    const [membership, setMembership] = useState(null);
    const [organization, setOrganization] = useState(null);
    const [teamCreditsAvailable, setTeamCreditsAvailable] = useState(0);
    const [teamTemplates, setTeamTemplates] = useState([]);

    // --- AUTO-ROUTE TO TEAM POOL ---
    useEffect(() => {
        if (!userManuallyChangedMode.current && organization) {
            setActiveMode('team');
        }
    }, [organization]);

    // --- ADD THIS NEW STATE ---
    const [pricingView, setPricingView] = useState('individual');

    // --- ADD THIS STATE ---
    // --- END ADD ---
    // --- END NEW STATE ---

    // --- State for Google Drive Integration ---
    const [googleDriveToken, setGoogleDriveToken] = useState(() => {
        return localStorage.getItem('googleDriveToken');
    });
    const [driveFolderId, setDriveFolderId] = useState(null);
    const [driveImages, setDriveImages] = useState([]);
    const [isFetchingDrive, setIsFetchingDrive] = useState(false);

    const [generatedImage] = useState(null);

    // --- ADD THIS STATE HERE ---
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    // --- END ADD ---

    // --- ADD THIS FUNCTION HERE ---
    const showToast = useCallback((message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => {
            setToast({ show: false, message: '', type: 'success' });
        }, 3000);
    }, [setToast]); // setToast is stable
    // --- END ADD ---

    // MODIFIED: Centralized state management with localStorage persistence
    const [generationAttempts, setGenerationAttempts] = useState(() => {
        const saved = localStorage.getItem('generationAttempts');
        return saved ? JSON.parse(saved) : 0;
    });

    const [sessionGeneratedImages, setSessionGeneratedImages] = useState(() => {
        const saved = localStorage.getItem('sessionGeneratedImages');
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                // Filter for valid image objects
                return parsed.filter(img =>
                    img &&
                    typeof img.url === 'string' &&
                    // img.url.startsWith('data:image/') && // Check for blob: too // <-- This was too restrictive
                    typeof img.id === 'string'
                );
            } catch (error) {
                console.warn('Failed to parse sessionGeneratedImages from localStorage:', error);
                return [];
            }
        }
        return [];
    });

    // Individual expiration times for each generated image (15 minutes = 900 seconds)
    const [imageExpirations, setImageExpirations] = useState(() => {
        const saved = localStorage.getItem('imageExpirations');
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                // Filter out expired timers on load
                const now = Date.now();
                const validExpirations = {};
                Object.entries(parsed).forEach(([id, time]) => { // <-- Key is now 'id'
                    if (typeof time === 'number' && time > now) {
                        validExpirations[id] = time; // <-- Store by 'id'
                    }
                });
                return validExpirations;
            } catch (e) {
                console.warn('Failed to parse imageExpirations from localStorage:', e);
                return {};
            }
        }
        return {};
    });


    // ========================================================================
    // LIFTED STATE FOR GENERATOR & SESSION
    // ========================================================================
    const [currentPreviewImage, setCurrentPreviewImage] = useState(null);

    // --- UI State moved from HeadshotGenerator ---
    const [uploadedImage, setUploadedImage] = useState(null);
    const [uploadedImagePreview, setUploadedImagePreview] = useState(null);
    const [selfieValidation, setSelfieValidation] = useState({ status: 'idle', message: '' });
    // REMOVED: backgroundValidation state
    // REMOVED: corporateBackgroundValidation state
    // REMOVED: referenceHeadshotValidation state
    const [logoValidation, setLogoValidation] = useState({ status: 'idle', message: '' });
    const [detectedGender, setDetectedGender] = useState(null);
    const [selectedCategoryId, setSelectedCategoryId] = useState('corporate'); // <-- Set default
    const [selectedPresetId, setSelectedPresetId] = useState('business_formal'); // <-- Set default
    const [selectedAspectRatio, setSelectedAspectRatio] = useState('1:1');

    // Template editor state
    const [templateName, setTemplateName] = useState('');
    const [templateDescription, setTemplateDescription] = useState('');

    // Corporate state
    const [selectedCorporateWardrobeId, setSelectedCorporateWardrobeId] = useState('business_formal');
    const [selectedCorporateBackgroundId, setSelectedCorporateBackgroundId] = useState('blurred_office');
    const [corporateBackgroundMode, setCorporateBackgroundMode] = useState('preset');
    // REMOVED: uploadedCorporateBackground state
    // REMOVED: uploadedCorporateBackgroundPreview state
    // REMOVED: uploadedReferenceHeadshot state
    // REMOVED: uploadedReferenceHeadshotPreview state
    // REMOVED: corporateConsent state

    // Medical/Institutional state
    const [uniformMode, setUniformMode] = useState('preset');
    const [backgroundMode, setBackgroundMode] = useState('preset');
    const [selectedUniformPresetId, setSelectedUniformPresetId] = useState('medical_scrubs');
    const [selectedBackgroundPresetId, setSelectedBackgroundPresetId] = useState('clinic_lobby');

    // Academic state
    const [academicBackgroundMode, setAcademicBackgroundMode] = useState('preset');
    const [backgroundColor, setBackgroundColor] = useState('#e0e0e0');
    // REMOVED: uploadedBackground state
    // REMOVED: uploadedBackgroundPreview state
    const [uploadedLogo, setUploadedLogo] = useState(null);
    const [uploadedLogoPreview, setUploadedLogoPreview] = useState(null);
    const [colorToneOverride, setColorToneOverride] = useState('');
    const [uniformColorOverride, setUniformColorOverride] = useState('');

    // Advanced settings
    const [blur, setBlur] = useState(8);
    const [brightness, setBrightness] = useState(0);
    const [tintColor, setTintColor] = useState('#FDE68A'); // <-- Restored color
    const [tintStrength, setTintStrength] = useState(0);
    const [vignette, setVignette] = useState(false);
    const [keepPerspective, setKeepPerspective] = useState(false); // <-- Restored default
    const [logoPosition, setLogoPosition] = useState('bottom_right');
    const [logoOpacity, setLogoOpacity] = useState(80); // <-- Restored default
    // REMOVED: consent state
    const [logoConsent, setLogoConsent] = useState(false);
    const [logoPlacement, setLogoPlacement] = useState('uniform');

    // ========================================================================
    // END LIFTED STATE
    // ========================================================================

    // --- LOGIN STATE & MODAL ---
    const [showLoginModal, setShowLoginModal] = useState(false);




    // --- AUTHENTICATION ---

    // Handle Google Sign-In
    const handleSignIn = useCallback(async () => {
        setIsAuthLoading(true);
        setError('');
        const provider = new GoogleAuthProvider();
        // Add scopes for Google Drive access
        provider.addScope('https://www.googleapis.com/auth/drive.file');
        provider.addScope('https://www.googleapis.com/auth/userinfo.email');
        provider.addScope('https://www.googleapis.com/auth/userinfo.profile');

        try {
            const result = await signInWithPopup(auth, provider);
            const user = result.user;

            // --- ADD THIS BLOCK ---
            const credential = GoogleAuthProvider.credentialFromResult(result);
            if (credential && credential.accessToken) {
                console.log("Successfully obtained Google Drive access token from Firebase");
                setGoogleDriveToken(credential.accessToken);
                // Save token to localStorage for persistence
                localStorage.setItem('googleDriveToken', credential.accessToken);
            } else {
                console.warn("No Google Drive access token found in credential.");
            }
            // --- END ADD ---

            // Check if user is new or existing
            const userRef = doc(db, 'profiles', user.uid);
            const userSnap = await getDoc(userRef);

            // --- IDENTITY BRIDGE: AUTO-PROVISION WORKSPACE ---
            const lid = localStorage.getItem('gh_lid');

            if (lid) {
                console.log(`Identity Bridge: Initiating auto-provisioning for lead ${lid}...`);
                let leadData = { Firm_Name: "Your Team", Niche: "professional services", Team_Size: 0 };

                try {
                    console.log(`Identity Bridge: Fetching lead info from internal DB...`);
                    const leadSnap = await getDoc(doc(db, 'leads_bridge', lid));
                    if (leadSnap.exists()) {
                        leadData = leadSnap.data();
                        console.log("Identity Bridge: Lead mapped successfully", leadData);
                    } else {
                        console.warn("Identity Bridge: Lead not found in database. Using generic fallback template.");
                    }
                } catch (err) {
                    console.warn("Identity Bridge: GTM server unreachable in this environment. Falling back to generic PLG team template.");
                }

                try {
                    const initialCredits = leadData.complimentaryCredits || 2; // Complimentary evaluation

                    // 1. Create Organization
                    const orgRef = doc(collection(db, 'organizations'));
                    await setDoc(orgRef, {
                        ownerId: user.uid,
                        orgName: `${leadData.Firm_Name || "Your Team"}'s Team`,
                        sharedCredits: initialCredits,
                        createdAt: new Date(),
                        niche: leadData.Niche || "professional services",
                        teamSizeOriginal: leadData.Team_Size || 0,
                        source: 'SDR_Identity_Bridge',
                        status: 'active'
                    });

                    // 1.5 Send Conversion Signal back to GTM pipeline!
                    try {
                        await updateDoc(doc(db, 'leads_bridge', lid), { signed_up: true, convertedAt: new Date() });
                    } catch (err) { console.log(err); }    // 2. Link User (Must use user.uid exactly like the webhook)
                    await setDoc(doc(db, 'memberships', user.uid), {
                        userId: user.uid,
                        organizationId: orgRef.id,
                        role: 'admin',
                        status: 'active',
                        allottedCredits: 99999,
                        usedCredits: 0,
                        joinedAt: new Date()
                    });

                    // 3. Force User Profile into Team Mode
                    const profileSdrRef = doc(db, 'profiles', user.uid);
                    await setDoc(profileSdrRef, {
                        activeOrganizationId: orgRef.id,
                        activeMode: 'team'
                    }, { merge: true });

                    setActiveMode('team'); // Force UI to instantly switch
                    localStorage.removeItem('gh_lid'); // Consume link
                } catch (err) {
                    console.error("Identity Bridge failed during Firestore injection: ", err);
                }
            }
            // --- END IDENTITY BRIDGE ---

            if (!userSnap.exists()) {
                // New user - create profile with default 0 credits
                // (Evaluation credits were placed safely into their Team Organization pool earlier)
                await setDoc(userRef, {
                    userId: user.uid,
                    email: user.email,
                    name: user.displayName,
                    credits: 0,
                    createdAt: new Date(),
                    authProvider: 'google'
                });
            }

            setShowLoginModal(false);
        } catch (error) {
            console.error(error);
            setError(`Failed to sign in: ${error.message}`);
        } finally {
            setIsAuthLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty dependency array since we don't need external dependencies



    // --- GOOGLE DRIVE INTEGRATION ---

    // Add the missing getDriveFolderId function
    const getDriveFolderId = useCallback(async () => {
        if (!googleDriveToken) return null;

        // Use a cached folder ID to prevent multiple folder creation
        if (driveFolderId) {
            console.log("Using cached Drive folder ID:", driveFolderId);
            return driveFolderId;
        }

        const folderName = "InstantHeadshot.pro Images"; // Make sure this matches your hook
        try {
            console.log("Searching for existing Drive folder...");
            const searchParams = new URLSearchParams({
                q: `name='${folderName}' and mimeType='application/vnd.google-apps.folder' and trashed=false`
            });
            const searchResponse = await fetch(`https://www.googleapis.com/drive/v3/files?${searchParams.toString()}`, {
                headers: { 'Authorization': `Bearer ${googleDriveToken}` }
            });

            if (!searchResponse.ok) {
                if (searchResponse.status === 401 || searchResponse.status === 403) {
                    console.log("Google Drive token expired or invalid, clearing token");
                    setGoogleDriveToken(null);
                    localStorage.removeItem('googleDriveToken');
                    setError("Google Drive authentication expired. Please sign in again.");
                    return null;
                }
                throw new Error('Failed to search for Drive folder.');
            }

            const searchData = await searchResponse.json();

            if (searchData.files && searchData.files.length > 0) {
                const folderId = searchData.files[0].id;
                console.log("Found existing Drive folder:", folderId);
                setDriveFolderId(folderId);
                return folderId;
            } else {
                console.log("Creating new Drive folder...");
                const createResponse = await fetch('https://www.googleapis.com/drive/v3/files', {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${googleDriveToken}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ name: folderName, mimeType: 'application/vnd.google-apps.folder' })
                });

                if (!createResponse.ok) {
                    if (createResponse.status === 401 || createResponse.status === 403) {
                        setGoogleDriveToken(null);
                        localStorage.removeItem('googleDriveToken');
                        setError("Google Drive authentication expired. Please sign in again.");
                        return null;
                    }
                    throw new Error('Failed to create Drive folder.');
                }

                const createData = await createResponse.json();
                console.log("Created new Drive folder:", createData.id);
                setDriveFolderId(createData.id);
                return createData.id;
            }
        } catch (error) {
            console.error("Google Drive folder error:", error);
            setError("Could not access Google Drive. Try signing out and in again.");
            return null;
        }
    }, [googleDriveToken, driveFolderId, setGoogleDriveToken, setDriveFolderId, setError]);


    const refreshDriveGallery = useCallback(async () => {
        if (!user) {
            console.log("No user, skipping gallery refresh.");
            return;
        }

        console.log("Refreshing Drive gallery...");
        setIsFetchingDrive(true);
        try {
            const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:3001' : '';

            // --- THIS IS THE FIX ---
            // Check if this is a Team Gallery request
            if (activeMode === 'team' && organization?.googleDriveFolderId) {

                console.log("Using TEAM gallery refresh flow");
                const token = await auth.currentUser.getIdToken();
                const response = await fetch(`${baseUrl}/api/drive/list-team-images`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (!response.ok) {
                    const errData = await response.json();
                    throw new Error(errData.error || "Failed to list team images");
                }

                const data = await response.json();
                setDriveImages(data.files || []);

            } else if (activeMode === 'personal' && googleDriveToken) {
                // This is the ORIGINAL logic for PERSONAL gallery
                console.log("Using PERSONAL gallery refresh flow");
                const folderId = await getDriveFolderId();
                if (!folderId) {
                    throw new Error("Could not find or create personal app folder.");
                }
                setDriveFolderId(folderId); // Save personal folder ID

                const queryParams = new URLSearchParams({
                    q: `'${folderId}' in parents and trashed = false`,
                    fields: 'files(id, name, thumbnailLink, webContentLink, createdTime, description)',
                    orderBy: 'createdTime desc',
                    pageSize: '100'
                });

                const url = `https://www.googleapis.com/drive/v3/files?${queryParams.toString()}`;

                const response = await fetch(url, {
                    headers: { 'Authorization': `Bearer ${googleDriveToken}` }
                });

                if (!response.ok) {
                    if (response.status === 401) {
                        console.log("Drive token expired or invalid. Clearing token.");
                        localStorage.removeItem('googleDriveToken');
                        setGoogleDriveToken(null);
                    }
                    throw new Error(`Failed to list Drive files: ${response.statusText}`);
                }

                const data = await response.json();
                setDriveImages(data.files || []);

            } else {
                console.log("Skipping gallery refresh: No valid folder or token for current mode.");
                setDriveImages([]); // Clear images if no valid state
            }
            // --- END FIX ---

        } catch (error) {
            console.error("Error refreshing Drive gallery:", error);
            // Don't show generic error to user, just log it

            // --- ADD THIS LINE ---
            setDriveImages([]); // Clear images on any error
            // --- END FIX ---
        } finally {
            setIsFetchingDrive(false);
        }
    }, [
        user, // <-- ADD
        googleDriveToken,
        getDriveFolderId,
        activeMode,
        organization?.googleDriveFolderId // <-- MODIFY
    ]);


    // --- PAYMENT PROCESSING ---

    const handleStripePayment = async (priceIdKey) => {
        if (!user) {
            setError("You must be logged in to make a purchase.");
            setShowLoginModal(true);
            return;
        }

        // 1. Validate Product Config
        const product = PRODUCT_CONFIG[priceIdKey];
        if (!product) {
            console.error(`Invalid priceIdKey: ${priceIdKey}`);
            setError("An error occurred. Please select a valid package.");
            return;
        }

        setIsProcessingPayment(true);
        setError('');

        try {
            const token = await auth.currentUser.getIdToken();
            const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:3001' : '';
            const apiUrl = `${baseUrl}/api/create-checkout-session`;

            console.log(`Attempting to fetch checkout session from: ${apiUrl}`);

            // 2. Define Redirect URLs
            // This automatically detects if you are on localhost or production
            const origin = window.location.origin;

            // 3. Create Checkout Session
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    // FIX 1: Send ONLY the string ID, not the whole object
                    priceId: product.stripePriceId,

                    // FIX 2: Send the required metadata that server.js expects
                    credits: product.credits,
                    planId: priceIdKey,
                    customerEmail: user.email,
                    userId: user.uid,

                    // FIX 3: Send the required Success/Cancel URLs
                    successUrl: `${origin}/success?session_id={CHECKOUT_SESSION_ID}`,
                    cancelUrl: `${origin}/cancel`,

                    // Keep existing context
                    activeMode: activeMode,
                    organizationId: membership?.organizationId || null
                })
            });

            // 4. Check Content-Type before parsing JSON
            const contentType = response.headers.get("content-type");

            if (!response.ok) {
                if (contentType && contentType.indexOf("application/json") !== -1) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || 'Failed to create checkout session.');
                } else {
                    const text = await response.text();
                    console.error("Server returned non-JSON error:", text);
                    throw new Error(`Server API connection failed (${response.status}). Ensure backend is running.`);
                }
            }

            // 5. Parse Success Response & Redirect
            if (contentType && contentType.indexOf("application/json") !== -1) {
                // FIX: Destructure 'id' and 'url' (Backend sends 'id', not 'sessionId')
                const { id, url } = await response.json();

                if (url) {
                    // Preferred: Redirect to the URL provided by Stripe
                    window.location.href = url;
                } else if (id) {
                    // Fallback: Construct URL manually if 'url' is missing
                    window.location.href = `https://checkout.stripe.com/pay/${id}`;
                } else {
                    throw new Error("Session ID missing from server response.");
                }
            } else {
                throw new Error("Received successful status but invalid response format.");
            }

        } catch (error) {
            console.error("Stripe payment error:", error);
            setError(`Payment failed: ${error.message}`);
            setIsProcessingPayment(false);
        }
        // No finally block, as redirect happens on success
    };

    // --- THIS IS THE NEW FUNCTION ORDER ---

    // 1. resetGeneratorState (NO dependencies)
    const resetGeneratorState = useCallback(() => {
        // console.log('DEBUG: App.js - Calling resetGeneratorState');
        setGenerationAttempts(0);
        setSessionGeneratedImages([]);
        setImageExpirations({});
        setCurrentPreviewImage(null); // Clear the single preview

        // Reset UI state to defaults
        setUploadedImage(null);
        setUploadedImagePreview(null);
        setSelfieValidation({ status: 'idle', message: '' });
        setLogoValidation({ status: 'idle', message: '' });
        setDetectedGender(null);
        setSelectedCategoryId('corporate');
        setSelectedPresetId('business_formal');
        setSelectedAspectRatio('1:1');

        // Reset Corporate state
        setSelectedCorporateWardrobeId('business_formal');
        setSelectedCorporateBackgroundId('blurred_office');
        setCorporateBackgroundMode('preset');

        // Reset Medical/Institutional state
        setUniformMode('preset');
        setBackgroundMode('preset');
        setSelectedUniformPresetId('medical_scrubs');
        setSelectedBackgroundPresetId('clinic_lobby');

        // Reset Academic state
        setAcademicBackgroundMode('preset');
        setBackgroundColor('#e0e0e0');

        // Reset shared state
        setUploadedLogo(null);
        setUploadedLogoPreview(null);
        setColorToneOverride('');
        setUniformColorOverride('');

        // Reset Advanced settings
        setBlur(8);
        setBrightness(0);
        setTintColor('#FDE68A');
        setTintStrength(0);
        setVignette(false);
        setKeepPerspective(false);
        setLogoPosition('bottom_right');
        setLogoOpacity(80);
        setLogoConsent(false);
        setLogoPlacement('uniform');

        // Clear template editor fields
        setTemplateName('');
        setTemplateDescription('');

        // Clear localStorage
        localStorage.removeItem('generationAttempts');
        localStorage.removeItem('sessionGeneratedImages');
        localStorage.removeItem('imageExpirations');
    }, [
        // All setters are stable and guaranteed by React
        // so they don't need to be listed, but we list them
        // for completeness to satisfy exhaustive-deps
        setGenerationAttempts, setSessionGeneratedImages, setImageExpirations,
        setCurrentPreviewImage, setUploadedImage, setUploadedImagePreview,
        setSelfieValidation, setLogoValidation, setDetectedGender,
        setSelectedCategoryId, setSelectedPresetId, setSelectedAspectRatio,
        setSelectedCorporateWardrobeId, setSelectedCorporateBackgroundId,
        setCorporateBackgroundMode, setUniformMode, setBackgroundMode,
        setSelectedUniformPresetId, setSelectedBackgroundPresetId,
        setAcademicBackgroundMode, setBackgroundColor, setUploadedLogo,
        setUploadedLogoPreview, setColorToneOverride, setUniformColorOverride,
        setBlur, setBrightness, setTintColor, setTintStrength, setVignette,
        setKeepPerspective, setLogoPosition, setLogoOpacity, setLogoConsent,
        setLogoPlacement, setTemplateName, setTemplateDescription
    ]);

    // 2. handleCreditDeduction (depends on resetGeneratorState)
    const handleCreditDeduction = useCallback(async (creditsToDeduct) => {
        if (!user) {
            console.error("Credit deduction failed: No user found.");
            return false;
        }

        // Determine which credit pool to use
        const useTeamCredits = activeMode === 'team' && membership?.organizationId;

        // Check for sufficient credits using local state
        const currentCredits = useTeamCredits ? teamCreditsAvailable : (profile?.credits || 0);

        if (currentCredits < creditsToDeduct) {
            console.error("Credit deduction failed: Insufficient credits.");
            setError("You do not have enough credits for this action.");
            return false;
        }

        // Use server-side credit deduction endpoint
        try {
            const token = await user.getIdToken();
            // --- FIX 1: Use dynamic URL base and port ---
            const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:3001' : '';

            const response = await fetch(`${baseUrl}/api/deduct-credit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    creditsToDeduct,
                    useTeamCredits,
                    organizationId: useTeamCredits ? membership.organizationId : null,
                    membershipId: useTeamCredits ? membership.id : null
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || "Credit deduction failed");
            }

            const result = await response.json();

            if (result.success) {
                console.log('Credit deduction successful.');

                // 4. Reset the generator state
                // REMOVE THIS LINE: resetGeneratorState();
                return true;
            } else {
                throw new Error(result.error || "Credit deduction failed");
            }

        } catch (error) {
            console.error("Credit deduction failed: ", error);
            setError(error.message); // Show the specific error
            return false;
        }
    }, [
        user,
        activeMode,
        membership,
        teamCreditsAvailable,
        profile?.credits,
        setError
    ]);

    // 3. uploadToIndividualDrive (NO local dependencies)
    const uploadToIndividualDrive = useCallback(async (blob, filename, token, folderIdGetter) => {
        try {
            // 1. Get the application-specific folder ID
            const folderId = await folderIdGetter();
            if (!folderId) {
                throw new Error("Could not find or create app folder.");
            }

            // 2. Create File Metadata
            const metadata = {
                name: filename,
                parents: [folderId],
                mimeType: blob.type || 'image/jpeg',
                description: 'Generated by InstantHeadshot.pro' // Add description
            };

            // 3. Use multipart upload
            const form = new FormData();
            form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
            form.append('file', blob);

            const response = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                body: form,
            });

            if (!response.ok) {
                const errorData = await response.json();
                console.error("Google Drive upload error:", errorData);
                throw new Error(`Google Drive upload failed: ${errorData.error.message}`);
            }

            const fileData = await response.json();
            console.log("File uploaded to Google Drive:", fileData);
            return true;
        } catch (err) {
            console.error("Individual Drive upload failed:", err);
            return false;
        }
    }, []); // No dependencies from App scope

    // 4. handleSaveToDrive (depends on 2 & 3)
    const handleSaveToDrive = useCallback(async (image) => { // image is {id, url}
        if (!user) {
            setError("You must be logged in to save images.");
            return false;
        }

        setIsProcessingPayment(true); // Use this state for "Saving..."

        try {
            // 1. Check if this is a "new" save that costs a credit
            // A "new" save is an image from the current session
            const isNewSave = sessionGeneratedImages.some(img => img.id === image.id);

            if (!isNewSave) {
                // This is an old image from history (Drive).
                // The Dashboard's handleGalleryDownload should just download it directly.
                console.warn("handleSaveToDrive called on non-session image. Skipping.");
                setIsProcessingPayment(false); // Make sure to stop loading
                return true; // Not a new save, so "success"
            }

            // --- THIS IS THE FIX: Differentiate between Team and Personal Save ---

            const token = await auth.currentUser.getIdToken();
            const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:3001' : '';

            // Check if this is a Team Save (Team mode active AND GDrive connected by admin)
            const isTeamSave = activeMode === 'team' && organization?.googleDriveFolderId;

            if (isTeamSave) {
                // --- FLOW 1: TEAM SAVE (Atomic Server-side) ---
                console.log(`Calling ATOMIC TEAM save endpoint for imageId: ${image.id}`);

                // Call the new atomic endpoint that handles team drive upload AND credit deduction
                const response = await fetch(`${baseUrl}/api/drive/save-and-deduct`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        imageId: image.id,
                        userId: user.uid,
                        organizationId: organization?.id
                    })
                });

                if (!response.ok) {
                    const errData = await response.json();
                    throw new Error(errData.error || "Failed to save to team drive.");
                }

                const result = await response.json();
                console.log("Successfully saved to Team Drive via atomic backend operation:", result);

            } else {
                // --- FLOW 2: PERSONAL SAVE (Client-side) ---
                console.log(`Calling PERSONAL save flow for imageId: ${image.id}`);

                // 1. Get blob from backend (same as old logic)
                const response = await fetch(`${baseUrl}/api/download-headshot`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({ imageId: image.id, userId: user.uid })
                });

                if (!response.ok) {
                    const errData = await response.json();
                    throw new Error(errData.error || "Failed to retrieve full image from server.");
                }

                const blob = await response.blob();
                const filename = `InstantHeadshot_${image.id}.png`;

                // 2. Try personal drive, or fallback to local download
                if (googleDriveToken) {
                    // This is the user's PERSONAL drive
                    console.log("Attempting to save to PERSONAL Google Drive...");
                    const driveSuccess = await uploadToIndividualDrive(blob, filename, googleDriveToken, getDriveFolderId);

                    if (!driveSuccess) {
                        console.warn("Personal Drive upload failed, falling back to local download.");
                        // Local download fallback
                        const link = document.createElement('a');
                        link.href = URL.createObjectURL(blob);
                        link.download = filename;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        URL.revokeObjectURL(link.href);
                    } else {
                        console.log("Successfully saved to Personal Google Drive.");
                    }
                } else {
                    // No Personal Google Drive, just do a local download
                    console.log("No personal Google Drive token. Saving locally.");
                    const link = document.createElement('a');
                    link.href = URL.createObjectURL(blob);
                    link.download = filename;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(link.href);
                }
            }

            // --- COMMON SUCCESS LOGIC: Deduct credit and refresh ---

            // 5. Deduct the credit (only for personal saves - team saves are handled atomically)
            if (!isTeamSave) {
                const deductionSuccess = await handleCreditDeduction(1);
                if (!deductionSuccess) {
                    // The error message from handleCreditDeduction will be set
                    throw new Error("Credit deduction failed. Your image was saved, but please contact support about your credit balance.");
                }
            }

            // 6. Refresh gallery if drive was used
            if (isTeamSave || googleDriveToken) {
                await refreshDriveGallery();
            }

            // --- ADD THIS CRITICAL LINE ---
            resetGeneratorState(); // <--- FIX: Reset state after successful save (Team or Personal)

            return true; // Total success

        } catch (error) {
            console.error("Error saving image:", error);
            setError(`Save failed: ${error.message}`);
            return false;
        } finally {
            setIsProcessingPayment(false);
        }
    }, [
        user,
        sessionGeneratedImages,
        googleDriveToken,
        activeMode,
        organization, // This includes organization.googleDriveFolderId
        getDriveFolderId,
        handleCreditDeduction,
        uploadToIndividualDrive,
        refreshDriveGallery,
        resetGeneratorState
        // We also implicitly depend on 'auth' for getIdToken, but it's stable.
    ]);

    // Handle back button from generator
    const handleBack = useCallback(() => {
        // console.log('DEBUG: App.js - onBack called, setting showHeadshotGenerator to false');
        setShowHeadshotGenerator(false);
    }, []);

    // Handle editor back button
    const handleEditorBack = useCallback(() => {
        handleBack(); // Call the other memoized function
        setIsTemplateEditorActive(false);
    }, [handleBack]); // Now this function is stable

    // Handle session expiration
    const onSessionExpired = useCallback(() => {
        console.log('Session expired, resetting generator state.');
        resetGeneratorState();
    }, [resetGeneratorState]); // Depends on the (now stable) reset function

    // Handle LinkedIn Bio generation
    const handleGenerateBio = async () => {
        if (!generatedImage) {
            setError("Please generate a headshot first.");
            return;
        }

        setIsTextLoading(true);
        setError('');
        setLinkedInBio('');

        try {
            const response = await fetch('/api/generate-with-gemini', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    prompt: `Based on this professional headshot, write a compelling 2-3 sentence LinkedIn bio summary. Style: ${customPrompt}`,
                    isJson: false,
                    userId: user?.uid || 'unknown'
                })
            });

            if (!response.ok) throw new Error('Failed to generate bio.');
            const result = await response.json();
            setLinkedInBio(result.generatedText);
        } catch (err) {
            console.error(err);
            setError("Sorry, we couldn't generate your LinkedIn bio.");
        } finally {
            setIsTextLoading(false);
        }
    };

    // --- Stripe Test Payment Functions (DEV ONLY) ---
    // (These are now commented out as per your file, but the logic
    // for handleStripePayment is what matters)

    // --- NEW: Drive Auth Handlers for TeamAdminPanel ---

    const handleAuthSuccess = useCallback(async (authCode) => {
        console.log("Auth code received, sending to backend...");
        // setIsAuthorizing(true); // This state is in TeamAdminPanel
        try {
            const token = await user.getIdToken();
            const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:3001' : '';
            const response = await fetch(`${baseUrl}/api/drive/admin-auth-callback`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    authCode: authCode,
                    organizationId: organization.id
                })
            });

            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.error || 'Failed to authorize with backend.');
            }

            await response.json();
            // setDriveStatus({connected: true, email: data.email }); // State is in TeamAdminPanel
            // Toast will be handled by TeamAdminPanel via showToast prop
        } catch (err) {
            console.error(err);
            // Toast will be handled by TeamAdminPanel via showToast prop
        } finally {
            // setIsAuthorizing(false); // State is in TeamAdminPanel
        }
    }, [organization?.id, user]);

    const handleAuthError = useCallback((err) => {
        console.error("Google Auth Code Error:", err);
        // Toast will be handled by TeamAdminPanel via showToast prop
    }, []);

    // --- LIFECYCLE & DATA FETCHING ---

    // Handle Auth State Change
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                console.log('Auth state changed: User is logged in.', user.uid);
                setUser(user);

                // --- IDENTITY BRIDGE: AUTO-PROVISION WORKSPACE FOR EXISTING SESSIONS ---
                const lid = localStorage.getItem('gh_lid');
                if (lid) {
                    let leadData = { Firm_Name: "Your Team", Niche: "professional services", Team_Size: 0 };
                    try {
                        console.log(`Identity Bridge (onAuth): Fetching lead ${lid}...`);
                        const leadSnap = await getDoc(doc(db, 'leads_bridge', lid));
                        if (leadSnap.exists()) {
                            leadData = leadSnap.data();
                            console.log("Identity Bridge (onAuth): Lead mapped successfully", leadData);
                        } else {
                            console.warn("Identity Bridge (onAuth): Lead not found in database. Using generic template.");
                        }
                    } catch (err) {
                        console.warn("Identity Bridge (onAuth): GTM server unreachable. Provisioning generic PLG team template.");
                    }

                    try {
                        const initialCredits = leadData.complimentaryCredits || 2; // Complimentary evaluation

                        const orgRef = doc(collection(db, 'organizations'));
                        await setDoc(orgRef, {
                            ownerId: user.uid,
                            orgName: `${leadData.Firm_Name || "Your Team"}'s Team`,
                            sharedCredits: initialCredits,
                            createdAt: new Date(),
                            niche: leadData.Niche || "professional services",
                            teamSizeOriginal: leadData.Team_Size || 0,
                            source: 'SDR_Identity_Bridge',
                            status: 'active'
                        });

                        // Send Conversion Signal back to GTM pipeline!
                        try {
                            await updateDoc(doc(db, 'leads_bridge', lid), { signed_up: true, convertedAt: new Date() });
                        } catch (err) { console.log(err); }
                        await setDoc(doc(db, 'memberships', user.uid), {
                            userId: user.uid,
                            organizationId: orgRef.id,
                            role: 'admin',
                            status: 'active',
                            allottedCredits: 99999,
                            usedCredits: 0,
                            joinedAt: new Date()
                        });

                        // Force User Profile into Team Mode
                        const profileSdrRef = doc(db, 'profiles', user.uid);
                        await setDoc(profileSdrRef, {
                            activeOrganizationId: orgRef.id,
                            activeMode: 'team'
                        }, { merge: true });

                        // Note: Evaluation credits are only added to the Team Organization (orgRef)
                        // We do NOT add them to the personal userRef to force them to test the Team features.
                        localStorage.removeItem('gh_lid');
                        console.log("Existing user Identity Bridge processed. Team evaluation granted.");
                    } catch (err) { console.error("Identity Bridge (onAuth) failed during Firestore update: ", err); }
                }
                // --- END IDENTITY BRIDGE ---

                // --- ADD THIS BLOCK: Check for pending invite ---
                const pendingInviteToken = localStorage.getItem('pendingInviteToken');
                if (pendingInviteToken) {
                    console.log('User logged in, found pending invite token.');
                    // Don't set isAuthLoading(false) yet
                    // The AcceptInvitePage will handle the flow
                    // and its own loading state.
                    return; // Stop execution to let AcceptInvitePage take over
                }
                // --- END ADD ---

            } else {
                console.log('Auth state changed: User is logged out.');
                setUser(null);
                setProfile(null);
                setMembership(null); // Clear team state
                setOrganization(null); // Clear team state
                setTeamCreditsAvailable(0); // Clear team state
                setTeamTemplates([]); // Clear team state
                setGoogleDriveToken(null); // Clear Drive token
                localStorage.removeItem('googleDriveToken'); // Clear from storage
                setDriveImages([]); // Clear drive images
            }
            setIsAuthLoading(false); // Auth check is complete
        });

        return () => unsubscribe();
    }, []); // Empty array, runs once



    // Listen to user's profile document
    useEffect(() => {
        if (!user) return;


        // Listen for profile changes
        const profileRef = doc(db, 'profiles', user.uid);
        const unsubscribeProfile = onSnapshot(profileRef, (docSnap) => {
            if (docSnap.exists()) {
                const profileData = docSnap.data(); // <-- 1. Get data
                setProfile(profileData);

                // --- THIS IS THE FIX ---
                // Set the activeMode from the user's saved preference
                // But only if the user hasn't manually changed the mode
                if (profileData.activeMode && !userManuallyChangedMode.current) {
                    setActiveMode(profileData.activeMode);
                }
                // --- END FIX ---

            } else {
                console.warn('Profile document not found for logged-in user.');
            }
        }, (error) => {
            console.error("Error listening to profile:", error);
        });

        // Listen for team membership changes
        // --- THIS IS THE FIX ---
        // We query for the membership doc using the userId field,
        // as the doc ID is auto-generated.
        const memberQuery = query(
            collection(db, 'memberships'),
            where('userId', '==', user.uid)
        );
        // --- END FIX ---

        let unsubscribeMembership = () => { };
        let unsubscribeOrganization = () => { };
        let unsubscribeTemplates = () => { };

        unsubscribeMembership = onSnapshot(memberQuery, (querySnapshot) => {
            if (querySnapshot.empty) {
                console.log('User is not part of a team.');
                setMembership(null);
                setOrganization(null);
                setTeamCreditsAvailable(0);
                setTeamTemplates([]); // <-- ADD THIS
                setActiveMode('personal'); // Default to personal
                if (unsubscribeOrganization) unsubscribeOrganization();
                if (unsubscribeTemplates) unsubscribeTemplates();
                return;
            }

            // --- This is a TEAM user ---
            // Get the first (and should be only) membership doc
            const memberDoc = querySnapshot.docs[0];
            const memberData = { ...memberDoc.data(), id: memberDoc.id };
            setMembership(memberData);

            // 3. Now that we have their membership, listen to their organization
            const orgId = memberData.organizationId;
            if (orgId) {
                const orgRef = doc(db, 'organizations', orgId);
                unsubscribeOrganization = onSnapshot(orgRef, (orgSnap) => {
                    if (orgSnap.exists()) {
                        // --- THIS IS THE FIX ---
                        setOrganization({ id: orgSnap.id, ...orgSnap.data() });
                        // --- END OF FIX ---

                        // --- CALCULATE AVAILABLE TEAM CREDITS ---
                        // This is the core logic
                        const orgCredits = orgSnap.data().sharedCredits || 0;
                        const memberUsed = memberData.usedCredits || 0;
                        const memberAllotment = memberData.allottedCredits || 0;

                        // Available to member is MIN( of what's left in their allotment, org pool)
                        const remainingInAllotment = memberAllotment - memberUsed;

                        // Admins get full access to the org pool
                        if (memberData.role === 'admin') {
                            setTeamCreditsAvailable(orgCredits);
                        } else {
                            // Members get the lower of the two values
                            setTeamCreditsAvailable(Math.min(orgCredits, remainingInAllotment));
                        }
                    } else {
                        console.warn('Organization document not found for member.');
                        setOrganization(null);
                        setTeamCreditsAvailable(0);
                    }
                }, (error) => {
                    console.error("Error listening to organization:", error);
                });

                // --- THIS IS THE FIX ---
                // Load templates for *any* team member, not just admins
                const templatesQuery = query(
                    collection(db, 'teamTemplates'),
                    where('organizationId', '==', orgId)
                );

                unsubscribeTemplates = onSnapshot(templatesQuery, (querySnapshot) => {
                    const templatesList = querySnapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    }));
                    setTeamTemplates(templatesList);
                }, (error) => {
                    console.error("Error fetching team templates:", error);
                    setTeamTemplates([]); // Clear on error
                });
                // --- END FIX ---

            } else {
                console.warn('Membership doc exists but has no organizationId.');
                setOrganization(null);
                setTeamCreditsAvailable(0);
                setTeamTemplates([]);
                if (unsubscribeTemplates) unsubscribeTemplates();
            }
        }, (error) => {
            console.error("Error listening to membership:", error);
            setMembership(null);
            setOrganization(null);
            setTeamCreditsAvailable(0);
            setTeamTemplates([]);
            if (unsubscribeOrganization) unsubscribeOrganization();
            if (unsubscribeTemplates) unsubscribeTemplates();
        });

        return () => {
            unsubscribeProfile();
            unsubscribeMembership();
            unsubscribeOrganization();
            unsubscribeTemplates();
        };
    }, [user, googleDriveToken]); // Re-run if user or token changes

    // Refresh Drive Gallery once token is available
    useEffect(() => {
        if (googleDriveToken) {
            refreshDriveGallery();
        }
    }, [googleDriveToken, refreshDriveGallery, activeMode]); // Runs when token is set or mode changes

    // Persist session state to localStorage
    useEffect(() => {
        try {
            localStorage.setItem('generationAttempts', JSON.stringify(generationAttempts));
        } catch (error) {
            console.warn('Failed to save generationAttempts to localStorage:', error);
        }
    }, [generationAttempts]);

    useEffect(() => {
        try {
            // Filter out any invalid entries before saving
            const validImages = sessionGeneratedImages.filter(img =>
                img && typeof img.url === 'string' && typeof img.id === 'string'
            );
            localStorage.setItem('sessionGeneratedImages', JSON.stringify(validImages));
        } catch (error) {
            console.warn('Failed to save sessionGeneratedImages to localStorage:', error);
        }
    }, [sessionGeneratedImages]);

    useEffect(() => {
        try {
            localStorage.setItem('imageExpirations', JSON.stringify(imageExpirations));
        } catch (error) {
            console.warn('Failed to save imageExpirations to localStorage:', error);
        }
    }, [imageExpirations]);

    // --- ADD THIS NEW BLOCK ---
    // Syncs the "Purchase Credits" toggle with the "Active Credit Pool" toggle
    useEffect(() => {
        if (activeMode === 'team') {
            setPricingView('team');
        } else {
            setPricingView('individual');
        }
    }, [activeMode]);
    // --- END NEW BLOCK ---

    // Track processed payment sessions to prevent duplicates
    const processedSessions = useRef(new Set());

    // Handle payment success
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get('session_id');

        if (sessionId && user && !isProcessingPayment) {

            if (processedSessions.current.has(sessionId)) {
                console.log('Payment session already processed:', sessionId);
                return;
            }
            processedSessions.current.add(sessionId);

            setIsProcessingPayment(true);
            console.log("Processing payment success for session:", sessionId);

            const handlePaymentSuccess = async (sessionId) => {
                try {
                    const token = await auth.currentUser.getIdToken();
                    const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:3001' : '';

                    const response = await fetch(`${baseUrl}/api/handle-payment-success`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        // --- FIX START: Add userId here ---
                        body: JSON.stringify({
                            sessionId: sessionId,
                            userId: user.uid // <--- This was missing
                        })
                        // --- FIX END ---
                    });

                    if (!response.ok) {
                        const errorData = await response.json();
                        throw new Error(errorData.error || 'Failed to verify payment.');
                    }

                    const { message } = await response.json();
                    console.log(message); // "Payment success processed."

                    // The onSnapshot listener for the profile/org will
                    // automatically update the credit count in the UI.

                    // Navigate to success page
                    // setPage('success');
                    window.history.replaceState({}, document.title, '/success');

                } catch (error) {
                    console.error("Payment processing error:", error);
                    setError(error.message);
                    window.history.replaceState({}, document.title, '/'); // Go home on error
                } finally {
                    setIsProcessingPayment(false);
                }
            };

            handlePaymentSuccess(sessionId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, isProcessingPayment]); // Only run when user or payment state changes


    return (
        <Router>
            <Routes>
                <Route path="/guides/:slug" element={<GuidePageLoader />} />
                <Route path="*" element={<>

                    {/* Toast renderer - visible on all pages */}
                    {toast.show && (
                        <div className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-lg shadow-lg border ${toast.type === 'success'
                            ? 'bg-green-900 border-green-700 text-green-100'
                            : 'bg-red-900 border-red-700 text-red-100'
                            }`}>
                            {toast.message}
                        </div>
                    )}

                    <AppContent
                        user={user}
                        profile={profile}
                        // page={page} // <-- No longer needed, AppContent manages this
                        isAuthLoading={isAuthLoading}
                        showHeadshotGenerator={showHeadshotGenerator}
                        generatedImage={generatedImage}
                        userManuallyChangedMode={userManuallyChangedMode}
                        sessionGeneratedImages={sessionGeneratedImages}
                        generationAttempts={generationAttempts}
                        onBack={handleBack}
                        onCreditDeduction={handleCreditDeduction}
                        onSessionExpired={onSessionExpired}
                        imageExpirations={imageExpirations}

                        // --- PASS TEAM STATE ---
                        activeMode={activeMode}
                        setActiveMode={setActiveMode}
                        membership={membership}
                        organization={organization}
                        teamCreditsAvailable={teamCreditsAvailable}

                        // --- PASS PRICING TOGGLE ---
                        pricingView={pricingView}
                        setPricingView={setPricingView}
                        setShowLoginModal={setShowLoginModal}

                        // --- PASS DRIVE STATE ---
                        refreshDriveGallery={refreshDriveGallery}

                        // --- PASS ALL PROPS FOR GENERATOR ---
                        isTemplateEditorActive={isTemplateEditorActive}
                        handleEditorBack={handleEditorBack}
                        handleSaveToDrive={handleSaveToDrive}
                        teamTemplates={teamTemplates}
                        setTeamTemplates={setTeamTemplates}
                        setIsTemplateEditorActive={setIsTemplateEditorActive}
                        // generationAttempts_prop={generationAttempts} // <-- Use 'generationAttempts'
                        setGenerationAttempts={setGenerationAttempts}
                        // sessionGeneratedImages_prop={sessionGeneratedImages} // <-- Use 'sessionGeneratedImages'
                        setSessionGeneratedImages={setSessionGeneratedImages}
                        // imageExpirations_prop={imageExpirations} // <-- Use 'imageExpirations'
                        setImageExpirations={setImageExpirations}
                        currentPreviewImage={currentPreviewImage}
                        setCurrentPreviewImage={setCurrentPreviewImage}
                        uploadedImage={uploadedImage}
                        setUploadedImage={setUploadedImage}
                        uploadedImagePreview={uploadedImagePreview}
                        setUploadedImagePreview={setUploadedImagePreview}
                        selfieValidation={selfieValidation}
                        setSelfieValidation={setSelfieValidation}
                        logoValidation={logoValidation}
                        setLogoValidation={setLogoValidation}
                        detectedGender={detectedGender}
                        setDetectedGender={setDetectedGender}
                        selectedCategoryId={selectedCategoryId}
                        setSelectedCategoryId={setSelectedCategoryId}
                        selectedPresetId={selectedPresetId}
                        setSelectedPresetId={setSelectedPresetId}
                        selectedAspectRatio={selectedAspectRatio}
                        setSelectedAspectRatio={setSelectedAspectRatio}

                        // Corporate state
                        selectedCorporateWardrobeId={selectedCorporateWardrobeId}
                        setSelectedCorporateWardrobeId={setSelectedCorporateWardrobeId}
                        selectedCorporateBackgroundId={selectedCorporateBackgroundId}
                        setSelectedCorporateBackgroundId={setSelectedCorporateBackgroundId}
                        corporateBackgroundMode={corporateBackgroundMode}
                        setCorporateBackgroundMode={setCorporateBackgroundMode}

                        // Medical/Institutional state
                        uniformMode={uniformMode}
                        setUniformMode={setUniformMode}
                        backgroundMode={backgroundMode}
                        setBackgroundMode={setBackgroundMode}
                        selectedUniformPresetId={selectedUniformPresetId}
                        setSelectedUniformPresetId={setSelectedUniformPresetId}
                        selectedBackgroundPresetId={selectedBackgroundPresetId}
                        setSelectedBackgroundPresetId={setSelectedBackgroundPresetId}

                        // Academic state
                        academicBackgroundMode={academicBackgroundMode}
                        setAcademicBackgroundMode={setAcademicBackgroundMode}
                        backgroundColor={backgroundColor}
                        setBackgroundColor={setBackgroundColor}
                        uploadedLogo={uploadedLogo}
                        setUploadedLogo={setUploadedLogo}
                        uploadedLogoPreview={uploadedLogoPreview}
                        setUploadedLogoPreview={setUploadedLogoPreview}
                        colorToneOverride={colorToneOverride}
                        setColorToneOverride={setColorToneOverride}
                        uniformColorOverride={uniformColorOverride}
                        setUniformColorOverride={setUniformColorOverride}

                        // Advanced settings
                        blur={blur}
                        setBlur={setBlur}
                        brightness={brightness}
                        setBrightness={setBrightness}
                        tintColor={tintColor}
                        setTintColor={setTintColor}
                        tintStrength={tintStrength}
                        setTintStrength={setTintStrength}
                        vignette={vignette}
                        setVignette={setVignette}
                        keepPerspective={keepPerspective}
                        setKeepPerspective={setKeepPerspective}
                        logoPosition={logoPosition}
                        setLogoPosition={setLogoPosition}
                        logoOpacity={logoOpacity}
                        setLogoOpacity={setLogoOpacity}
                        logoConsent={logoConsent}
                        setLogoConsent={setLogoConsent}
                        logoPlacement={logoPlacement}
                        setLogoPlacement={setLogoPlacement}

                        // Template editor state
                        templateName={templateName}
                        setTemplateName={setTemplateName}
                        templateDescription={templateDescription}
                        setTemplateDescription={setTemplateDescription}

                        // Pass-through props for Dashboard/Generator
                        handleStripePayment={handleStripePayment}
                        isProcessingPayment={isProcessingPayment}
                        setError={setError}
                        setShowHeadshotGenerator={setShowHeadshotGenerator}
                        googleDriveToken={googleDriveToken}
                        error={error}

                        // --- PASS DRIVE AUTH HANDLERS ---
                        handleAuthSuccess={handleAuthSuccess}
                        handleAuthError={handleAuthError}

                        // --- ADD THESE MISSING PROPS ---
                        isFetchingDrive={isFetchingDrive}
                        driveImages={driveImages}
                        generatedImages={generatedImages}
                        handleGenerateBio={handleGenerateBio}
                        isTextLoading={isTextLoading}
                        linkedInBio={linkedInBio}

                        // --- ADD THESE TWO LINES ---
                        db={db}
                        storage={storage}

                        // --- ADD TOAST STATE AND FUNCTION ---
                        toast={toast}
                        showToast={showToast}
                    // --- END OF ADDITIONS ---
                    />
                    {/* LoginModal - Google Sign-In only */}
                    <LoginModal
                        showLoginModal={showLoginModal}
                        setShowLoginModal={setShowLoginModal}
                        isAuthLoading={isAuthLoading}
                        error={error}
                        handleSignIn={handleSignIn}
                    />
                </>} />
            </Routes>
        </Router>
    );
}

export default App;